/* live_v2/index.js — host Alpine controller for the v2 widget dashboard.
 *
 * Composes dashboard() (the existing controller in static/js/live.js, ~3000
 * LOC of slate/lineup/SSE state) with widget-host concerns: layout, drag,
 * persistence, mobile auto-stack.
 *
 * IMPORTANT: do NOT compose via object spread (`{ ...dashboard() }`). Spread
 * reads each property, which INVOKES `get foo()` getters and turns them
 * into static data properties — every reactive computed in dashboard()
 * would freeze. Use Object.defineProperties + getOwnPropertyDescriptors
 * to preserve accessor descriptors.
 *
 * LAYOUT SHAPE (typed slots — Row 1 / Row 2 / Row 3+):
 *   layout = {
 *     scoreboard_id: 'nba_quarter' | null,   // Row 1 — one widget, sport-driven
 *     kpis:          [widget_id, ...],        // Row 2 — ordered horizontal strip
 *     panels:        [{id,x,y,w,h}, ...],     // Row 3+ — 12-col draggable grid
 *   }
 *
 * Saved JSONB from the OLD shape ({widgets: [...]}) is migrated on load via
 * _migrateLayout so existing users don't see anything break.
 */

/**
 * Fit-to-viewport defaults.
 *
 * Panel h values mean "share of the visible grid" (1fr each, totalRows = max
 * y+h). KPIs are a fixed-height horizontal strip; user picks which 3-6
 * appear. Scoreboard for Row 1 is null until per-sport bespoke scoreboards
 * are registered with slot:'scoreboard' (today it's still rendered via the
 * legacy dash_matches_duel partial in the headline zone).
 */
/**
 * Per-sport starter layouts. Each entry is { scoreboard_id?, kpis, panels }.
 * `_defaultLayout(sport_id)` picks the matching key, falling back to `global`.
 *
 * The scoreboard_id is OPTIONAL here because the headline zone resolves
 * the active scoreboard automatically via activeScoreboardId getter
 * (which uses LSWidgets.forSlot('scoreboard', sport_id)). Set it only
 * when you want to override the auto-resolution.
 *
 * Adding a new sport's starter: add a key here + register its bespoke
 * widgets with sportScope:[<sport>] in static/js/widgets/. Done.
 */
/* Picker section ordering. Drives the "+ Add panel" / "+ Add KPI"
 * dropdown's category header order (Phase 5). Categories not listed
 * here sort alphabetically. 'Other' (widgets with no category) always
 * lands last regardless of position in this list. */
const LS_PICKER_CATEGORY_ORDER = [
  'Lineups',
  'Leaderboard',
  'Optimisation',
  'Live state',
  'Feed',
  'Schedule',
  'Analytics',
];

// (Phase 7 + Cleanup Phase B — Xav 2026-05-31): DEFAULT_LAYOUTS,
// PRESET_DEFAULTS, and DEFAULT_LAYOUT alias removed. System presets
// are now CODE-DEFINED in dashboard_defaults_seed.SYSTEM_PRESETS_V2
// and served via /api/system-presets — the frontend never falls back
// to a hardcoded JS layout. _migrateLayout below uses an inline empty
// default for empty-input safety.

const _emptyLayout = () => ({
  scoreboard_id: null,
  kpis:          [],
  panels:        [],
  widgetOpts:    {},
});

const _clone = (o) => JSON.parse(JSON.stringify(o));
// TV mode's tab rotation (Xav 2026-10-12): seconds on each tab, and how long a
// tab picked by hand holds the screen before rotation resumes.
const TV_DWELL_S = 40;
const TV_PIN_S = 120;

/**
 * Convert any prior-shape layout blob to the new typed-slots shape.
 * - Already-new layouts ({scoreboard_id, kpis, panels, widgetOpts})
 *   pass through; widgetOpts defaults to empty if absent.
 * - Old layouts ({widgets: [...]}) split by each widget's slot via the
 *   JS registry. Unknown widget ids default to slot:'panel'.
 * - Legacy per-panel opts (panels[i].opts) get LIFTED into the flat
 *   widgetOpts map (Phase 1 refactor 2026-05-27) and stripped from
 *   their panel entry so the next save isn't double-storing.
 * - Empty/missing layouts get an empty layout (callers above us load
 *   the actual default from /api/system-presets — this fallback only
 *   keeps the shape sane on null input).
 */
/** Carry a layout's before-kick-off KPI strip (Broadcast's `pregame_kpis`)
 *  onto a storage-shape copy. The shapes below were written before the key
 *  existed and list their fields by hand, so without this a Studio publish of
 *  Broadcast would quietly drop its pre-game strip. */
function _keepPregame(out, src) {
  if (src && Array.isArray(src.pregame_kpis)) out.pregame_kpis = [...src.pregame_kpis];
  if (src && Array.isArray(src.final_kpis)) out.final_kpis = [...src.final_kpis];
  return out;
}

function _migrateLayout(raw, sport_id) {
  const sid     = (sport_id || '').toLowerCase();
  const _default = _emptyLayout();

  // Helper: lift any panels[i].opts up into a top-level widgetOpts map.
  // Idempotent — re-running on an already-migrated layout is a no-op.
  // Top-level widgetOpts wins on key collision (already authoritative).
  function _liftLegacyOpts(panels, widgetOpts) {
    const lifted = { ...(widgetOpts || {}) };
    const cleanPanels = (panels || []).map(p => {
      if (!p || !p.opts) return p;
      lifted[p.id] = { ...(p.opts || {}), ...(lifted[p.id] || {}) };
      const { opts, ...rest } = p;
      return rest;
    });
    return { panels: cleanPanels, widgetOpts: lifted };
  }

  // Widget renames / merges — rewrite legacy ids before the rest of the
  // shape migration touches them. Keep this list short and append-only.
  // (Xav 2026-05-29: "rankings" merged into "top10_my_lineups" which now
  // displays as "My Rankings".)
  const RENAMES = {
    'rankings': 'top10_my_lineups',
  };
  function _applyRenames(panels, kpis, sb) {
    const seenPanels = new Set();
    const renamedPanels = (panels || []).map(p => {
      const newId = RENAMES[p.id] || p.id;
      return { ...p, id: newId };
    }).filter(p => {
      // Dedupe in case both the old and new id were present.
      if (seenPanels.has(p.id)) return false;
      seenPanels.add(p.id);
      return true;
    });
    const seenKpis = new Set();
    const renamedKpis = (kpis || []).map(id => RENAMES[id] || id).filter(id => {
      if (seenKpis.has(id)) return false;
      seenKpis.add(id);
      return true;
    });
    const renamedSb = sb ? (RENAMES[sb] || sb) : sb;
    return { panels: renamedPanels, kpis: renamedKpis, sb: renamedSb };
  }

  if (!raw || typeof raw !== 'object') return _clone(_default);
  if (raw.panels !== undefined || raw.kpis !== undefined) {
    // Already new shape — fill in missing keys defensively + lift any
    // legacy per-panel opts to the flat map.
    const lifted = _liftLegacyOpts(
      Array.isArray(raw.panels) ? raw.panels : [],
      raw.widgetOpts || {}
    );
    const renamed = _applyRenames(
      lifted.panels,
      Array.isArray(raw.kpis) ? raw.kpis : [],
      raw.scoreboard_id ?? null,
    );
    return {
      scoreboard_id: renamed.sb,
      kpis:          renamed.kpis,
      panels:        renamed.panels,
      widgetOpts:    lifted.widgetOpts,
      // A board's before-kick-off KPI strip (Broadcast), swapped in by
      // _kpiList until a game goes live. Absent on every other board.
      ...(Array.isArray(raw.pregame_kpis) ? { pregame_kpis: [...raw.pregame_kpis] } : {}),
      // ...and its after-the-final-whistle strip, once every game is final.
      ...(Array.isArray(raw.final_kpis) ? { final_kpis: [...raw.final_kpis] } : {}),
      // Phase 7: which version of the sport default this layout
      // branched from / last acknowledged. Missing → 0 → user will
      // see an upgrade prompt the first time after the admin bumps
      // the default's version.
      defaultVersion: typeof raw.defaultVersion === 'number' ? raw.defaultVersion : 0,
    };
  }
  if (Array.isArray(raw.widgets)) {
    const panels = [];
    const kpis   = [];
    let sb       = null;
    for (const w of raw.widgets) {
      const def  = window.LS_WIDGETS && window.LS_WIDGETS[w.id];
      const slot = (def && def.slot) || 'panel';
      if      (slot === 'panel')      panels.push(w);
      else if (slot === 'kpi')        kpis.push(w.id);
      else if (slot === 'scoreboard') sb = w.id;
    }
    const lifted  = _liftLegacyOpts(panels, {});
    const renamed = _applyRenames(lifted.panels, kpis, sb);
    return {
      scoreboard_id: renamed.sb,
      // If user had no KPIs (because the slot didn't exist), seed sport defaults.
      kpis:          renamed.kpis.length ? renamed.kpis : [..._default.kpis],
      panels:        renamed.panels,
      widgetOpts:    lifted.widgetOpts,
      defaultVersion: 0,                                  // legacy → prompt once
    };
  }
  return _clone(_default);
}

function liveDashboardV2() {
  // Base = full dashboard() controller, with all getters intact.
  const base = dashboard();

  const host = {
    // ── Layout state ───────────────────────────────────────────────────
    // Typed slots: {scoreboard_id, kpis: [], panels: [], widgetOpts: {}, defaultVersion: 0}.
    // widgetOpts is a flat {[widget_id]: {opts}} map decoupled from
    // layout placement so KPIs / scoreboard / panels can all carry
    // options. See top of file + getOpt/setOpt.
    // `defaultVersion` field on layout: legacy from the old
    // dashboard_defaults upgrade-banner system (removed Phase 7).
    // Left in the layout shape only because some downstream readers
    // may still inspect it; harmless default of 0.
    layout:        { scoreboard_id: null, kpis: [], panels: [], widgetOpts: {}, defaultVersion: 0 },
    layoutLoaded:  false,
    layoutDirty:   false,
    // BO Dashboard Studio (P2): read-only preview mode. Set by initStudioPreview
    // before first render; makes hasLineup true so EVERY tab + panel shows (the
    // admin lays out the full board, not the spectator subset), and suppresses
    // the "pick a slate" / "no lineup" empty-state prompts. Always false on live.
    _studioPreview: false,
    // Studio edit/save state (P3). editMode is the shared user-dashboard edit
    // machinery, unlocked in the Studio via _editable; saveStudioDraft POSTs the
    // whole board as a DRAFT version (not published — invisible to the live site).
    _studioSaving:  false,
    _studioSaveMsg: '',
    _studioLabel:   '',
    // Version management (P4): the board's saved versions + the drawer state.
    _studioVersions:     [],
    _studioVersionsOpen: false,
    _studioVersionsBusy: false,
    _studioSampleOn:     false,   // P5: demo data fed into the preview frames
    _studioDiffId:       null,    // P5: version id being reviewed (diff vs live)
    _studioDiff:         null,    // P5: computed diff for _studioDiffId
    _studioExportOpen:   false,   // P5: export-to-code panel
    _studioExportCode:   '',      // P5: generated Python snippet
    mobile:        false,       // <md viewport
    _tabletMq:     false,       // 768-1399px (iPad landscape, small laptops) - see get tablet
    _bcDesktop:    false,       // a mouse and >=1280px: Broadcast keeps its own grid
    // Grid width, recorded by the existing resize measure. Declared here so
    // Alpine tracks it and _tabletPlacements re-projects on rotate/resize;
    // reading offsetWidth inside that getter would force a reflow per cell.
    _gridW:        0,
    tabletTouch:   false,       // 768-1099px on a touch screen (iPad portrait) - gets the shell
    touchDevice:   false,       // any coarse pointer - no dashboard editing
    tvMode:        false,       // couch / second-screen full-bleed mode (Shift+T, ?tv=1, fullscreen)
    // TV mode rotates the board's tabs (Xav 2026-10-12: the tab bar was hidden in
    // TV mode, so a TV sat on one tab all night). _tvPct drives the active tab's
    // progress bar; touching a tab pins it for TV_PIN_S.
    _tvPct:        0,
    _tvElapsed:    0,
    _tvPinnedUntil: 0,
    _tvTimer:      null,
    pickerOpen:    false,
    pickerSlot:    'panel',     // which slot's catalog is open in the picker
    // Edit Dashboard mode — toggled via the Edit tab in the sub-nav.
    // When on, each panel cell's chrome (title + width buttons + remove
    // ×) becomes visible AND the SE-corner resize handle activates.
    // When off, the dashboard reads as a finished surface — no editing
    // widgets to clash with widget content (e.g. Vidiprinter filter
    // pills that previously got covered by the hover chrome).
    editMode:      false,
    // Widget options popover state — null when closed, widget id when open.
    // Set via openOptions(id, anchorEl). The shared #ls-options-popover
    // element in the template binds its x-show to this.
    optionsOpenFor: null,
    _optionsAnchor: null,   // the cog that opened it - focus goes back there on Escape
    // Pulled from DASHBOARD_INIT so the admin pill knows whether to render.
    get isAdmin() { return !!(window.DASHBOARD_INIT && window.DASHBOARD_INIT.isAdmin); },
    // Free plan → Analysis with The Race and The Lab locked, no picker, no
    // custom boards. Demo showcase is exempt (it's a full-experience shop window).
    get isFree() { return !this.demoShowcase && !!(window.DASHBOARD_INIT && window.DASHBOARD_INIT.isFree); },
    /** The Free format on the Broadcast board: a Free account, or an admin
     *  previewing it with ?free=1 (plan Phase 1). */
    get _boardFree() {
      if (this.isFree) return true;
      try { return !!this.isAdmin && new URLSearchParams(location.search).get('free') === '1'; } catch (_) { return false; }
    },
    /** Custom dashboards are admin-only until plan Phase 6 rebuilds them in
     *  the new look (core/dashboard_formats.USER_DASHBOARDS_ON). Off: no Mine
     *  section, no Create, no phone board, and no saved board is restored. */
    get _userBoardsOn() {
      return !!this.isAdmin || !!(window.LSFormat && window.LSFormat.userDashboardsOn);
    },
    _lockedTab: null,   // the locked tab whose "what's in it" card is open
    _lockedAt: null,    // that tab's rect: the card is fixed under it (the tab bar's scroller clips)
    /** What a locked tab holds, in a line (the card under it). */
    lockedTabCopy(id) {
      return ({
        analytics:  'The Lab - how your night was built: trajectory, value, who carried you.',
      })[id] || 'More of your night, with Pro.';
    },

    /** Anchor a sub-nav dropdown to its trigger button via position:fixed
     *  inline styles. Required because the .sp-tabs-scroll wrapper has
     *  overflow-x:auto, which per CSS spec implies overflow-y:auto — so
     *  absolutely-positioned dropdowns get clipped below the bar. Fixed
     *  positioning escapes the scroll context.
     *
     *  Containing-block twist: .sp-tabs-wrap has backdrop-filter set, and
     *  CSS spec says any of {transform, filter, backdrop-filter,
     *  perspective, will-change} on an ancestor makes IT the containing
     *  block for position:fixed descendants instead of the viewport. So
     *  we have to express the coords relative to the wrap, not the
     *  viewport — subtract the wrap's viewport offset.
     *
     *  Pass align='left' to align dropdown's left edge to button's left,
     *  or 'right' for the right edges (used by the Save popover so it
     *  doesn't run off the right side of the bar). */
    _anchorDropdown(buttonEl, align) {
      if (!buttonEl) return;
      const r  = buttonEl.getBoundingClientRect();
      // First try sibling lookup — sub-nav buttons have their dropdown
      // colocated. Fall back to the shared picker by ID for in-grid +
      // triggers (Phase 2 Edit-mode redesign) that live outside the
      // sub-nav and share ONE dropdown rendered inside .sp-tabs-wrap.
      let dd = buttonEl.parentElement && buttonEl.parentElement.querySelector('.ls-subnav-dropdown');
      if (!dd) dd = document.getElementById('ls-shared-picker');
      if (!dd) return;
      // LIFT IT OUT OF THE TAB STRIP, ONCE.
      //
      // Every sub-nav dropdown on the desktop dashboard - slate picker, mode
      // picker, Settings, Admin - rendered and painted NOTHING. The panel
      // measured 300x256 at the right coordinates, opaque, z-index 200,
      // `visible`, with seven children and real content; a screenshot of that
      // rectangle showed the board straight through it. Raising its z-index to
      // 2147483647 changed nothing, which is what ruled out a stacking fight:
      // you cannot out-rank a parent you are trapped inside. Moving the node up
      // to .sp-tabs-wrap made it appear immediately (2026-09-20, reported by
      // Xav against the live dashboard - "crucial live page").
      //
      // The markup had these panels nested one level deeper than this function
      // has always assumed: the line below anchors against .sp-tabs-wrap and
      // calls it "the nearest ancestor that establishes a containing block",
      // which is only true if the panel is a CHILD of it. So the fix is to put
      // it where the arithmetic already expects, rather than to teach the
      // arithmetic about a second home.
      //
      // Idempotent, and deliberately not document.body: staying inside the
      // wrap keeps the panel in the same subtree as its button, so any
      // click-outside handling still sees the two as related.
      const wrap = buttonEl.closest('.sp-tabs-wrap');
      if (wrap && dd.parentElement !== wrap) wrap.appendChild(dd);
      // Nearest ancestor that establishes a containing block for fixed
      // descendants. .sp-tabs-wrap is the practical choice; fall back to
      // viewport-equivalent (0,0) if no wrap found.
      const cbEl = dd.closest('.sp-tabs-wrap');
      const cbR  = cbEl ? cbEl.getBoundingClientRect() : { top: 0, left: 0, right: window.innerWidth };
      dd.style.top = (r.bottom - cbR.top + 4) + 'px';
      if (align === 'right') {
        dd.style.right = (cbR.right - r.right) + 'px';
        dd.style.left  = 'auto';
      } else {
        dd.style.left  = (r.left - cbR.left) + 'px';
        dd.style.right = 'auto';
      }
      // Spill-guard: after the browser lays out the dropdown, check if it
      // overflows the viewport's right edge (typical case: + Add Panel
      // button sits far right, left-aligning pushes the 260px menu off
      // screen — Xav 2026-05-30). If so, re-anchor against the button's
      // right edge so the menu opens leftward instead.
      // Shift by the MEASURED overflow, not by recomputing the anchor: the
      // wrap can sit wider than the viewport, and re-anchoring to the same
      // edge left the Settings and board pickers running off the right side
      // (Xav 2026-10-12, both themes, on prod).
      // ...and measure only once it is SHOWN: this runs in the click that
      // opens it, before Alpine's x-show has applied, and a hidden panel
      // measures 0x0 at x=0 - which read as "off the left edge" and pinned
      // every right-hand menu to its button's left, running off screen.
      let tries = 0;
      const guard = () => {
        const dr     = dd.getBoundingClientRect();
        if (!dr.width) { if (++tries < 10) requestAnimationFrame(guard); return; }
        const margin = 12;                          // viewport breathing room
        const vw     = document.documentElement.clientWidth || window.innerWidth;
        if (dr.right > vw - margin) {
          const cur = dd.style.right && dd.style.right !== 'auto'
            ? parseFloat(dd.style.right) : (cbR.right - dr.right);
          dd.style.left  = 'auto';
          dd.style.right = (cur + (dr.right - (vw - margin))) + 'px';
        } else if (dr.left < margin) {
          const cur = dd.style.left && dd.style.left !== 'auto'
            ? parseFloat(dd.style.left) : (dr.left - cbR.left);
          dd.style.right = 'auto';
          dd.style.left  = (cur + (margin - dr.left)) + 'px';
        }
      };
      requestAnimationFrame(guard);
    },
    // ── Widget options (cog) ─────────────────────────────────────────
    /** Does this widget declare an options partial? Drives whether the
     *  cog ⚙ button renders in panel chrome. Widgets opt in by setting
     *  `optionsPartial: 'widgets/<id>_options.html'` in their registry
     *  entry; non-opt-in widgets just don't show a cog. */
    hasOptions(id) {
      const w = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id);
      return !!(w && w.optionsPartial);
    },
    /** Toggle the options popover for a widget. Anchors to the clicked
     *  cog button via the same shared dropdown positioning helper used
     *  by the picker (different anchor element, same containing block
     *  math). */
    openOptions(id, anchorEl) {
      if (this.optionsOpenFor === id) {
        this.optionsOpenFor = null;
        return;
      }
      this.optionsOpenFor = id;
      this._optionsAnchor = anchorEl || null;
      this.$nextTick(() => {
        this._anchorOptions(anchorEl);
        // keyboard: focus moves into the popover (Broadcast audit run 2, FIT-16)
        const f = this._optionsFocusables();
        if (f.length) f[0].focus({ preventScroll: true });
      });
    },
    /** Escape closes the popover and hands focus back to its cog. */
    closeOptions() {
      this.optionsOpenFor = null;
      const a = this._optionsAnchor;
      this._optionsAnchor = null;
      if (a && a.isConnected) a.focus({ preventScroll: true });
    },
    _optionsFocusables() {
      const dd = document.getElementById('ls-options-popover');
      if (!dd) return [];
      return [...dd.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        .filter(el => !el.disabled && el.getClientRects().length);
    },
    /** Tab cycles inside the open popover instead of walking off into the board. */
    _trapOptions(e) {
      const f = this._optionsFocusables();
      if (!f.length) return;
      const i = f.indexOf(document.activeElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && (i === -1 || i === f.length - 1)) { e.preventDefault(); f[0].focus(); }
    },
    _anchorOptions(buttonEl) {
      if (!buttonEl) return;
      const r  = buttonEl.getBoundingClientRect();
      const dd = document.getElementById('ls-options-popover');
      if (!dd) return;
      const cbEl = dd.closest('.sp-tabs-wrap');
      const cbR  = cbEl ? cbEl.getBoundingClientRect() : { top: 0, left: 0, right: window.innerWidth };
      dd.style.top   = (r.bottom - cbR.top + 4) + 'px';
      // Right-align by default (cogs sit at the right edge of their
      // widget, so the popover grows leftward into the page).
      dd.style.right = (cbR.right - r.right) + 'px';
      dd.style.left  = 'auto';
      // Self-verifying edge correction (Xav 2026-06-12): the scoreboard
      // RAIL's cog is ~300px from the viewport's left edge, so the
      // right-aligned popover lands off-screen left. Predicting that
      // from button coords proved fragile under UI-scale zoom, so
      // instead we read the popover's REAL rendered rect and correct
      // with the real delta — immune to zoom units and containing-
      // block quirks. Two passes: flip to the cog's left edge, then a
      // hard clamp to 8px if even that still clips.
      let z = 1;
      try { z = parseFloat(document.documentElement.style.zoom || '1') || 1; } catch (e) {}
      const dr = dd.getBoundingClientRect();
      if (dr.left < 8) {
        dd.style.right = 'auto';
        dd.style.left  = (r.left - cbR.left) + 'px';
        const dr2 = dd.getBoundingClientRect();
        if (dr2.left < 8) {
          dd.style.left = ((parseFloat(dd.style.left) || 0) + (8 - dr2.left) / z) + 'px';
        }
      }
    },

    // ── Widget info (ⓘ) ──────────────────────────────────────────────
    // The single, consistent "what is this / how do I read it" affordance.
    // Sits next to the cog on every panel + KPI; replaces the inconsistent
    // native title tooltips. infoOpenFor mirrors optionsOpenFor; the shared
    // #ls-info-popover binds its x-show to it.
    infoOpenFor: null,
    /** Toggle the info popover for a widget, anchored to its ⓘ button. */
    /** Does this widget have anything to SAY? The ⓘ button used to render on
     *  every panel and open a popover that might be empty; now the title pill
     *  is the trigger, so a widget with no copy simply is not interactive —
     *  same rule as hasOptions() and the cog. */
    hasInfo(id) {
      const i = this.widgetInfo(id) || {};
      return !!(i.what || i.howTo || (i.badges || []).length);
    },
    /* HOVER-OPENED (Xav's call, 2026-08-21) — so showInfo must not TOGGLE the
       way openInfo does. Re-entering a pill you are already on would otherwise
       close the card under the pointer.

       The close is delayed: without it, moving from the pill down into the card
       crosses a gap and the mouseleave fires, so "Full docs →" is unclickable.
       The card cancels the timer on its own mouseenter. */
    _infoHideTimer: null,
    showInfo(id, anchorEl) {
      clearTimeout(this._infoHideTimer);
      this.optionsOpenFor = null;          // never both at once
      if (this.infoOpenFor !== id) {
        this.infoOpenFor = id;
        this.$nextTick(() => this._anchorPopoverEl(anchorEl,
          document.getElementById('ls-info-popover')));
      }
    },
    scheduleHideInfo() {
      clearTimeout(this._infoHideTimer);
      this._infoHideTimer = setTimeout(() => { this.infoOpenFor = null; }, 220);
    },
    cancelHideInfo() { clearTimeout(this._infoHideTimer); },
    openInfo(id, anchorEl) {
      if (this.infoOpenFor === id) { this.infoOpenFor = null; return; }
      this.optionsOpenFor = null;          // never both at once
      this.infoOpenFor = id;
      this.$nextTick(() => this._anchorPopoverEl(anchorEl,
        document.getElementById('ls-info-popover')));
    },
    /** Shared anchoring used by both the options + info popovers. Extracted
     *  from _anchorOptions so the two stay pixel-identical. */
    _anchorPopoverEl(buttonEl, dd) {
      if (!buttonEl || !dd) return;
      const r    = buttonEl.getBoundingClientRect();
      const cbEl = dd.closest('.sp-tabs-wrap');
      const cbR  = cbEl ? cbEl.getBoundingClientRect() : { top: 0, left: 0, right: window.innerWidth };
      dd.style.top   = (r.bottom - cbR.top + 6) + 'px';
      dd.style.right = (cbR.right - r.right) + 'px';
      dd.style.left  = 'auto';
      let z = 1;
      try { z = parseFloat(document.documentElement.style.zoom || '1') || 1; } catch (e) {}
      const dr = dd.getBoundingClientRect();
      if (dr.left < 8) {
        dd.style.right = 'auto';
        dd.style.left  = (r.left - cbR.left) + 'px';
        const dr2 = dd.getBoundingClientRect();
        if (dr2.left < 8) {
          dd.style.left = ((parseFloat(dd.style.left) || 0) + (8 - dr2.left) / z) + 'px';
        }
      }
    },
    /** Assemble the help card content for a widget from its registry
     *  entry. Returns a stable shape the popover renders, with graceful
     *  fallbacks so EVERY widget shows something coherent (consistent
     *  theme + tone) even before bespoke `help` copy is written.
     *
     *    eyebrow  — slot-derived kicker ("Graph-KPI" / "Panel")
     *    title    — widget name (user override respected)
     *    what     — one-line description (the `description` field)
     *    howTo    — "how to read it" (the optional `help` field)
     *    badges   — [{label, kind}] derived from requires / bestAt
     */
    widgetInfo(id) {
      const w = (window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id)) || {};
      const slot = w.slot || 'panel';
      const eyebrow = slot === 'kpi' ? 'Graph-KPI'
                    : slot === 'scoreboard' ? 'Scoreboard'
                    : 'Panel';
      const badges = [];
      const needsProj = w.requiresProjections
        || (Array.isArray(w.requires) && w.requires.includes('projections'));
      if (needsProj) badges.push({ label: 'Needs projections', kind: 'warn' });
      if (w.bestAt === 'multi') badges.push({ label: 'Best with 5+ lineups', kind: 'muted' });
      // Admin-editable copy (DB) WINS over the JS registration defaults, so
      // wording can be tuned from /admin/widgets without a code round-trip.
      // Falls back to the widget's own description/help if no override.
      const copy = (window.DASHBOARD_INIT && window.DASHBOARD_INIT.widgetCopy
                    && window.DASHBOARD_INIT.widgetCopy[id]) || {};
      // A widget that reads differently per sport says so for THIS sport
      // (helpBySport / descriptionBySport); it beats the admin copy, which is
      // one text for every sport - Stacks on NHL explained quarterbacks (Xav
      // 2026-10-10).
      const sport = String(this._sportId || '').toLowerCase();
      const bySport = (m) => (m && (m[sport] || m['*'])) || '';
      return {
        eyebrow,
        title: this.widgetTitle ? this.widgetTitle(id, w.title || id) : (w.title || id),
        what:  bySport(w.descriptionBySport) || copy.description || w.description || '',
        howTo: bySport(w.helpBySport) || copy.help || w.help || '',
        badges,
      };
    },
    /** Read a widget option. Falls back to the supplied default if the
     *  widget has no opts yet OR the key isn't set. Reactive — Alpine
     *  tracks the layout.widgetOpts object as a dependency.
     *
     *  Storage path: layout.widgetOpts[id][key]. Decoupled from
     *  layout.panels[] (Phase 1 refactor 2026-05-27) so options work
     *  for ANY widget slot — panels, KPIs, scoreboard alike. Was
     *  previously layout.panels.find(p => p.id === id).opts, which
     *  meant KPIs / scoreboard couldn't have options at all because
     *  they don't live in the panels array. */
    getOpt(id, key, fallback) {
      // The scoreboard is one rail across every tab: on Broadcast its view
      // choices (density, which games, Cards / Wire) are the board's, not the
      // tab's - The Action showed 4 compact cards, My Lineups 8, the rest 4
      // full, with only 4 ticked in the menu (Xav 2026-10-06).
      if (this._sbBoardKey(id, key)) {
        const v = this._sbBoardOpts[id + '.' + key];
        return v !== undefined ? v : fallback;
      }
      const opts = (this.layout.widgetOpts || {})[id];
      if (!opts) return fallback;
      return (opts[key] !== undefined) ? opts[key] : fallback;
    },
    _sbBoardOpts: (function () { try { return JSON.parse(localStorage.getItem('ls-sb-opts') || '{}') || {}; } catch (_) { return {}; } })(),
    _sbBoardKey(id, key) {
      return this.activeSkin === 'broadcast' && /^scoreboard/.test(String(id || ''))
        && ['density', 'follow', 'view', 'wire_scope'].includes(key);
    },
    /** Write a widget option. Replaces the widgetOpts blob immutably
     *  so Alpine reactivity fires and the change persists. */
    setOpt(id, key, value) {
      if (this._sbBoardKey(id, key)) {
        this._sbBoardOpts = { ...this._sbBoardOpts, [id + '.' + key]: value };
        try { localStorage.setItem('ls-sb-opts', JSON.stringify(this._sbBoardOpts)); } catch (_) {}
      }
      const widgetOpts = {
        ...(this.layout.widgetOpts || {}),
        [id]: { ...((this.layout.widgetOpts || {})[id] || {}), [key]: value },
      };
      this.layout = { ...this.layout, widgetOpts };
      this._saveOptsSoon();
    },

    /** Persist cog options. On a USER dashboard they ride the layout PUT
     *  (via _saveSoon). On a SYSTEM preset the layout is read-only, so
     *  _saveSoon no-ops — route the opts to the per-(user, sport, preset,
     *  section) preset_opts store instead, debounced so a slider drag
     *  coalesces into one write. This is the seam that stops the default
     *  experience silently forgetting cog changes on reload. */
    _saveOptsSoon() {
      if (this._activeDashboard.kind === 'user') { this._saveSoon(); return; }
      clearTimeout(this._presetOptsT);
      this._presetOptsT = setTimeout(() => this._persistPresetOpts(), 600);
    },

    // ── Widget rename (Xav 2026-06-14) ────────────────────────────────
    // Custom titles for panels + KPIs, stored as widgetOpts[id].title so
    // they ride the same per-dashboard persistence as every other opt.
    // Only reachable in editMode, which is itself only on user dashboards
    // (system presets are read-only), so renames never touch a preset.
    _renamingId:   null,
    _renameValue:  '',
    /** Display title: the user's override if set, else the built-in label
     *  the template passes as `fallback`. */
    widgetTitle(id, fallback) {
      // a preset's after-the-final name ("The night's big moments")
      if (this.activeSkin === 'broadcast' && !this.editMode && this._slateFinal) {
        const f = this.getOpt(id, 'finalTitle', '');
        if (f) return f;
      }
      const t = this.getOpt(id, 'title', '');
      return (t && String(t).trim()) ? t : fallback;
    },
    _startRename(id, fallback) {
      if (!this.editMode) return;
      this._renameValue = this.widgetTitle(id, fallback);
      this._renamingId  = id;
    },
    /** Commit the inline edit. Empty or unchanged-from-default clears the
     *  override so the title reverts cleanly to the built-in label. */
    _commitRename(id, fallback) {
      if (this._renamingId !== id) return;
      const v = (this._renameValue || '').trim().slice(0, 28);
      this.setOpt(id, 'title', (v && v !== fallback) ? v : '');
      this._renamingId = null;
    },
    _cancelRename() { this._renamingId = null; },

    /** Scoreboard position (Xav 2026-06-12, four-way 2026-06-14):
     *  'left'/'right' = vertical rail (takes ~326px of width from the
     *  panel grid), 'top'/'bottom' = full-width horizontal band. 'left'
     *  is the default. Persisted per-dashboard per-sport via
     *  widgetOpts.scoreboard.position. CSS only applies the rails
     *  ≥1024px; below that everything stacks as a band regardless. */
    get sbPos() {
      return this.getOpt('scoreboard', 'position', 'left');
    },
    setSbPos(v) {
      this.setOpt('scoreboard', 'position', v);
      // Grid height is measured from the grid's viewport offset, which
      // shifts when the scoreboard leaves/enters the flow above it.
      this.$nextTick(() => window.dispatchEvent(new Event('resize')));
    },
    /** Broadcast's scoreboard has two views (Xav 2026-10-05): the score
     *  cards, or the Score Wire - every score on the slate as a wire
     *  (widgets/score_wire.js) - with ALL / MINE. Saved like the position. */
    /*  One choice for the whole board: saved per section it flipped back to
     *  Cards on every tab switch (audit run 1, LIVE-17). The board-wide pick
     *  lives on this device; a section's own saved option is the fallback. */
    _sbViewPref:  (function () { try { return localStorage.getItem('ls-sb-view'); } catch (_) { return null; } })(),
    _sbScopePref: (function () { try { return localStorage.getItem('ls-sb-scope'); } catch (_) { return null; } })(),
    get sbView() { return this._sbViewPref || this.getOpt('scoreboard', 'view', 'cards'); },
    setSbView(v) {
      v = v === 'wire' ? 'wire' : 'cards';
      this._sbViewPref = v;
      try { localStorage.setItem('ls-sb-view', v); } catch (_) {}
      this.setOpt('scoreboard', 'view', v);
    },
    get sbWireScope() { return this._sbScopePref || this.getOpt('scoreboard', 'wire_scope', 'all'); },
    setSbWireScope(v) {
      v = v === 'mine' ? 'mine' : 'all';
      this._sbScopePref = v;
      try { localStorage.setItem('ls-sb-scope', v); } catch (_) {}
      this.setOpt('scoreboard', 'wire_scope', v);
    },
    // Once every game is final the Wire steps aside: its rows would only
    // repeat the final scores the Cards already show (Xav 2026-10-06).
    get _sbWireOn() { return this.activeSkin === 'broadcast' && this.sbView === 'wire' && !this.sbCounts.done; },
    /** The rail header's slate status: games live / to come / final, and
     *  done once every game is final (Broadcast). */
    get sbCounts() {
      const gs = this.allGames || [];
      let live = 0, soon = 0, final = 0;
      for (const g of gs) { const s = this.effStatus(g); if (s === 2) live++; else if (s >= 3) final++; else soon++; }
      return { live, soon, final, total: gs.length, done: gs.length > 0 && final === gs.length };
    },

    _dragSrcId:    null,
    _saveTimer:    null,
    gridHeight:    0,
    // Row floor (Xav 2026-06-14) — the grid keeps at least this many rows
    // even after a panel shrinks, so the freed space stays EMPTY instead of
    // the remaining 1fr rows re-expanding to refill the fixed grid height
    // (which made the tallest panel snap back to full height on shrink).
    // Captured = the layout's natural row count when it loads, so normal
    // viewing is unchanged; only resizing below the design height shows a gap.
    _rowsFloor:    1,
    _resizeBound:  null,

    // ── Drill-down modal state ─────────────────────────────────────────
    activeModal:   null,

    // ── Preset state ───────────────────────────────────────────────────
    /** Currently-active preset: 'broadcast' (Analysis) | 'broadcast_simple'
     *  | a locked format's board. Read on initWidgets() in priority order:
     *    1. URL ?preset=...
     *    2. the remembered preset for this sport (localStorage)
     *    3. 'broadcast' - Analysis, the default since plan Phase 2
     *  Switching presets means re-running the layout load against a
     *  different sport_overrides[sid][preset] bucket. Each preset retains
     *  its own customisations — they are SAVED PROFILES the user
     *  switches between, not throwaway templates. See memory
     *  dashboard-presets.md. */
    _preset:       'broadcast',
    // Crossfade flag for dashboard/preset flips (Xav 2026-06-14). True
    // while the main column is faded out so the in-place layout swap
    // happens invisibly — see _withSwitchTransition.
    _switching:    false,
    // Phase 6 (Xav 2026-05-31): 'tv' removed from the preset list. TV
    // is now a RENDER MODE toggle (Shift+T / Settings → TV mode /
    // ?tv=1 / fullscreen), not a layout source. Removing it from this
    // list means _resolveInitialPreset can't latch onto a legacy
    // localStorage 'tv' value and switchPreset rejects 'tv' as
    // unknown — both correct now.
    _PRESETS:      ['broadcast', 'broadcast_simple', 'broadcast_tiers', 'single_stat',
                    'single_stat_hr', 'single_stat_rbi', 'single_stat_k'],
    /** A preset id a URL or a remembered preference may name. The old boards
     *  (Simple / Analytical / Free / Spectator / Tiers) were deleted in plan
     *  Phase 3 (Xav 2026-10-11); a link naming one lands on Analysis. */
    _presetAllowed(pid) {
      return this._PRESETS.includes(pid);
    },

    /** Active dashboard pointer — Phase 2 of the user-dashboards
     *  architecture (Xav 2026-05-31, see memory
     *  [[user-dashboards-architecture]]).
     *
     *    { kind: 'system'|'user', id: 'simple'|'analytical'|'tv'|<uuid> }
     *
     *  For Phase 2 it tracks _preset 1:1 (system presets only — no UI
     *  yet to select a user dashboard). Phase 3 wires the Mode-picker
     *  rebuild that lets the user pick from `kind: 'user'`. Lookups
     *  that need to know "is this read-only?" use `_isSystemDashboard`
     *  rather than checking _preset directly so the migration to Phase
     *  3 only needs to touch one helper.
     */
    _activeDashboard: { kind: 'system', id: 'broadcast' },

    /** User-created dashboards for the current sport. Populated by
     *  _fetchUserDashboards on init + on sport change. Shape per item:
     *    { id, name, sport_id, created_at, updated_at, created_from,
     *      scoreboard_id, kpis, panels, widgetOpts }
     *  Phase 3 of the user-dashboards architecture. */
    userDashboards: [],

    /** Create-new-dashboard modal (plan Phase 6, Xav 2026-10-11): start from
     *  the slate's own board (Analysis, or the Tiers board), one of its tabs,
     *  one of your boards, or blank. */
    createDashboardOpen: false,
    createDashboardForm:  {
      name:    '',
      start:   'board',                 // 'board' | 'tab' | 'clone' | 'empty'
      tabId:   '',                      // start === 'tab'
      cloneId: '',                      // start === 'clone'
    },
    /** The system board a new one copies, and its tabs: {id, label, tabs[]}. */
    createFrom: { id: 'broadcast', label: 'Analysis', tabs: [] },
    /** Surfaces server-side errors back into the modal (e.g. missing
     *  name, unknown source). Cleared on every submit attempt. */
    createDashboardError: '',
    /** True while the POST is in flight — disables the Create button to
     *  prevent double-submits. */
    createDashboardBusy:  false,

    /** Phase 4 — section tab strip state (system presets only).
     *  Cached response from /api/system-presets/{preset}/{sport} so
     *  section switches re-materialise from memory instead of re-fetching.
     *  Re-populated whenever _loadLayoutFromSystemPreset runs. Null
     *  for user dashboards (they're single-grid). */
    _systemPresetData: null,

    /** Per-(section) cog-option overrides the user has saved on the ACTIVE
     *  system preset, keyed { [section_id]: widgetOpts }. System presets are
     *  read-only for LAYOUT (edit mode is hidden), but their view-mode cog —
     *  density / sort / scoreboard position — still writes widgetOpts. Those
     *  used to evaporate on reload because _saveSoon no-ops for presets. We
     *  now persist them to user_settings.dashboard_layout.preset_opts and
     *  overlay them on the preset's design defaults at load, so the DEFAULT
     *  experience (Simple/Analytical) remembers cog changes. Null for user
     *  dashboards (they persist widgetOpts in their own row via the PUT). */
    _presetOpts:  null,
    _presetOptsT: null,   // debounce timer for _persistPresetOpts

    /** The currently-active section ID inside the active system preset.
     *  One of 'right_now' | 'my_stuff' | 'slate' | 'analytics' (or
     *  whatever sections that preset declares). Reset to the preset's
     *  default_section when switching presets. Null for user
     *  dashboards. */
    _activeSection: null,

    /** True when the currently-active dashboard is a LiveSlates system
     *  preset (Simple / Analytical / TV). System presets are read-only:
     *  Edit / Reset / Save / Add KPI / Add Panel are all disabled with a
     *  "Customize to make your own" tooltip. User dashboards stay fully
     *  mutable. (Phase 2 of the user-dashboards architecture.) */
    get _isSystemDashboard() {
      return (this._activeDashboard && this._activeDashboard.kind) !== 'user';
    },
    /** The active board's own look, when it has one ('broadcast' for the
     *  Broadcast preview), else ''. live.html stamps it on
     *  <html data-ls-skin> so skin_broadcast.css can scope every rule under
     *  it — the standard boards never match, and render exactly as before. */
    /** The 2-up tablet packing - except Broadcast on a laptop, which is laid
     *  out to fit the screen itself (core/device_class.py BROADCAST_DESKTOP). */
    get tablet() {
      return this._tabletMq && !(this._bcDesktop && this.activeSkin === 'broadcast');
    },
    /** The board's look. The Broadcast look is every board's since plan
     *  Phase 3 (Xav 2026-10-11): Analysis and its tab lists, single stat, and
     *  custom boards when Phase 6 brings them back. The phone shell has its
     *  own look (_applySkin clears it there). */
    get activeSkin() {
      return 'broadcast';
    },
    /** Stamp the look on <html>: the Broadcast look everywhere but the phone
     *  shell, which has its own. live.html's head script paints the same
     *  before first paint, so this only corrects it when the shell turns on
     *  or off (a rotate across the breakpoint). */
    _applySkin() {
      document.documentElement.dataset.lsSkin = this.shellMode ? '' : 'broadcast';
    },
    /** The Broadcast slate bar's second line: platform · games · first start,
     *  then how many of your entries are on it. Same wording as the phone's
     *  slate switcher (shellSlateTag), so the two surfaces agree. */
    /** The Action's own line, under the tabs (Xav 2026-10-10, mock v2):
     *  where the slate is - live / final / up next - and, for anyone with a
     *  lineup, Mine / All for the Wire and Big Moments. It replaced "Around
     *  the slate" (big plays by players you don't have): Big Moments on All
     *  says that now, and with no lineup it only repeated the Wire. At the
     *  final whistle it carries the night's two ends (slateRecap). */
    get broadcastTickerOn() {
      // Simple's one screen carries the Wire too, so it gets the same line
      // (where the slate is, and Mine / All for the Wire)
      return this.activeSkin === 'broadcast' && ['right_now', 'simple'].includes(this._sectionKind(this._activeSection));
    },
    get actionTabLine() {
      const gs = this.allGames || [];
      const st = (g) => this.effStatus(g);
      const live = gs.filter(g => st(g) === 2).length, done = gs.filter(g => st(g) >= 3).length;
      const to = gs.length - live - done;
      const recap = this.slateRecap;
      if (recap) return { k: 'Final', bits: recap };
      const n = (v, w) => ({ k: '', v: v + ' ' + w });
      if (live) return { k: 'Live', bits: [{ k: '', v: live + ' of ' + gs.length + ' games on' }, done ? n(done, 'final') : null, to ? n(to, 'to come') : null].filter(Boolean) };
      if (done) return { k: 'Between games', bits: [n(done, 'final'), n(to, 'to come')] };
      return { k: 'Up next', bits: gs.length ? [n(gs.length, gs.length === 1 ? 'game' : 'games')] : [] };
    },
    /** The ticker row once every game is final: the night in one line. */
    get slateRecap() {
      const gs = this.allGames || [];
      if (!gs.length || !gs.every(g => this.effStatus(g) >= 3)) return null;
      const fmt = (v) => (typeof this._fmtScore === 'function' ? this._fmtScore(v) : (Number(v) || 0).toFixed(2));
      const bits = [];
      // Facts no tile on another tab already says: the top scorer and the
      // Dream Team are The Slate's and The Podium's, and sat here as well -
      // 44.30 seven times across the board (run 2, PREM-2 / UX-8). The
      // night's best game and its closest finish instead.
      // Not "Biggest game N FPts" either: that was row 1 of The Slate's Games
      // panel (audit v2 run 3, NUMBERS-5). The night's two ends instead: its
      // closest finish and its biggest win, each with its OT / shootout /
      // extra-innings mark - a game decided in OT is the close one (SPORT-7).
      void fmt;
      const label = (g) => String(g.away_team || '') + ' @ ' + String(g.home_team || '');
      const extra = (g) => { const m = /\b(\d*OT|SO|F\/\d+)\b/i.exec(String(g.status_text || '')); return m ? ' ' + m[1].toUpperCase() : ''; };
      const scored = gs.filter(g => g.home_score != null && g.away_score != null);
      const margin = (g) => Math.abs(g.home_score - g.away_score);
      const close = scored.slice().sort((a, b) => (margin(a) - margin(b)) || ((extra(b) ? 1 : 0) - (extra(a) ? 1 : 0)))[0];
      if (close) bits.push({ k: 'Closest finish', v: label(close) + ' ' + close.away_score + '-' + close.home_score + extra(close) });
      const wide = scored.slice().sort((a, b) => margin(b) - margin(a))[0];
      if (wide && wide !== close && margin(wide) > 0) bits.push({ k: 'Biggest win', v: label(wide) + ' ' + wide.away_score + '-' + wide.home_score + extra(wide) });
      // no "Games 12 final": the rail says it above, the director beside it -
      // "final" five times on one tab (Broadcast audit v2, PREM-2)
      return bits;
    },
    get broadcastSlateTag() {
      const s = this.selectedSlate;
      if (!s) return '';
      // The slate row doesn't always carry n_games; the games feed does.
      const withGames = s.n_games ? s : { ...s, n_games: (this.allGames || []).length };
      const parts = [this.shellSlateTag ? this.shellSlateTag(withGames) : ''];
      const n = (this.activeLineupRows || []).length;
      if (n) parts.push(n + (n === 1 ? ' lineup' : ' lineups'));
      return parts.filter(Boolean).join(' · ');
    },
    /** Can the current board be edited (drag/resize/add/remove/sections)? On the
     *  live site only USER dashboards are editable; system presets are read-only.
     *  In the BO Studio (P3) a system board IS editable — edits are captured into
     *  a DRAFT version (dashboard_layout_versions), never written to the live user
     *  path. Used by the edit-op guards + _sectionsEditable so the whole existing
     *  edit machinery unlocks in the Studio without touching live behaviour. The
     *  auto-save (_saveSoon) still no-ops here — it hard-gates on kind==='user'. */
    get _editable() {
      return this._studioPreview || !this._isSystemDashboard;
    },

    /** Display label for the active dashboard — used by the EDITING pill
     *  suffix. On a user dashboard it's the dashboard's NAME, not the
     *  lingering _preset value (which stays 'simple' for legacy readers and
     *  was leaking into the pill). System presets keep the preset id.
     *  (Xav 2026-06-14) */
    get _activeDashboardLabel() {
      if (this._activeDashboard && this._activeDashboard.kind === 'user') {
        const d = (this.userDashboards || []).find(x => x.id === this._activeDashboard.id);
        return ((d && d.name) || 'Dashboard').toUpperCase();
      }
      return (this._preset || '').toUpperCase();
    },

    /** One-shot wipe of pre-Phase-2 layout shape. Old shape stored
     *  per-user (sport, preset) customizations in
     *  user_settings.dashboard_layout.sport_overrides — that's no
     *  longer authoritative now that system presets are read-only and
     *  user dashboards live under .user_dashboards. Run once: stamps
     *  `dashboard_layout.v: 3` so the wipe doesn't re-fire on every
     *  load. Per memory [[no-user-migration-yet]], pre-launch — no
     *  banner, no preserve.
     *
     *  Best-effort: silent on failure (anonymous users can't write
     *  user_settings; that's fine, they have nothing to wipe).
     */
    async _maybeWipeLegacyLayout(sport_id) {
      if (!this._uid) return;
      // This one writes dashboard_layout with its OWN fetch, so the chokepoint
      // in _queueSettingsWrite never saw it: a phone opening a pre-v3 account
      // would rewrite the row and drop the desktop's active_section and
      // preset_opts (audit re-run, 2026-09-12). A migration is a DESKTOP job -
      // the phone just reads.
      if (this.shellMode) return;
      try {
        const r = await fetch('/api/settings', { credentials: 'same-origin' });
        if (!r.ok) return;
        const settings = await r.json();
        const layout = settings && settings.dashboard_layout;
        if (!layout || (layout.v || 0) >= 3) return;
        // Drop legacy keys; keep .user_dashboards (Phase 1) untouched.
        const next = {
          v:                3,
          user_dashboards:  layout.user_dashboards || {},
          active_dashboard: layout.active_dashboard || {},
        };
        await fetch('/api/settings', {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({ dashboard_layout: next }),
        });
        // (Cleanup Phase B: localStorage cache flush dropped. The
        // post-rebuild read paths never touch LSLayoutStore.readCached,
        // so stale cache entries are inert. Legacy live.html still
        // reads them but that's slated for removal in Phase C.)
      } catch (_) {
        /* silent — wipe is best-effort */
      }
    },

    async _loadLayoutFromSystemPreset(sport_id, preset_id) {
      const sid = (sport_id  || this._sportId || 'global').toLowerCase();
      const pid = (preset_id || this._preset  || 'broadcast').toLowerCase();
      try {
        const r = await fetch(`/api/system-presets/${encodeURIComponent(pid)}/${encodeURIComponent(sid)}`,
                              { credentials: 'same-origin' });
        if (!r.ok) return null;
        const data = await r.json();
        const preset = data && data.preset;
        if (!preset) return null;
        // Cache the full preset so section switches re-materialise
        // from memory (no extra API call).
        this._systemPresetData = preset;
        // Load the user's saved cog-option overrides for this preset once
        // (covers every section) so _materialiseSectionLayout can overlay
        // them on the design defaults — this is what makes cog changes on
        // the default experience survive a reload.
        this._presetOpts = await this._readPersistedPresetOpts(sid, pid);
        // Resolve which section to render: persisted choice (if it's
        // still enabled in this preset) > preset.default_section.
        const persisted = await this._readPersistedActiveSection(sid, pid);
        // Resolve against VISIBLE sections so a spectator never lands on a
        // hidden (lineup-gated) tab like single-stat MY STUFF.
        const sectionIds = this._visibleSections.filter(s => !s._locked).map(s => s.id);   // a locked tab (Free) is never landed on
        // THE SLATE (Cover) wins as the landing tab on a pre-game slate — the
        // "open the book at its cover" default. This also lazy-defers the heavy
        // widget board: the Cover has no layout to materialise, so the ~30-widget
        // grid isn't built until the user clicks into a real tab.
        const coverId = this._visibleSections.find(s => s._pregame) ? 'pregame' : null;
        const wantSection = coverId
          ? 'pregame'
          : ((persisted && sectionIds.includes(persisted))
              ? persisted
              : (sectionIds.includes(preset.default_section) ? preset.default_section : (sectionIds[0] || null)));
        this._activeSection = wantSection;
        if (wantSection === 'pregame') {
          // Curated, layout-less Cover — mark loaded with an empty grid so the
          // board render (the expensive part) is deferred to the first tab switch.
          this.layout = { scoreboard_id: null, kpis: [], panels: [], widgetOpts: {} };
          this.layoutLoaded = true;
          return null;
        }
        return this._materialiseSectionLayout(wantSection);
      } catch (_) {
        return null;
      }
    },

    /** BO Dashboard Studio (P2 — read-only preview). Paints a board in the Back
     *  Office with NO slate / SSE / preset bootstrap: the live init() is left
     *  untouched (the Studio template calls THIS via x-init instead, so nothing
     *  studio-only can ever run on the money page). The board arrives already
     *  materialised from the route (system_presets.get_preset_full shape) on
     *  window.LS_STUDIO; widget data stays at its empty defaults, so the board
     *  renders its frames — a pre-game-style preview. This is the P2 de-risk:
     *  prove the shared render (_dashboard_grid + _dashboard_scripts) works
     *  outside live.html. Kept as a SEPARATE entry point so swapping to a
     *  thin-harness renderer later (spec option B) touches only this method. */
    initStudioPreview(studio) {
      if (this._initialized) return;
      this._initialized  = true;
      this._studioPreview = true;                    // marker for future edit-mode gating
      if (!studio) return;
      this._sportId        = (studio.sport || 'global').toLowerCase();
      this.formatVariant   = studio.format_variant || '';
      this._activeDashboard = { kind: 'system', id: studio.preset };
      // Same shape _loadLayoutFromSystemPreset caches, so switchSection + the
      // section-tab strip work unchanged against the injected board.
      this._systemPresetData = {
        id:              studio.preset,
        default_section: studio.default_section,
        sections:        studio.sections || [],
        skin:            studio.skin || null,   // edit Broadcast in its own look
      };
      const first = studio.default_section
        || (studio.sections && studio.sections[0] && studio.sections[0].id)
        || null;
      this._activeSection = first;
      if (first) this._materialiseSectionLayout(first);
      this.layoutLoaded = true;
      // Sample data (P5): populate the preview frames with a demo frame so the
      // admin judges layouts against realistic content, not empty boxes.
      if (studio.sample_demo) this._loadStudioSample();
    },

    /** Studio (P5): feed a demo frame into the preview via the SAME _applyData
     *  path the live board + demo showcase use — no fabricated data shapes. A
     *  late frame (~85% through) gives rich accumulated stats. Best-effort: on
     *  any failure the frames just stay empty. */
    async _loadStudioSample() {
      const s = window.LS_STUDIO || {};
      if (!s.sample_demo) return;
      try {
        // Synthetic slate so applyData's sport-specific paths resolve — there's
        // no real slate in the Studio. template.format_variant carries the
        // preset's format so the _formatVariant getter resolves it and scores
        // render in the board's units (HR / RBI / K integer, not FPts 2dp) —
        // matching the live board exactly.
        this.selectedSlate = { id: 'studio-sample', sport_id: s.sport,
                               name: 'Sample', platform_id: 'fanteam',
                               template: { format_variant: s.format_variant || '' } };
        const t = Math.max(1, Math.floor((s.sample_duration || 600) * 0.85));
        // fmt = the board's format so the demo is SCORED in it (single-stat →
        // real HR/RBI/K counts, not FPts relabelled). Standard/tiers = full FPts.
        const data = await fetch(`/api/demo-live/${encodeURIComponent(s.sample_demo)}?t=${t}`
                                 + `&fmt=${encodeURIComponent(s.format_variant || '')}`)
          .then(r => r.json());
        this._applyData(data);
        this.loading         = false;
        this._studioSampleOn = true;
      } catch (e) { /* frames stay empty — sample is a nicety */ }
    },

    /** Studio (P3): capture the fully-edited board — every section, live edits
     *  folded in — and POST it as a new DRAFT version to dashboard_layout_versions.
     *  A draft is NOT published: it's invisible to the live site until the publish
     *  step (P4). _sectionsForSave already flattens each section to storage shape;
     *  we just re-key it by section id into the {sections:{<id>:{…}}} column shape. */
    async saveStudioDraft() {
      if (!this._studioPreview || this._studioSaving) return;
      const studio = window.LS_STUDIO || {};
      const arr = this._sectionsForSave();
      const sections = {};
      for (const s of arr) {
        sections[s.id] = _keepPregame({
          scoreboard_id: s.scoreboard_id || null,
          kpis:          s.kpis          || [],
          panels:        s.panels        || [],
          widgetOpts:    s.widgetOpts    || {},
        }, s);
      }
      // section_order carries the tab set + labels + order (P5) so the published
      // board can reorder / add / drop tabs vs the preset's fixed section list.
      const section_order = arr.map(s => ({ id: s.id, label: s.label }));
      this._studioSaving  = true;
      this._studioSaveMsg = 'Saving…';
      try {
        const r = await fetch('/api/admin/dashboard-studio/save-draft', {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({
            preset_id: studio.preset,
            sport_id:  studio.sport,
            label:     (this._studioLabel || '').trim() || null,
            layout:    { sections, section_order },
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { this._studioSaveMsg = (data && data.error) || `Save failed (${r.status})`; return; }
        this._studioSaveMsg = `Draft v${data.version_no} saved`;
        this._studioLabel   = '';
        this.editMode       = false;
        if (this._studioVersionsOpen) this.openStudioVersions();  // refresh the list
      } catch (e) {
        this._studioSaveMsg = 'Network error — try again';
      } finally {
        this._studioSaving = false;
      }
    },

    /** Studio (P4): open the versions drawer + (re)load this board's saved
     *  versions (draft / published / archived), newest first. */
    async openStudioVersions() {
      if (!this._studioPreview) return;
      const s = window.LS_STUDIO || {};
      this._studioVersionsOpen = true;
      this._studioVersionsBusy = true;
      try {
        const r = await fetch('/api/admin/dashboard-studio/versions'
          + `?preset_id=${encodeURIComponent(s.preset)}&sport_id=${encodeURIComponent(s.sport)}`,
          { credentials: 'same-origin' });
        const data = await r.json().catch(() => ({}));
        this._studioVersions = (data && data.versions) || [];
      } catch (e) {
        this._studioVersions = [];
      } finally {
        this._studioVersionsBusy = false;
      }
    },

    /** Publish a version → it goes live through the P1 override resolution
     *  (WEB picks it up within the override cache TTL). ROLLBACK is just
     *  publishing an older version — same call. */
    async publishStudioVersion(id) {
      if (!this._studioPreview || this._studioSaving) return;
      this._studioSaving  = true;
      this._studioSaveMsg = 'Publishing…';
      try {
        const r = await fetch('/api/admin/dashboard-studio/publish', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body:   JSON.stringify({ id }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { this._studioSaveMsg = (data && data.error) || `Publish failed (${r.status})`; return; }
        this._studioSaveMsg = 'Published — live within ~30s';
        this._studioDiffId  = null;
        this._studioDiff    = null;
        await this.openStudioVersions();
      } catch (e) {
        this._studioSaveMsg = 'Network error — try again';
      } finally {
        this._studioSaving = false;
      }
    },

    /** Studio (P5): diff a saved version against the LIVE board before publishing.
     *  "Live" is what the Studio loaded — get_preset_full already folds in the
     *  currently-published override (else the code baseline) — i.e. LS_STUDIO
     *  .sections. Pure: returns {tabsRemoved, sections:[…per changed section…],
     *  hasChanges}. New tabs surface as sections flagged isNew. */
    _studioComputeDiff(v) {
      const live = (window.LS_STUDIO || {}).sections || [];
      return window.LSStudioDiff.compute(live, (v && v.layout) || {});
    },

    /** Studio (P5): fetch a version's layout rendered as a SYSTEM_PRESETS_V2
     *  Python snippet (export-to-code) and open the copy panel. */
    async exportStudioVersion(v) {
      try {
        const r = await fetch(`/api/admin/dashboard-studio/export?id=${encodeURIComponent(v.id)}`,
          { credentials: 'same-origin' });
        const data = await r.json().catch(() => ({}));
        this._studioExportCode = (data && data.python) || '# export failed';
      } catch (e) {
        this._studioExportCode = '# export failed — network error';
      }
      this._studioExportOpen = true;
    },
    _copyStudioExport() {
      try {
        navigator.clipboard.writeText(this._studioExportCode);
        this._studioSaveMsg = 'Copied to clipboard';
      } catch (e) { /* clipboard blocked — the textarea is selectable anyway */ }
    },

    /** Toggle the diff panel for a version (Review-before-publish). */
    reviewStudioVersion(v) {
      if (this._studioDiffId === v.id) { this._studioDiffId = null; this._studioDiff = null; return; }
      this._studioDiff   = this._studioComputeDiff(v);
      this._studioDiffId = v.id;
    },

    /** Load a saved version's layout into the editor (view / continue editing).
     *  The version stores per-section layouts keyed by id; we overlay them onto
     *  the board's baseline sections (for labels + order), falling back to the
     *  baseline layout for any section the version doesn't define. */
    loadStudioVersion(v) {
      const s       = window.LS_STUDIO || {};
      const vlayout = (v && v.layout) || {};
      const vs      = vlayout.sections || {};
      const baseById = {};
      for (const b of (s.sections || [])) baseById[b.id] = b;
      // Prefer the version's section_order (carries custom tabs + reordering +
      // labels); fall back to the baseline section list for older drafts.
      const seq = (vlayout.section_order && vlayout.section_order.length)
        ? vlayout.section_order.map(o => ({
            id: o.id, label: o.label || (baseById[o.id] && baseById[o.id].label) || o.id }))
        : (s.sections || []).map(b => ({ id: b.id, label: b.label }));
      const EMPTY = { scoreboard_id: null, kpis: [], panels: [], widgetOpts: {} };
      const sections = seq.map(({ id, label }) => ({
        id, label,
        order: (baseById[id] && baseById[id].order) || 99,
        layout: vs[id]
          ? { scoreboard_id: vs[id].scoreboard_id || null,
              kpis:   vs[id].kpis   || [],
              panels: vs[id].panels || [],
              widgetOpts: vs[id].widgetOpts || {} }
          : (baseById[id] ? baseById[id].layout : { ...EMPTY }),
      }));
      this._systemPresetData = { id: s.preset, default_section: s.default_section, sections };
      const first = (this._activeSection && sections.some(x => x.id === this._activeSection))
        ? this._activeSection
        : (s.default_section || (sections[0] && sections[0].id) || null);
      this._activeSection = first;
      if (first) this._materialiseSectionLayout(first);
      this._studioSaveMsg      = `Loaded v${v.version_no}`;
      this._studioVersionsOpen = false;
    },

    /** Delete a version. Draft cleanup, or remove a published override → the
     *  live board reverts to the built-in code baseline. */
    async deleteStudioVersion(v) {
      if (!this._studioPreview) return;
      const ok = await this._confirm({
        title:   'Delete version',
        message: v.status === 'published'
          ? `Delete published v${v.version_no}? The live board reverts to the built-in baseline.`
          : `Delete v${v.version_no}? This can't be undone.`,
        confirmLabel: 'Delete',
        danger:       true,
      });
      if (!ok) return;
      try {
        const r = await fetch('/api/admin/dashboard-studio/delete', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body:   JSON.stringify({ id: v.id }),
        });
        if (r.ok) await this.openStudioVersions();
      } catch (e) { /* best-effort; drawer stays open */ }
    },

    /** Pull one section's layout from the cached _systemPresetData and
     *  set it as the active layout. Returns the materialised layout
     *  object (or null if cache missing / section not found). Used by
     *  switchSection AND by the initial load. */
    _materialiseSectionLayout(section_id) {
      const preset = this._systemPresetData;
      if (!preset || !section_id) return null;
      const section = (preset.sections || []).find(s => s.id === section_id);
      if (!section || !section.layout) return null;
      const sid = this._sportId;
      const layout = _migrateLayout(section.layout, sid);
      // Overlay the user's saved cog options for THIS section on top of the
      // preset's design-time widgetOpts. Deep-merge per widget id so a saved
      // key (e.g. scoreboard density) doesn't wipe the preset's other keys,
      // and a preset shipping a NEW default key is preserved unless overridden.
      const saved = (this._presetOpts && this._presetOpts[section_id]) || null;
      if (saved) {
        const base = layout.widgetOpts || {};
        const merged = { ...base };
        for (const [wid, o] of Object.entries(saved)) {
          // Titles are the preset's: a preset cannot be renamed (no edit
          // mode), but a save writes the whole widgetOpts back, so a copy of
          // an old title ("On now") outlived the preset's own ("On Now").
          const { title, finalTitle, ...rest } = o || {};
          void title; void finalTitle;
          merged[wid] = { ...(base[wid] || {}), ...rest };
        }
        layout.widgetOpts = merged;
      }
      // Phone curation (Xav 2026-07-10): stacking the full ~15-widget analytical
      // grid on a phone is an endless scroll. On mobile, render a lean, curated
      // subset instead — same preset + widgets, we just filter which ones show.
      // A custom board on the phone (Phase 5) renders its PHONE layout for the
      // tab instead - the saved one, or the automatic one derived from the
      // desktop tab. Render-time only: the cached section (desktop) is untouched,
      // and nothing on the phone saves layouts (_saveSoon / the sync below bail).
      const finalLayout = this._phoneBoardOn
        ? window.LSPhoneLayout.project(layout, this._phoneEntry(section_id))
        : (this.mobile && this._mobileCurationOn) ? this._mobileCurate(layout) : layout;
      this.layout = finalLayout;
      this.layoutLoaded = true;
      // Capture the design row count as the floor so a later shrink leaves
      // empty space rather than refilling. Persisted floor (layout.rows)
      // wins if larger, so a saved gap survives reload.
      this._rowsFloor = Math.max(this._maxPanelRows(finalLayout.panels), (finalLayout.rows | 0) || 0) || 1;
      return finalLayout;
    },

    /** Phone-curated layout — a handful of high-value widgets in a phone-first
     *  order + a trimmed KPI strip, instead of the whole analytical grid. This
     *  is the "curate, don't duplicate" approach: NO separate mobile dashboard,
     *  we just filter which widgets the existing grid renders (cellOrder reads
     *  the panels array position, so the sort below IS the stack order). Long
     *  lists like All Slate Players are simply dropped rather than shrunk.
     *  Widget id whitelists — extend per-sport later if needed. */
    get _mobileCurationOn() { return !!this.demoShowcase; },   // prototype scope: NFL demo. Drop guard to roll out.
    _mobileCurate(layout) {
      // The analytical preset is sectioned (tabs), so we DON'T whitelist (that
      // could empty a tab) — we drop the two things that wreck phone UX: the big
      // All-Players list (endless scroll) and the chart widgets (unreadable at
      // phone width). The wire, tables + top-N lists stay. Extend HIDE as new
      // chart widgets land.
      // One list with the phone board's automatic layout (live_v2/phone_layout.js).
      const HIDE = new Set(window.LSPhoneLayout.HIDE);
      const panels = (layout.panels || []).filter(p => !HIDE.has(p.id));
      // KPI strip: keep the 3 headline tiles (fall back to the first 3) so it
      // doesn't wrap into a wall on a phone.
      const KEEP_KPI = ['kpi_my_best_lineup', 'kpi_top_scorer', 'kpi_best_value'];
      let kpis = (layout.kpis || []).filter(id => KEEP_KPI.includes(id));
      if (!kpis.length) kpis = (layout.kpis || []).slice(0, 3);
      return { ...layout, panels, kpis };
    },
    /** Re-materialise the active system-preset section when the device class
     *  flips (rotate / resize across the mobile breakpoint), so the curated vs
     *  full layout swaps live. No-op for user dashboards / no active preset. */
    _reflowForDevice() {
      if (this._systemPresetData && this._activeSection) {
        this._materialiseSectionLayout(this._activeSection);
      }
    },

    // ── Custom boards on the phone (Phase 5, Xav 2026-09-11) ──────────
    // "Nothing we do on the phone can affect the desktop version." A board is
    // shared data (the phone shows the one you built), but on the phone:
    //   · which board / tab you're on is remembered ON THE DEVICE, never in the
    //     account-wide pointers the desktop reads (_readPersisted* / _persist*);
    //   · the layout rendered is the board's phone layout (_materialiseSection-
    //     Layout → LSPhoneLayout.project), never folded back into the desktop
    //     section (_syncLayoutIntoActiveSection) and never saved (_saveSoon).
    /** The dashboard object behind the active user board (sections + phone),
     *  as last loaded. Null on a system preset. */
    _activeUserDash: null,
    /** The phone shell is showing a custom board (vs the LiveSlates Live tab). */
    get _phoneBoardOn() {
      return !!this.shellMode && this._activeDashboard.kind === 'user';
    },
    /** This tab's saved phone layout, or undefined (automatic). */
    _phoneEntry(section_id) {
      const d = this._activeUserDash;
      return (d && d.phone && d.phone[section_id]) || undefined;
    },
    _deviceKey(kind, scope) {
      return 'ls_phone_' + kind + ':' + (this._uid || 'anon') + ':' + String(scope || '').toLowerCase();
    },
    _deviceRead(kind, scope) {
      try { return localStorage.getItem(this._deviceKey(kind, scope)) || ''; } catch (_) { return ''; }
    },
    _deviceWrite(kind, scope, value) {
      try {
        if (value) localStorage.setItem(this._deviceKey(kind, scope), value);
        else localStorage.removeItem(this._deviceKey(kind, scope));
      } catch (_) { /* private mode - the phone just forgets */ }
    },

    // ── The editor's Phone canvas (desktop, user boards) ──────────────
    // Edit mode gets a Desktop | Phone switch. Phone swaps the grid for a
    // phone-shaped list of the tab's panels: order, Full / Half width, on or
    // off the phone. Its state is the dashboard's `phone` map, NEVER
    // this.layout, and it saves on its own PUT ({phone}) - so the desktop
    // layout can't be touched from here, and a desktop save can't clear it.
    editCanvas: 'desktop',            // 'desktop' | 'phone'
    _phoneSaving: false,
    _phoneSaved:  false,
    _phoneSaveErr: '',
    _phoneSavedT: null,
    _phoneSaveT:  null,
    /** The switch shows on your own boards, editing on a desktop. */
    get _phoneCanvasAvail() {
      return this.editMode && !this._isSystemDashboard && !this._studioPreview && !this.touchDevice;
    },
    get _phoneCanvasOn() { return this._phoneCanvasAvail && this.editCanvas === 'phone'; },
    get _phoneCanvasCustom() {
      return window.LSPhoneLayout.isCustom(this._phoneEntry(this._activeSection));
    },
    /** The phone list for the active tab, against the LIVE desktop panels
     *  (unsaved desktop edits included), with display names. */
    get phoneCanvasList() {
      const L = window.LSPhoneLayout;
      return L.resolve(this.layout.panels, this._phoneEntry(this._activeSection))
              .map(p => ({ ...p, name: this.widgetInfo(p.id).title }));
    },
    /** Desktop panels not on the phone. `chart` flags the ones automatic
     *  leaves off, so the tray can say why. */
    get phoneCanvasOff() {
      const L = window.LSPhoneLayout;
      const list = L.resolve(this.layout.panels, this._phoneEntry(this._activeSection));
      return L.offPhone(this.layout.panels, list).map(id => ({
        id, name: this.widgetInfo(id).title, chart: L.HIDE.includes(id),
      }));
    },
    get phoneCanvasKpis() {
      return (this.layout.kpis || []).slice(0, window.LSPhoneLayout.MAX_KPIS)
        .map(id => this.widgetInfo(id).title);
    },
    /** Any change saves the tab's phone list - from then on it's custom. */
    _phoneEdit(mutate) {
      const d = this._activeUserDash;
      const sec = this._activeSection;
      if (!d || !sec) return;
      const list = window.LSPhoneLayout.resolve(this.layout.panels, this._phoneEntry(sec))
                     .map(p => ({ id: p.id, span: p.span }));
      mutate(list);
      d.phone = { ...(d.phone || {}), [sec]: { panels: list } };
      this._activeUserDash = { ...d };
      this._savePhoneSoon();
    },
    phoneMove(id, dir) {
      this._phoneEdit(list => {
        const i = list.findIndex(p => p.id === id);
        const j = i + (dir === 'up' ? -1 : 1);
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
      });
    },
    phoneSpan(id, span) {
      this._phoneEdit(list => { const p = list.find(x => x.id === id); if (p) p.span = span; });
    },
    phoneOff(id) {
      this._phoneEdit(list => { const i = list.findIndex(p => p.id === id); if (i >= 0) list.splice(i, 1); });
    },
    phoneAdd(id) {
      this._phoneEdit(list => { if (!list.some(p => p.id === id)) list.push({ id, span: 'full' }); });
    },
    /** Back to automatic for this tab. */
    phoneReset() {
      const d = this._activeUserDash;
      const sec = this._activeSection;
      if (!d || !sec || !d.phone || !d.phone[sec]) return;
      const next = { ...d.phone };
      delete next[sec];
      this._activeUserDash = { ...d, phone: next };
      this._savePhoneSoon();
    },
    _savePhoneSoon() {
      if (!this._uid) return;
      clearTimeout(this._phoneSaveT);
      this._phoneSaved = false;
      this._phoneSaveT = setTimeout(() => this._savePhone(), 500);
    },
    async _savePhone() {
      const d = this._activeUserDash;
      if (!d || !d.id || this.shellMode) return;
      this._phoneSaving = true;
      this._phoneSaveErr = '';
      try {
        const r = await fetch(`/api/user-dashboards/${encodeURIComponent(d.id)}`, {
          method:      'PUT',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({ phone: d.phone || {} }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'not saved');
        // Keep the picker's copy current so a board switch + back shows it.
        if (data.dashboard) {
          const idx = (this.userDashboards || []).findIndex(x => x.id === data.dashboard.id);
          if (idx >= 0) this.userDashboards[idx] = data.dashboard;
        }
        this._phoneSaved = true;
        clearTimeout(this._phoneSavedT);
        this._phoneSavedT = setTimeout(() => { this._phoneSaved = false; }, 1800);
      } catch (e) {
        this._phoneSaveErr = "Phone layout not saved - it'll retry on your next change.";
      } finally {
        this._phoneSaving = false;
      }
    },

    // ── Phase 3: user-dashboards picker / CRUD ────────────────────────

    /** Load user dashboards for the current (or supplied) sport into
     *  this.userDashboards. Newest-updated first. Silent on failure —
     *  user just sees an empty "Mine" section in the picker. */
    async _fetchUserDashboards(sport_id) {
      if (!this._uid || !this._userBoardsOn) { this.userDashboards = []; return; }
      const sid = (sport_id || this._sportId || '').toLowerCase();
      const url = sid
        ? `/api/user-dashboards?sport_id=${encodeURIComponent(sid)}`
        : '/api/user-dashboards';
      try {
        const r = await fetch(url, { credentials: 'same-origin' });
        if (!r.ok) { this.userDashboards = []; return; }
        const data = await r.json();
        // Only boards built for this slate's family: a Classic board's salary
        // and leverage panels mean nothing on a Tiers slate, and back. A board
        // from before the field existed is a Classic one.
        const fam = this._boardFamily;
        this.userDashboards = (Array.isArray(data && data.dashboards) ? data.dashboards : [])
          .filter(d => (d.format || 'standard') === fam);
      } catch (_) {
        this.userDashboards = [];
      }
    },

    /** Load a single user dashboard into this.layout. Returns the
     *  dashboard object or null. Same shape contract as
     *  _loadLayoutFromSystemPreset so the renderer doesn't care which
     *  kind of dashboard is active. */
    async _loadLayoutFromUserDashboard(dashboard_id) {
      // User dashboards persist their own widgetOpts in their row (via the
      // PUT), so the preset-opts overlay must never apply here. Clear it at
      // the loader so no caller can leak a stale preset overlay onto a user
      // dashboard's sections.
      this._presetOpts = null;
      try {
        const r = await fetch(`/api/user-dashboards`, { credentials: 'same-origin' });
        if (!r.ok) return null;
        const data = await r.json();
        const list = (data && data.dashboards) || [];
        const dash = list.find(d => d.id === dashboard_id);
        if (!dash) return null;
        // Sectioned model (Xav 2026-06-14): user dashboards now own a
        // `sections` list — same shape as a system preset — so the tab
        // strip + switchSection + _materialiseSectionLayout all work for
        // them too. The difference is `editable:true` (add/rename/delete
        // tabs). Legacy single-grid dashboards are wrapped server-side,
        // but guard here too.
        const secs = (dash.sections && dash.sections.length) ? dash.sections : [{
          id: 'main', label: 'Main',
          scoreboard_id: dash.scoreboard_id, kpis: dash.kpis,
          panels: dash.panels, widgetOpts: dash.widgetOpts,
        }];
        this._systemPresetData = {
          editable:        true,
          default_section: secs[0].id,
          // origin: the system tab a copied tab came from ('right_now' etc.),
          // so tab-keyed features (The Action's ticker) follow the copy.
          sections: secs.map(s => ({
            id: s.id, label: s.label, origin: s.origin || null,
            layout: _keepPregame({
              scoreboard_id: s.scoreboard_id || null,
              kpis:          s.kpis          || [],
              panels:        s.panels        || [],
              widgetOpts:    s.widgetOpts    || {},
            }, s),
          })),
        };
        // Kept whole for the phone layout (dash.phone) - read by the phone
        // board render and the editor's Phone canvas.
        this._activeUserDash = dash;
        const sid       = this._sportId;
        const persisted = await this._readPersistedActiveSection(sid, dashboard_id);
        const ids       = this._systemPresetData.sections.map(s => s.id);
        this._activeSection = (persisted && ids.includes(persisted)) ? persisted : secs[0].id;
        this._materialiseSectionLayout(this._activeSection);
        return dash;
      } catch (_) {
        return null;
      }
    },

    /** Single switcher for any dashboard kind. Updates _activeDashboard,
     *  persists the choice per-sport, and triggers a layout reload.
     *  Use this instead of switchPreset for the post-Phase-3 path. */
    async switchToActiveDashboard(kind, id) {
      const k = (kind === 'user') ? 'user' : 'system';
      const newId = String(id || '').trim();
      if (!newId) return;
      // No-op if already active.
      if (this._activeDashboard.kind === k && this._activeDashboard.id === newId) {
        return;
      }
      // System presets are read-only — the Edit pill is hidden, so we
      // must also drop editMode if it happened to be on (user was
      // editing a user dashboard then switched to a preset). Without
      // this the page stays in editMode state with no visible exit.
      if (k === 'system' && this.editMode) {
        this.editMode = false;
      }
      // Persist the choice so F5 restores it. TWO mechanisms, belt + braces:
      //  1. ?preset= in the URL — the ROBUST one. _resolveInitialPreset reads it
      //     FIRST and synchronously at first paint, with no dependency on the
      //     async slate-load → sport → watcher → localStorage chain that kept
      //     silently reverting to Simple on reload. (Xav 2026-07-09)
      //  2. localStorage per-sport preference — cross-tab / next-session nicety.
      // System preset → stamp the preset id; user dashboard → clear it so the
      // server active-dashboard pointer restores the user board instead.
      // Not from the phone shell: the phone's choice is device-local
      // (_persistActiveDashboard), and on an iPad the URL + preference belong
      // to the landscape board, which is the desktop.
      if (this.shellMode) {
        /* device-local - see _persistActiveDashboard */
      } else if (k === 'system' && this._PRESETS.includes(newId)) {
        this._writePresetToUrl(newId);
        LSLayoutStore.writePreference(this._uid, this._sportId, newId);
      } else {
        this._writePresetToUrl(null);
      }
      // Reload layout against the new active — inside a crossfade so the
      // flip reads as a clean page transition, not a piecemeal swap.
      await this._withSwitchTransition(async () => {
        // ALL visible-state flips happen here, while the column is
        // invisible (Xav 2026-06-14):
        //  • _activeDashboard drives _isSystemDashboard, which toggles the
        //    section-tab strip. Toggling it on still-visible content
        //    reflows the panels (a ~row of height appears/disappears), and
        //    the panels' height-keyed @container font tiers snap with it —
        //    the system↔user "fonts shrink then flip" flash.
        //  • _preset drives <html data-preset>, which bumps headline fonts
        //    on Simple. Same reason it must change while invisible.
        this._activeDashboard = { kind: k, id: newId };
        if (k === 'system' && this._PRESETS.includes(newId)) {
          this._preset = newId;
        }
        await this._persistActiveDashboard();
        if (k === 'system') {
          await this._loadLayoutFromSystemPreset(this._sportId, newId);
        } else {
          // Reset section state; _loadLayoutFromUserDashboard rebuilds it
          // from the dashboard's own sections (Xav 2026-06-14 — user
          // dashboards are sectioned + editable now).
          this._systemPresetData = null;
          this._activeSection    = null;
          this._presetOpts       = null;   // user dashboards persist their own widgetOpts
          this._activeUserDash   = null;
          await this._loadLayoutFromUserDashboard(newId);
        }
      });
    },

    /** Switch the active section inside the current system preset.
     *  No-op for user dashboards (single-grid model). Re-materialises
     *  the layout from cached preset data — no API call — then
     *  persists the choice per (sport, preset). */
    async switchSection(section_id, opts = {}) {
      if (!this._systemPresetData) return;
      if (!section_id || section_id === this._activeSection) return;
      // A tab picked by hand in TV mode holds the screen for a while.
      if (this.tvMode && !opts.auto) { this._tvPinnedUntil = Date.now() + TV_PIN_S * 1000; this._tvElapsed = 0; this._tvPct = 0; }
      // a locked tab (Free) opens its card, never the tab - from a deep link,
      // a remembered tab or a keyboard path alike
      const sec = (this._visibleSections || []).find(s => s.id === section_id);
      if (sec && sec._locked) { this._lockedTab = section_id; return; }
      this._lockedTab = null;
      // an open options menu does not follow you to the next tab - it stayed
      // over the rail and covered its own cog (run 2, UX-1)
      this.optionsOpenFor = null;
      // Pre-game tab: synthetic, layout-less curated component — just activate
      // it (no layout to materialise, nothing to persist). (Pre-Slate slice 1.)
      if (section_id === 'pregame') { this._activeSection = 'pregame'; return; }
      // Editable (user) dashboards: stash the outgoing section's live edits
      // back into _systemPresetData before _materialiseSectionLayout swaps
      // this.layout out from under it — otherwise unsaved edits are lost on
      // a tab switch.
      if (this._editable) this._syncLayoutIntoActiveSection();
      const layout = this._materialiseSectionLayout(section_id);
      if (!layout) return;
      this._activeSection = section_id;
      // NOT awaited. Cloudflare RUM named this button as the worst interaction
      // in the product - four of the five slowest INP samples are this tab,
      // between 328ms and 1,416ms - and awaiting a settings round-trip inside
      // the click handler keeps the network on the interaction's critical path
      // for a write nothing is waiting on. It remembers which tab you were on;
      // the tab has already switched. _addSection (below) already fires it
      // without awaiting, so this aligns the two call sites rather than
      // inventing a rule.
      // The TV's own rotation is not a preference - writing it would save the
      // user's tab every TV_DWELL_S seconds for as long as the TV is on.
      if (opts.auto) return;
      this._persistActiveSection(this._sportId, this._activeDashboard.id, section_id)
          .catch(() => {});   // preference write; a failure must not surface as an unhandled rejection
    },

    /** TV mode's rotation: every TV_DWELL_S seconds the next open tab (never a
     *  locked one, never Pre-Slate once the slate has started is the board's own
     *  rule via _visibleSections). Stops when TV mode does. */
    _tvRotate(on) {
      clearInterval(this._tvTimer);
      this._tvTimer = null; this._tvElapsed = 0; this._tvPct = 0;
      if (!on) return;
      this._tvTimer = setInterval(() => {
        if (document.visibilityState !== 'visible' || this.editMode) return;
        if (Date.now() < this._tvPinnedUntil) { this._tvPct = 0; return; }
        const ids = (this._visibleSections || []).filter(x => !x._locked).map(x => x.id);
        if (ids.length < 2) { this._tvPct = 0; return; }
        this._tvElapsed += 1;
        this._tvPct = Math.min(100, this._tvElapsed / TV_DWELL_S * 100);
        if (this._tvElapsed >= TV_DWELL_S) {
          this._tvElapsed = 0; this._tvPct = 0;
          const i = ids.indexOf(this._activeSection);
          this.switchSection(ids[(i + 1) % ids.length], { auto: true });
        }
      }, 1000);
    },

    // ── User-dashboard tabs (sections) — add / rename / delete ────────
    // (Xav 2026-06-14) User dashboards are sectioned like system presets
    // but EDITABLE. _systemPresetData.editable flags it; these mutate its
    // sections in place then _saveSoon persists the whole array.
    get _sectionsEditable() {
      return this._editable && !!this._systemPresetData;
    },
    /** Fold the live this.layout back into the active section's cached
     *  layout so it isn't lost on the next tab switch / save. */
    _syncLayoutIntoActiveSection() {
      // The phone renders a PHONE projection of the section - folding that
      // back would overwrite the desktop layout in memory (and an iPad rotated
      // to landscape would then show, and could save, the phone version).
      if (this.shellMode) return;
      const pd = this._systemPresetData;
      if (!pd || !pd.sections) return;
      const sec = pd.sections.find(s => s.id === this._activeSection);
      if (!sec) return;
      sec.layout = _keepPregame({
        scoreboard_id: this.layout.scoreboard_id || null,
        kpis:          this.layout.kpis          || [],
        panels:        this.layout.panels        || [],
        widgetOpts:    this.layout.widgetOpts     || {},
      }, this.layout);
    },
    /** Build the sections array for a PUT — live layout folded into the
     *  active section, every section flattened to the storage shape. */
    _sectionsForSave() {
      const pd = this._systemPresetData;
      if (!pd || !pd.sections) {
        return [{ id: 'main', label: 'Main',
                  scoreboard_id: this.layout.scoreboard_id || null,
                  kpis: this.layout.kpis || [], panels: this.layout.panels || [],
                  widgetOpts: this.layout.widgetOpts || {} }];
      }
      this._syncLayoutIntoActiveSection();
      return pd.sections.map(s => _keepPregame({
        id: s.id, label: s.label, ...(s.origin ? { origin: s.origin } : {}),
        scoreboard_id: (s.layout && s.layout.scoreboard_id) || null,
        kpis:          (s.layout && s.layout.kpis)       || [],
        panels:        (s.layout && s.layout.panels)     || [],
        widgetOpts:    (s.layout && s.layout.widgetOpts) || {},
      }, s.layout));
    },
    // Tab cap — mirrors the server's MAX_SECTIONS_PER_DASHBOARD so the user is
    // stopped here with a disabled ＋ rather than hitting a 422 on save.
    MAX_SECTIONS: 8,
    get _canAddSection() {
      return this._sectionsEditable
          && ((this._systemPresetData?.sections?.length) || 0) < this.MAX_SECTIONS;
    },
    addSection() {
      if (!this._canAddSection) return;
      this._syncLayoutIntoActiveSection();
      const id  = 'sec_' + Math.random().toString(36).slice(2, 10);
      const n   = this._systemPresetData.sections.length + 1;
      this._systemPresetData.sections.push({
        id, label: 'Tab ' + n,
        layout: { scoreboard_id: null, kpis: [], panels: [], widgetOpts: {} },
      });
      this._activeSection = id;
      this._materialiseSectionLayout(id);
      this._persistActiveSection(this._sportId, this._activeDashboard.id, id);
      this._saveSoon();
    },
    renameSection(id, label) {
      if (!this._sectionsEditable) return;
      const sec = this._systemPresetData.sections.find(s => s.id === id);
      if (!sec) return;
      sec.label = (label || '').trim().slice(0, 24) || sec.label;
      this._saveSoon();
    },
    deleteSection(id) {
      if (!this._sectionsEditable) return;
      const secs = this._systemPresetData.sections;
      if (secs.length <= 1) return;                  // never delete the last tab
      const idx = secs.findIndex(s => s.id === id);
      if (idx < 0) return;
      secs.splice(idx, 1);
      if (this._activeSection === id) {
        const next = secs[Math.max(0, idx - 1)];
        this._activeSection = next.id;
        this._materialiseSectionLayout(next.id);
        this._persistActiveSection(this._sportId, this._activeDashboard.id, next.id);
      }
      this._saveSoon();
    },
    /** What a tab IS: its own id on a system board, the system tab it was
     *  copied from on a custom one ('right_now' = The Action). */
    _sectionKind(id) {
      const s = ((this._systemPresetData && this._systemPresetData.sections) || []).find(x => x.id === id);
      return (s && s.origin) || id;
    },
    /** Move a tab one place left (-1) or right (+1). The tab order is the
     *  order the board's tab bar shows them in. */
    moveSection(id, dir) {
      if (!this._sectionsEditable) return;
      const secs = this._systemPresetData.sections;
      const i = secs.findIndex(s => s.id === id), j = i + dir;
      if (i < 0 || j < 0 || j >= secs.length) return;
      [secs[i], secs[j]] = [secs[j], secs[i]];
      this._saveSoon();
    },
    // Inline tab-rename state (mirrors the widget-rename pattern).
    _renamingSectionId:  null,
    _sectionRenameValue: '',
    _startSectionRename(id, label) {
      if (!this._sectionsEditable) return;
      this._sectionRenameValue = label || '';
      this._renamingSectionId  = id;
    },
    _commitSectionRename(id) {
      if (this._renamingSectionId !== id) return;
      this.renameSection(id, this._sectionRenameValue);
      this._renamingSectionId = null;
    },
    _cancelSectionRename() { this._renamingSectionId = null; },

    /** Serialize read-merge-write of user_settings.dashboard_layout.
     *  active_section, active_dashboard AND preset_opts all live in the one
     *  dashboard_layout blob, and the server POST replaces it wholesale
     *  (current.update(body)), so two writers that GET-then-POST concurrently
     *  clobber each other's just-added key. Chaining every write through one
     *  promise means each mutator sees the previous write's result. mutator
     *  receives the current layout object and mutates it in place. Best-effort;
     *  a failed link resets the chain so it can't wedge. */
    _settingsWriteChain: Promise.resolve(),
    _queueSettingsWrite(mutator) {
      // THE chokepoint for the phone rule: dashboard_layout (active board and
      // tab, preset cog options) is what the DESKTOP reads back, so the phone
      // shell never writes it - whatever called. It used to rely on each writer
      // remembering its own shellMode check, and one didn't: collapsing a
      // lineup card on the phone (setOpt -> _saveOptsSoon -> _persistPresetOpts)
      // changed the desktop board (mobile audit 2026-09-11, B9). The phone's
      // own choices go to device storage (_deviceWrite). Behaviour-tested in
      // tests/js/phone_isolation_guards.test.js.
      if (this.shellMode) return this._settingsWriteChain;
      this._settingsWriteChain = this._settingsWriteChain.then(async () => {
        if (!this._uid) return;
        const r = await fetch('/api/settings', { credentials: 'same-origin' });
        if (!r.ok) return;
        const settings = await r.json();
        const layout = settings.dashboard_layout || { v: 3 };
        mutator(layout);
        // Custom dashboards are not this write's to make (/api/user-dashboards
        // owns them; the server keeps its own copy - core/settings_write.py).
        const { user_dashboards, ...rest } = layout;
        await fetch('/api/settings', {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({ dashboard_layout: rest }),
        });
      }).catch(() => {});
      return this._settingsWriteChain;
    },

    /** Read the persisted section for (sport, preset). Falls back to
     *  null if absent — caller uses preset.default_section then. Per
     *  the architecture doc, scoping is per (user, sport, dashboard)
     *  so switching from MLB Analytical to NBA Analytical doesn't drag
     *  the section along (different sports, different muscle memory). */
    async _readPersistedActiveSection(sport_id, dashboard_id) {
      if (!this._uid) return null;
      const sid = (sport_id || '').toLowerCase();
      const did = (dashboard_id || '').toLowerCase();
      if (!sid || !did) return null;
      if (this.shellMode) return this._deviceRead('tab', did) || null;
      try {
        const r = await fetch('/api/settings', { credentials: 'same-origin' });
        if (!r.ok) return null;
        const settings = await r.json();
        const layout   = settings && settings.dashboard_layout;
        const bySport  = (layout && layout.active_section) || {};
        const byDash   = bySport[sid] || {};
        return byDash[did] || null;
      } catch (_) { return null; }
    },

    /** Persist the active section per (sport, dashboard). Stored in
     *  user_settings.dashboard_layout.active_section[sport][dashboard]
     *  = section_id. Best-effort. */
    async _persistActiveSection(sport_id, dashboard_id, section_id) {
      if (!this._uid) return;
      const sid = (sport_id || '').toLowerCase();
      const did = (dashboard_id || '').toLowerCase();
      const sec = (section_id  || '').toLowerCase();
      if (!sid || !did || !sec) return;
      if (this.shellMode) { this._deviceWrite('tab', did, sec); return; }
      try {
        await this._queueSettingsWrite((layout) => {
          const bySport = layout.active_section || {};
          const byDash  = bySport[sid] || {};
          byDash[did] = sec;
          bySport[sid] = byDash;
          layout.active_section = bySport;
        });
      } catch (_) { /* persistence is best-effort */ }
    },

    /** Read the saved cog-option overrides for (sport, preset). Returns
     *  { [section_id]: widgetOpts } or {} when absent. Scoped per (user,
     *  sport, preset) — same reasoning as active_section: NBA Analytical and
     *  MLB Analytical are different muscle memory. */
    async _readPersistedPresetOpts(sport_id, preset_id) {
      if (!this._uid) return {};
      const sid = (sport_id || '').toLowerCase();
      const pid = (preset_id || '').toLowerCase();
      if (!sid || !pid) return {};
      try {
        const r = await fetch('/api/settings', { credentials: 'same-origin' });
        if (!r.ok) return {};
        const settings = await r.json();
        const layout   = settings && settings.dashboard_layout;
        const bySport  = (layout && layout.preset_opts) || {};
        return (bySport[sid] && bySport[sid][pid]) || {};
      } catch (_) { return {}; }
    },

    /** Persist the active preset's current cog options to
     *  user_settings.dashboard_layout.preset_opts[sport][preset][section].
     *  Only for system presets (user dashboards persist their own
     *  widgetOpts). Best-effort; serialized with the other dashboard_layout
     *  writers via _queueSettingsWrite so concurrent writes don't clobber. */
    async _persistPresetOpts() {
      if (!this._uid) return;
      if (this._activeDashboard.kind !== 'system') return;
      const sid = (this._sportId || '').toLowerCase();
      const pid = (this._activeDashboard.id || '').toLowerCase();
      const sec = (this._activeSection || 'main').toLowerCase();
      if (!sid || !pid) return;
      const opts = this.layout.widgetOpts || {};
      // Keep the in-memory overlay in sync so a section switch away and back
      // (no reload) still shows the change without a round-trip.
      if (!this._presetOpts) this._presetOpts = {};
      this._presetOpts[sec] = opts;
      await this._queueSettingsWrite((layout) => {
        const bySport   = layout.preset_opts || {};
        const byPreset  = bySport[sid] || {};
        const bySection = byPreset[pid] || {};
        bySection[sec] = opts;
        byPreset[pid]  = bySection;
        bySport[sid]   = byPreset;
        layout.preset_opts = bySport;
      });
    },

    /** Read the persisted active-dashboard pointer for a sport.
     *  Returns {kind, id} or null. Used by init() to resume the user
     *  on whichever dashboard they last visited per sport. */
    async _readPersistedActiveDashboard(sport_id) {
      if (!this._uid) return null;
      const sid = (sport_id || this._sportId || '').toLowerCase();
      if (!sid) return null;
      // The phone remembers its own board (the Live tab switch), so the board
      // left open on the desktop never takes over the phone - and vice versa.
      if (this.shellMode) {
        const id = this._deviceRead('board', sid);
        return id ? { kind: 'user', id } : null;
      }
      try {
        const r = await fetch('/api/settings', { credentials: 'same-origin' });
        if (!r.ok) return null;
        const settings = await r.json();
        const layout   = settings && settings.dashboard_layout;
        const map      = (layout && layout.active_dashboard) || {};
        const entry    = map[sid];
        if (!entry || !entry.kind || !entry.id) return null;
        return { kind: entry.kind, id: entry.id };
      } catch (_) { return null; }
    },

    /** Persist the active dashboard pointer to user_settings so the
     *  next visit lands on the same dashboard. Per-sport scoped via
     *  dashboard_layout.active_dashboard[sport_id]. Best-effort —
     *  anonymous users skip persistence (no server account). */
    async _persistActiveDashboard() {
      if (!this._uid) return;
      // Lowercase to match the READ side (_readPersistedActiveDashboard /
      // _persistActiveSection both lowercase) — keeps the active_dashboard map
      // keys symmetric so a pointer written here always reads back. (P2 audit.)
      const sid = (this._sportId || '').toLowerCase();
      if (!sid) return;
      if (this.shellMode) {
        this._deviceWrite('board', sid, this._activeDashboard.kind === 'user' ? this._activeDashboard.id : '');
        return;
      }
      const entry = { kind: this._activeDashboard.kind, id: this._activeDashboard.id };
      await this._queueSettingsWrite((layout) => {
        const map = layout.active_dashboard || {};
        map[sid] = entry;
        layout.active_dashboard = map;
      });
    },

    // ── Create-new flow ────────────────────────────────────────────────

    /** Open the "Create new dashboard" modal with the form reset to
     *  defaults. Called from the Mode picker's "+ Create new" button. */
    openCreateDashboard() {
      const fmt = window.LSFormat && window.LSFormat.resolve(this._formatVariant || '');
      const id  = (fmt && fmt.default) || 'broadcast';
      const label = (((fmt && fmt.presets) || []).find(p => p.id === id) || {}).label || 'Analysis';
      this.createFrom = { id, label, tabs: [] };
      this.createDashboardForm  = { name: 'My ' + label, start: 'board', tabId: '', cloneId: '' };
      this.createDashboardError = '';
      this.createDashboardBusy  = false;
      this.createDashboardOpen  = true;
      // Its tabs, for "one tab of it" - the board the copy will actually get.
      fetch('/api/system-presets/' + encodeURIComponent(id) + '/' + encodeURIComponent(this._sportId || ''),
            { credentials: 'same-origin' })
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          const tabs = ((d && d.preset && d.preset.sections) || []).map(t => ({ id: t.id, label: t.label }));
          this.createFrom = { ...this.createFrom, tabs };
          if (tabs.length && !this.createDashboardForm.tabId) this.createDashboardForm.tabId = tabs[0].id;
        })
        .catch(() => {});
    },
    closeCreateDashboard() { this.createDashboardOpen = false; },
    /** The slate family custom boards are kept per (standard / tiers). */
    get _boardFamily() {
      const t = window.LSFormat && window.LSFormat.traitsOf(this._formatVariant || '');
      return (t && t.family) || 'standard';
    },
    /** Your boards on this sport - the ones "duplicate" can copy. */
    get createCloneable() { return (this.userDashboards || []).filter(d => (d.sport_id || '') === (this._sportId || '')); },

    /** POST /api/user-dashboards with the modal form values, then switch
     *  into the new dashboard in edit mode. Server errors surface in the
     *  modal (createDashboardError). */
    async createUserDashboard() {
      const form = this.createDashboardForm || {};
      const name = (form.name || '').trim();
      if (!name) {
        this.createDashboardError = 'Pick a name first.';
        return;
      }
      let source = 'empty';
      if (form.start === 'board') source = 'board:' + this.createFrom.id;
      else if (form.start === 'tab') {
        if (!form.tabId) { this.createDashboardError = 'Pick a tab to copy.'; return; }
        source = 'tab:' + this.createFrom.id + ':' + form.tabId;
      } else if (form.start === 'clone') {
        if (!form.cloneId) { this.createDashboardError = 'Pick one of your boards.'; return; }
        source = 'clone:' + form.cloneId;
      }
      this.createDashboardBusy  = true;
      this.createDashboardError = '';
      try {
        const r = await fetch('/api/user-dashboards', {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({
            sport_id: this._sportId || '',
            name,
            source,
            format:   this._boardFamily,
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          this.createDashboardError = (data && data.error) || `Create failed (${r.status}).`;
          this.createDashboardBusy  = false;
          return;
        }
        // Refresh the picker list + activate the new dashboard.
        await this._fetchUserDashboards(this._sportId);
        await this.switchToActiveDashboard('user', data.dashboard.id);
        // Straight into edit mode: they made it to change it.
        this.editMode = true;
        this.createDashboardBusy = false;
        this.createDashboardOpen = false;
      } catch (e) {
        this.createDashboardError = 'Network error — try again.';
        this.createDashboardBusy  = false;
      }
    },

    // ── Branded confirm / prompt modal ───────────────────────────────
    // Replaces the native window.prompt / window.confirm on the destructive
    // flows (rename / delete / reset) so they match the rest of the product's
    // popover chrome instead of an OS dialog. Promise-based: _confirm() ->
    // Promise<bool>, _prompt() -> Promise<string|null> (null = cancelled).
    _modal:        { open: false, kind: 'confirm', title: '', message: '', value: '',
                     placeholder: '', maxlength: 80, confirmLabel: 'Confirm', danger: false },
    _modalResolve: null,
    _openModal(opts) {
      // Resolve any modal already waiting (defensive — shouldn't stack).
      if (this._modalResolve) { this._modalResolve(opts.kind === 'prompt' ? null : false); }
      return new Promise((resolve) => {
        this._modalResolve = resolve;
        this._modal = {
          open:         true,
          kind:         opts.kind || 'confirm',
          title:        opts.title || '',
          message:      opts.message || '',
          value:        opts.value || '',
          placeholder:  opts.placeholder || '',
          maxlength:    opts.maxlength || 80,
          confirmLabel: opts.confirmLabel || (opts.kind === 'prompt' ? 'Save' : 'Confirm'),
          danger:       !!opts.danger,
        };
      });
    },
    _confirm(opts) { return this._openModal({ ...opts, kind: 'confirm' }); },
    _prompt(opts)  { return this._openModal({ ...opts, kind: 'prompt'  }); },
    _modalConfirm() {
      const r = this._modalResolve, kind = this._modal.kind, val = this._modal.value;
      this._modal = { ...this._modal, open: false };
      this._modalResolve = null;
      if (r) r(kind === 'prompt' ? val : true);
    },
    _modalCancel() {
      const r = this._modalResolve, kind = this._modal.kind;
      this._modal = { ...this._modal, open: false };
      this._modalResolve = null;
      if (r) r(kind === 'prompt' ? null : false);
    },

    /** Rename a user dashboard via the branded prompt modal. 24-char cap
     *  mirrors the create-modal input maxlength (Xav 2026-06-02 — keeps the
     *  sub-nav mode pill from blowing out). */
    async renameUserDashboard(dashboard_id) {
      const dash = (this.userDashboards || []).find(d => d.id === dashboard_id);
      if (!dash) return;
      const raw = await this._prompt({
        title:        'Rename dashboard',
        value:        dash.name || '',
        placeholder:  'Dashboard name',
        maxlength:    24,
        confirmLabel: 'Rename',
      });
      if (raw === null) return;                       // cancelled
      const next = (raw || '').trim().slice(0, 24);
      if (!next || next === dash.name) return;
      try {
        const r = await fetch(`/api/user-dashboards/${encodeURIComponent(dashboard_id)}`, {
          method:      'PUT',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({ name: next }),
        });
        if (!r.ok) return;
        await this._fetchUserDashboards(this._sportId);
      } catch (_) { /* silent — rename is non-critical */ }
    },

    /** Delete via the branded confirm modal. If the deleted dashboard was
     *  the active one, falls back to system Simple to avoid an empty view. */
    async deleteUserDashboard(dashboard_id) {
      const dash = (this.userDashboards || []).find(d => d.id === dashboard_id);
      if (!dash) return;
      const ok = await this._confirm({
        title:        'Delete dashboard',
        message:      `Delete "${dash.name}"? This can't be undone.`,
        confirmLabel: 'Delete',
        danger:       true,
      });
      if (!ok) return;
      try {
        const r = await fetch(`/api/user-dashboards/${encodeURIComponent(dashboard_id)}`, {
          method:      'DELETE',
          credentials: 'same-origin',
        });
        if (!r.ok) return;
        const wasActive = (this._activeDashboard.kind === 'user'
                           && this._activeDashboard.id === dashboard_id);
        await this._fetchUserDashboards(this._sportId);
        if (wasActive) {
          await this.switchToActiveDashboard('system', 'broadcast');
        }
      } catch (_) { /* silent — delete is best-effort */ }
    },

    // ── Identifiers needed by persistence ─────────────────────────────
    get _uid() {
      return document.querySelector('meta[name="user-id"]')?.content || '';
    },
    get _sportId() {
      return (this.selectedSlate && this.selectedSlate.sport_id) || '';
    },
    // The active slate's scoring FORMAT variant (from its template) — '' for
    // standard Classic/Showdown. Drives format-aware widgets (Phase C) and the
    // mode-picker policy (window.LSFormat). See docs/dashboard_formats_spec.md.
    get _formatVariant() {
      // Prefer the slate-template join, but fall back to the SERVER-authoritative
      // formatVariant carried by the SSE payload (set in dashboard/sse.js). The
      // client template join can be null/lagging (region filter, cache miss, not
      // yet loaded); without this fallback a single-stat/tiers slate silently
      // resolves to a STANDARD board — the exact case the lock exists to prevent.
      // (P0 allocation audit, 2026-08-08)
      return (this.selectedSlate && this.selectedSlate.template
              && this.selectedSlate.template.format_variant)
             || this.formatVariant || '';
    },
    // Live-score display precision from the format's `decimals` trait — 2 for
    // fantasy points, 0 for single-stat (a whole count). Feeds LSTween.num's
    // `decimals` option (animated numbers can't take a pre-formatted string).
    get _fmtDecimals() {
      return window.LSFormat ? window.LSFormat.traitsOf(this._formatVariant || '').decimals : 2;
    },
    // THE way to render a live score for the active slate's format. Any widget/
    // KPI showing an FPts/score value should call this instead of its own
    // toFixed — precision then follows the format trait automatically.
    _fmtScore(v) {
      return window.LSFormat ? window.LSFormat.fmtScore(v, this._formatVariant || '') : (Number(v) || 0).toFixed(2);
    },
    // The score's display unit for the active format — "FPts" (fantasy), or the
    // stat itself (HR / RBI / K) for single-stat. INVARIANT (no plural).
    get _scoreUnit() {
      return window.LSFormat ? window.LSFormat.traitsOf(this._formatVariant || '').unit : 'FPts';
    },
    // Formatted score + unit, e.g. "3 HR" / "12.50 FPts". The one call for any
    // "number + its unit" score display.
    _fmtScoreU(v) { return this._fmtScore(v) + ' ' + this._scoreUnit; },
    // Unit SUFFIX for panels that show a bare number on standard boards — " HR"
    // for single-stat, "" for standard (so we don't slap "FPts" onto every
    // standard leaderboard row). Sites that already print a unit use _scoreUnit.
    get _scoreUnitSfx() {
      const u = this._scoreUnit;
      return (u && u !== 'FPts') ? ' ' + u : '';
    },

    // ── Lifecycle ──────────────────────────────────────────────────────
    initWidgets() {
      // Device classes come from ONE place - core/device_class.py, rendered into
      // window.LS_DEVICE by base.html and into every @media/media= that styles
      // the shell. Reading them here is what stops the JS that MOUNTS the shell
      // and the CSS that STYLES it from drifting apart; there is deliberately no
      // hardcoded fallback, because a fallback is just a sixth copy of the rule.
      const D = window.LS_DEVICE;
      // Mobile detection — re-run on resize.
      const mq = window.matchMedia(D.phone);
      this.mobile = mq.matches;
      mq.addEventListener('change', e => { this.mobile = e.matches; this._reflowForDevice(); });
      // Tablet detection — 768-1399px (covers every iPad in landscape:
      // iPad mini @1133, iPad Air 11" @1180, iPad Pro 11" @1194, iPad
      // Pro 12.9" / Air 13" @1366; plus older 1366×768 laptops). At
      // these widths the desktop 12-col grid produces 280-340px cells
      // which truncate dense tables ("Jack..." instead of "Jackson
      // Jobe"). _tabletPlacements packs panels 2-up so each cell gets
      // at least half the viewport to breathe.
      const tq = window.matchMedia(D.tablet_grid);
      this._tabletMq = tq.matches;
      tq.addEventListener('change', e => { this._tabletMq = e.matches; });
      const bq = window.matchMedia(D.broadcast_desktop);
      this._bcDesktop = bq.matches;
      bq.addEventListener('change', e => { this._bcDesktop = e.matches; });
      // A tablet held UPRIGHT (and a phone on its side): the shell with a left
      // rail, not a 12-col grid squeezed into ~820px. Decided by how the device
      // is held, not by a width cut - the old `max-width: 1099px` sent a 10.2"
      // iPad on its side to the phone shell (audit re-run 2026-09-12). Rotating
      // flips this and the board comes back; shellMode reads it, so the two
      // x-if trees swap live and the slate/tab state rides through.
      const pq = window.matchMedia(D.tablet_touch);
      this.tabletTouch = pq.matches;
      pq.addEventListener('change', e => { this.tabletTouch = e.matches; this._reflowForDevice(); });
      // Any touch screen: dashboard EDITING is desktop-only (Xav), so the
      // Edit pill and "Create new" hide on touch - drag/resize were already
      // off below 1400px, which left an Edit button that could do nothing.
      const cq = window.matchMedia(D.coarse);
      this.touchDevice = cq.matches;
      cq.addEventListener('change', e => { this.touchDevice = e.matches; });

      // Fit-to-viewport sizing (panel grid only — KPI strip is fixed height).
      const measure = () => {
        const el = this.$refs && this.$refs.grid;
        if (!el) return;
        this._gridW = Math.round(el.getBoundingClientRect().width);
        // UI-scale correction (Xav 2026-06-12): the Text size setting
        // applies CSS zoom on <html>, which scales rendered px but NOT
        // window.innerHeight. A height computed in viewport px renders
        // zoom× larger, overshooting the page bottom by (zoom-1)·height
        // (~110px at L/1.1 — the phantom 3cm scrollbar). Divide the
        // computed height back into the element's local (zoomed) units.
        let z = 1;
        try { z = parseFloat(document.documentElement.style.zoom || '1') || 1; } catch (e) {}
        // + scrollY anchors the measurement in DOCUMENT coordinates.
        // Without it, a re-measure firing while the page is scrolled
        // reads the grid "higher up" → computes a taller grid → more
        // scroll → the next re-measure compounds it. (That feedback
        // loop is how the leftover scroll kept matching whatever
        // chrome the page had jumped past — Xav 2026-06-12.)
        const top = el.getBoundingClientRect().top + window.scrollY;
        // Bottom band (Xav 2026-06-14): the scoreboard sits BELOW the grid,
        // so the grid must stop short of the viewport bottom to leave the
        // band room — otherwise it fills the screen and shoves the band off
        // the bottom (only its title peeks). The band's height is its own
        // content (not affected by the grid height), so measuring it here
        // isn't circular. ~50px = the main frame's bottom padding/border +
        // the band's 14px margin-top gap.
        let reserve = 36;
        if (this.sbPos === 'bottom') {
          const band = document.querySelector('.ls-dash-body.is-band-bottom > .ls-scoreboard-frame');
          if (band) reserve = Math.ceil(band.getBoundingClientRect().height) + 50;
        }
        // Broadcast's dock sits under the grid: leave it its row.
        const dock = document.querySelector('.ls-bc-dock');
        if (dock && dock.offsetParent) reserve += Math.ceil(dock.getBoundingClientRect().height) + 12;
        const h = Math.max(280, Math.floor((window.innerHeight - top - reserve) / z));
        this.gridHeight = h;
        // Scoreboard rail: size to the viewport bottom with the same
        // slack as the grid, so the page never scrolls. Measured (not
        // the CSS calc estimate) because nav/sub-nav/margins vary.
        // Cleared when rail mode is off so the top band keeps its
        // natural height.
        const rail = document.querySelector('.ls-dash-body.is-rail > .ls-scoreboard-frame');
        const anyFrame = document.querySelector('.ls-dash-body > .ls-scoreboard-frame');
        // MOBILE: never pin a fixed height on the frame. The rail/band CSS
        // collapses to a single full-width stacked column at ≤739px, so the
        // frame must grow with its content and push the grid down. Setting a
        // viewport-derived rail height here (this branch is NOT gated by a
        // media query) stamped an inline px height that the CSS can't beat,
        // so the tall stacked card list overflowed onto the panels below.
        // Clearing it lets the frame be content-height on phones. (Xav 2026-07-04)
        if (this.mobile) {
          if (anyFrame) anyFrame.style.height = '';
        } else if (rail) {
          const rtop = rail.getBoundingClientRect().top + window.scrollY;
          // End the rail where the main column's CONTENT ends: the grid's new
          // bottom, plus the main frame's own bottom padding + border, plus
          // Broadcast's dock when it shows. Read from computed style, never
          // from the main frame's height: the row stretches main to the
          // rail's height, so measuring main fed the rail back into itself
          // and each re-measure grew it to the window bottom (Xav
          // 2026-09-30, "seems worse than before"). This was a fixed 12px -
          // right for the framed boards, but Broadcast's skin strips the
          // frame's padding and border, so its rail ran 12px past the panels.
          const main = el.closest('.ls-dash-main');
          let below = 12;
          if (main) {
            const cs = getComputedStyle(main);
            below = (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
            const dk = main.querySelector('.ls-bc-dock');
            if (dk && dk.offsetParent) below += Math.max(0, dk.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom) / z;
          }
          // top / rtop are viewport px, h and below the frame's own (zoomed) units.
          rail.style.height = Math.max(280, Math.floor((top - rtop) / z + h + below)) + 'px';
        } else if (anyFrame) {
          anyFrame.style.height = '';
        }
      };
      this.$nextTick(() => measure());
      this._resizeBound = measure;
      window.addEventListener('resize', this._resizeBound);
      // ...and the moment a row above or below it comes or goes, or changes
      // height. Leaving The Action takes its ticker row away; the grid was
      // sized under it and the watchers below only re-measured ~70ms later,
      // after the new tab's panels had painted - every panel squeezed 58px,
      // then stretched (Xav 2026-10-07, "My Lineups jitters"). A mutation
      // callback runs before the browser paints, and a ResizeObserver after
      // layout but before paint: no wrong frame either way.
      this.$nextTick(() => {
        const g = this.$refs && this.$refs.grid;
        if (!g || !g.parentElement) return;
        if (window.MutationObserver) {
          this._aroundGridMO = new MutationObserver(() => measure());
          this._aroundGridMO.observe(g.parentElement, { childList: true });
        }
        if (window.ResizeObserver) {
          this._aroundGridRO = new ResizeObserver(() => measure());
          for (const c of g.parentElement.children) if (c !== g) this._aroundGridRO.observe(c);
        }
      });
      // Re-measure when late-arriving chrome shifts the grid's top
      // offset: the section tab strip (renders after the system preset
      // fetch) and the KPI strip (renders after layout load) both pop
      // in 100-300ms after mount, pushing the grid down ~100px. The
      // one-shot mount measure missed that, so the page scrolled by
      // exactly that amount (Xav 2026-06-12). $watch + nextTick keeps
      // the grid + rail flush with the viewport bottom whenever any of
      // these change; the extra calls are idempotent.
      const remeasure = () => this.$nextTick(() => measure());
      this.$watch('layoutLoaded',      remeasure);
      this.$watch('layout',            remeasure);
      this.$watch('_systemPresetData', remeasure);
      this.$watch('_activeSection',    remeasure);
      this.$watch('broadcastTickerOn', remeasure);   // The Action's tab line arriving
      // Breathing panels: the dock appearing / emptying changes the room left
      // for the grid; a panel leaving the dock is ringed for a few seconds so
      // the eye finds what just came back.
      this.$watch('_breathQuietKey', (now, prev) => {
        remeasure();
        const back = String(prev || '').split(',').filter(id => id && !String(now || '').split(',').includes(id));
        for (const id of back) {
          this._breathWoken = { ...this._breathWoken, [id]: true };
          setTimeout(() => {
            const next = { ...this._breathWoken }; delete next[id]; this._breathWoken = next;
          }, 8000);
        }
      });
      if (!this._breathTimer) {
        this._breathTimer = setInterval(() => { if (this._breathOn) this._breathTick++; }, 2000);
      }
      this.$watch('editMode',          remeasure);
      // Belt-and-braces: fonts/images settling can still nudge offsets
      // a frame or two after the watches fire.
      setTimeout(measure, 400);
      setTimeout(measure, 1200);

      // Body-scroll lock when a modal opens.
      this.$watch('activeModal', (id) => {
        document.body.style.overflow = id ? 'hidden' : '';
      });

      // Esc closes the active modal; also exits TV mode if no modal open.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (this.activeModal) { this.closeModal(); return; }
          if (this.tvMode) this.setTvMode(false);
        }
        // Shift+T toggles couch/TV mode — global, but only on this page
        // (the host's keydown listener is unmounted with the controller).
        // Ignored while the user is typing in an input so it doesn't
        // capture a literal "T" in a search field.
        if (e.shiftKey && (e.key === 'T' || e.key === 't')) {
          const t = e.target;
          if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
          e.preventDefault();
          this.setTvMode(!this.tvMode);
        }
      });

      // TV mode bootstrap — URL param ?tv=1, fullscreen autoswitch, and
      // <html data-tv-mode> attribute mirroring so CSS can hide chrome.
      // Independent of `_preset === 'tv'`: the preset is a layout
      // source (which widgets are visible / how they're arranged), TV
      // mode is the visual chrome stripping. They compose.
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('tv') === '1') this.tvMode = true;
      } catch (_) { /* ignore malformed query strings */ }
      const applyTv = (on) => {
        const html = document.documentElement;
        if (on) html.setAttribute('data-tv-mode', '');
        else    html.removeAttribute('data-tv-mode');
      };
      applyTv(this.tvMode);
      this.$watch('tvMode', applyTv);
      this._tvRotate(this.tvMode);
      this.$watch('tvMode', (on) => this._tvRotate(on));
      // Auto-enter TV mode when the browser enters fullscreen (F11, the
      // fullscreen toggle in Settings, or the corner hint we render).
      document.addEventListener('fullscreenchange', () => {
        const fs = !!document.fullscreenElement;
        if (fs && !this.tvMode) this.tvMode = true;
        if (!fs && this.tvMode) this.tvMode = false;
      });

      // Mirror the active preset onto <html data-preset="…"> so CSS
      // rules can scope by preset (e.g. Simple bumps headline content
      // sizes since it ships fewer widgets and has whitespace to spare).
      // Mirrors the data-tv-mode pattern above. (Xav 2026-05-31.)
      const applyPreset = (p) => {
        const html = document.documentElement;
        if (p) html.setAttribute('data-preset', p);
        else   html.removeAttribute('data-preset');
      };
      applyPreset(this._preset);
      this.$watch('_preset', applyPreset);

      // Fire-and-forget DB catalog load. The picker uses LSWidgets.forSlot
      // which reads the in-memory registry; the catalog merges admin
      // overrides (title / is_active / sort_order) into that map. Anonymous
      // users / 401 / network errors silently fall back to the JS registry.
      if (window.LSWidgetCatalog && !window.LSWidgetCatalog.loaded) {
        window.LSWidgetCatalog.load();
      }

      // Resolve initial preset before painting anything. Priority:
      //   1. ?preset= in URL (deep link / share)
      //   2. last active preset for this sport in localStorage
      //   3. 'broadcast' - Analysis (the default since plan Phase 2)
      const sid    = this._sportId;
      this._preset = this._resolveInitialPreset(sid);
      this._activeDashboard = { kind: 'system', id: this._preset };

      // Phase 2 wipe migration — old user_settings.dashboard_layout
      // shape with `sport_overrides` is no longer authoritative (system
      // presets win now). One-shot drop on first load post-Phase-2 so
      // the LSLayoutStore localStorage cache doesn't keep painting
      // stale customizations on top of the new defaults. v3 marker
      // prevents re-running. Per [[no-user-migration-yet]], we don't
      // preserve the old data — pre-launch.
      this._maybeWipeLegacyLayout(sid);

      // Resolve + load the active dashboard for the current sport. At
      // init() the slate often hasn't loaded yet, so `sid` is empty and
      // everything would default to 'simple' — the sport-change watcher
      // below re-runs this the moment the real sport lands, which is
      // what restores the user's actual preset on F5.
      this._resolveActiveForSport(sid);

      // (Phase 7 cleanup: admin-default fetch + upgrade-prompt
      // reconciliation removed. System presets are code-only now.)

      // Re-load when the user picks a different slate (sport may change).
      // This is also the path that fixes F5-reverts-to-Simple: init ran
      // before the slate loaded (empty sport), so the persisted per-sport
      // preset wasn't read; here the real sport is known, so we re-resolve.
      this.$watch && this.$watch('selectedSlate', (s, prev) => {
        const a = (s && s.sport_id) || '';
        const b = (prev && prev.sport_id) || '';
        // Format can change WITHOUT the sport changing (Classic MLB → single-stat
        // MLB), and a locked format must re-resolve the board — so watch the
        // format variant too, not just the sport.
        const fa = (s && s.template && s.template.format_variant) || '';
        const fb = (prev && prev.template && prev.template.format_variant) || '';
        // New slate → reset the lineup-race history (a fresh night).
        if ((s && s.id) !== (prev && prev.id)) this._lineupHistory = [];
        if (a !== b || fa !== fb) {
          this._resolveActiveForSport(a);
        }
      });
      // The SERVER-authoritative format arrives via SSE (this.formatVariant, set
      // in applyData) AFTER the slate is selected — and the selectedSlate watch
      // above won't fire for it (same object identity). When it reveals a format
      // the client template join LACKED (join was empty → resolved to standard),
      // re-resolve so a single-stat / tiers slate gets its own board. Guarded to the
      // join-empty case so a correct join never triggers a redundant reload.
      // (P0 allocation audit, 2026-08-08)
      this.$watch && this.$watch('formatVariant', (nf, pf) => {
        const joinFmt = (this.selectedSlate && this.selectedSlate.template
                         && this.selectedSlate.template.format_variant) || '';
        if (!joinFmt && (nf || '') !== (pf || '') && this.selectedSlate) {
          this._resolveActiveForSport(this._sportId);
        }
      });
      // Lineup-race time series — accumulate each lineup's live total per
      // poll tick (Xav 2026-06-14). Sampled on the host so it survives
      // section/tab switches; the Lineup Race graph reads it.
      this.$watch('lastUpdated', () => this._sampleLineupHistory());
      this._sampleLineupHistory();
    },

    /** Append a snapshot of every lineup's live total to the race history.
     *  Caps at 180 points (~90 min of 30s polls / a full replay). Skips
     *  exact-duplicate tails so a paused slate doesn't pad the series. */
    _lineupHistory: [],
    // Server-reconstructed trajectory (real, from slate start) — set by sse.js
    // from the payload's `lineupHistory`. Widgets prefer this over the
    // session-sampled _lineupHistory. Declared here so Alpine tracks it.
    lineupHistory: [],
    _sampleLineupHistory() {
      const rows = this.lineupRows || [];
      if (!rows.length) return;
      const totals = {};
      for (const lu of rows) totals[String(lu.id)] = +(lu.live_total || 0);
      const h = this._lineupHistory;
      const last = h.length ? h[h.length - 1].totals : null;
      if (last && Object.keys(totals).every(k => last[k] === totals[k])) return;
      h.push({ t: Date.now(), totals });
      if (h.length > 180) h.shift();
    },

    /** Resolve and load the active dashboard for a sport (Xav 2026-06-14).
     *  Re-reads the per-sport system-preset preference (URL ?preset wins,
     *  then localStorage, then Analysis), then honours a persisted USER
     *  dashboard if one is saved + still exists, else loads the system
     *  preset. Called from init() AND whenever the slate's sport changes
     *  — init runs before the slate is known (empty sport → would always
     *  default to Simple), so re-running this once the sport lands is
     *  what makes Analytical survive a refresh. */
    async _resolveActiveForSport(sid) {
      sid = (sid || '').toLowerCase();
      // Nothing to resolve until the slate's sport is known. The init call runs
      // with an empty sport (slate not loaded yet) and the sport-change watcher
      // re-runs this the moment the real sport lands. Bailing here avoids
      // materialising a board for an unknown sport that then flips to the Cover
      // once start_time/sport arrive — the "THE ACTION → THE SLATE" flash.
      if (!sid) return;
      // Locked formats (single-stat, tiers) force their curated board for EVERY
      // viewer — free, spectator, a cached Simple/Analytical preference, or a
      // persisted user dashboard alike. A full-fantasy board on a one-stat slate
      // misleads, so the format policy wins outright here at the resolution
      // layer (the picker only hides the OTHER options; this is what makes the
      // lock real). window.LSFormat is the single source of the policy.
      const fmt = window.LSFormat && window.LSFormat.resolve(this._formatVariant || '');
      if (fmt && Array.isArray(fmt.presets) && fmt.presets.length && !fmt.user_dashboards_allowed) {
        const forced = fmt.default || fmt.presets[0].id;
        this._preset = forced;
        this._activeDashboard = { kind: 'system', id: forced };
        await this._loadLayoutFromSystemPreset(sid, forced);
        this.layoutLoaded = true;
        return;
      }
      this._preset = this._resolveInitialPreset(sid);
      this._activeDashboard = { kind: 'system', id: this._preset };
      await this._fetchUserDashboards(sid);
      // An explicit ?preset in the URL is a HARD override (deep-link + our own
      // F5-persistence): honour it as a system preset and skip the persisted
      // USER-dashboard restore, so a stale user pointer can't clobber it.
      // (Xav 2026-07-09)
      const persisted = this._urlPreset() ? null : await this._readPersistedActiveDashboard(sid);
      // Free / Spectator / Demo are FORCED-mode boards (like the format lock):
      // _resolveInitialPreset already set _preset to Analysis. A
      // downgraded Pro→Free user (dashboards hidden-not-deleted) or a spectator
      // must NOT have their persisted custom board restored over the locked
      // system board — so gate the restore, mirroring the format lock's intent.
      // (P0 allocation audit, 2026-08-08)
      const lockedMode = this.isFree || this.spectatorMode || this.demoShowcase;
      if (!lockedMode && persisted && persisted.kind === 'user'
          && this.userDashboards.some(d => d.id === persisted.id)) {
        this._activeDashboard = { kind: 'user', id: persisted.id };
        const dash = await this._loadLayoutFromUserDashboard(persisted.id);
        if (dash) { this.layoutLoaded = true; return; }
      }
      // System preset (or empty-sport global fallback). If the API call
      // fails we leave the current layout rather than painting stale.
      await this._loadLayoutFromSystemPreset(sid, this._preset);
      this.layoutLoaded = true;
    },

    /** Resolve the initial preset on first paint. Default is Analysis
     *  ('broadcast') on every device since plan Phase 2 (Xav 2026-10-11);
     *  Simple ('broadcast_simple') is one screen, no tabs (Xav 2026-10-12).
     *  ?preset= in the URL wins, then a persisted per-sport preference
     *  in localStorage, then the default. */
    _resolveInitialPreset(sport_id) {
      // The slate's format names its boards and its default (core/
      // dashboard_formats): Analysis / Simple on a Classic slate, the Tiers
      // board on a Tiers slate. Tiers is not locked any more - custom boards
      // are allowed there (Xav 2026-10-11) - so a preference remembered from
      // a Classic slate of the same sport must not open Analysis on it.
      const fmt = window.LSFormat && window.LSFormat.resolve(this._formatVariant || '');
      const def = (fmt && fmt.default) || 'broadcast';
      const ids = ((fmt && fmt.presets) || []).map(p => p.id);
      const fits = (pid) => !ids.length || ids.includes(pid);
      // Showcase demo, a spectator (no lineup on this slate) and the Free plan
      // all get the format's board, overriding URL/cached preference:
      // _visibleSections drops the lineup tabs for no lineup and locks The Lab
      // for Free (plan Phase 2, Xav 2026-10-11). Admins are never isFree.
      if (this.demoShowcase || this.spectatorMode || this.isFree) return def;
      const fromUrl = this._urlPreset();
      if (fromUrl && (this.isAdmin || fits(fromUrl))) return fromUrl;   // an admin previews any board
      const sid = (sport_id || '').toLowerCase();
      const cached = LSLayoutStore.readPreference(this._uid, sid);
      if (cached && this._presetAllowed(cached) && fits(cached)) return cached;
      return def;
    },

    _urlPreset() {
      try {
        const v = (new URL(window.location.href)).searchParams.get('preset');
        return v && this._presetAllowed(v.toLowerCase()) ? v.toLowerCase() : null;
      } catch (_) { return null; }
    },

    /** Stamp the active system preset into the URL (?preset=) via replaceState
     *  so a hard reload restores it synchronously through _urlPreset — the one
     *  persistence path that doesn't depend on the async slate-load/watcher/
     *  localStorage chain. Pass null to clear (user dashboards restore via the
     *  server pointer). No history entry — just rewrites the current URL.
     *  (Xav 2026-07-09) */
    _writePresetToUrl(presetId) {
      try {
        const url = new URL(window.location.href);
        if (presetId && this._PRESETS.includes(presetId)) url.searchParams.set('preset', presetId);
        else url.searchParams.delete('preset');
        window.history.replaceState(null, '', url.toString());
      } catch (_) { /* history/URL API unavailable — localStorage still covers it */ }
    },

    /** User-facing action — switch to a different preset. Loads that
     *  preset's saved layout (or admin default, or hardcoded fallback)
     *  for the current sport. Each preset retains its own customisations
     *  so this is fully reversible. */
    /** Wrap a whole-dashboard layout reload in a crossfade. Alpine
     *  reconciles `this.layout` in place, so widgets common to both
     *  presets keep their DOM and only the diffs visibly pop — which
     *  reads as a janky partial mutation. Fading the main column to 0
     *  first means the swap happens while it's invisible; on fade-in the
     *  whole section appears together, so a Simple↔Analytical flip looks
     *  like a clean page transition. (Xav 2026-06-14)
     *
     *  The scoreboard rail is OUTSIDE .ls-dash-main, so it stays put —
     *  only the content area "reloads", which is exactly the mental model.
     */
    async _withSwitchTransition(loadFn) {
      this._switching = true;
      // Let the fade-out actually paint before swapping the layout under it.
      await new Promise(res => setTimeout(res, 170));
      try {
        await loadFn();
      } finally {
        // Let the new layout FULLY settle while still invisible before
        // fading in. Container-query font tiers (rank tables / KPIs keyed
        // to panel height) and the card-fit ResizeObserver resolve a frame
        // or two AFTER mount — revealing too early shows the fonts snap
        // from the base tier up, which read as "shrink then flip". Two
        // frames + a short beat lands the reveal on the settled sizes.
        // (Xav 2026-06-14)
        await this.$nextTick();
        await new Promise(res => requestAnimationFrame(() =>
          requestAnimationFrame(() => setTimeout(res, 90))));
        this._switching = false;
      }
    },

    async switchPreset(presetId) {
      const pid = (presetId || '').toLowerCase();
      if (!this._PRESETS.includes(pid)) return;
      // Phase 6 (Xav 2026-05-31): TV is no longer a preset, so this
      // method no longer needs to toggle tvMode. Use setTvMode() or
      // Shift+T directly when you want to enter/exit TV mode.
      if (pid === this._preset) return;
      // Phase 7: no flush of pending saves against the OLD preset. System
      // presets are read-only — there's never a pending save to flush.
      // Keep the active-dashboard pointer in sync.
      this._activeDashboard = { kind: 'system', id: pid };
      const sid = this._sportId;
      // Per-sport preset preference still lives in localStorage so the
      // next session lands on whichever system preset they last picked.
      LSLayoutStore.writePreference(this._uid, sid, pid);
      // Robust F5 persistence — stamp the preset into the URL (see
      // _writePresetToUrl / _resolveInitialPreset). (Xav 2026-07-09)
      this._writePresetToUrl(pid);
      // NB: _preset is assigned INSIDE the transition (below), not here —
      // its watcher flips <html data-preset> which resizes headline fonts
      // (Simple bumps them). Doing it now would shrink the still-visible
      // old content before the fade. (Xav 2026-06-14)
      this._withSwitchTransition(() => {
        this._preset = pid;
        return this._reloadLayoutForCurrentSport();
      });
    },

    /** Toggle couch/TV mode. Used by Shift+T, the Mode dropdown's TV
     *  pick, the exit hint in TV chrome, and the fullscreen autoswitch.
     *  Also requests / exits document fullscreen so the OS chrome
     *  (taskbar, address bar) hides too — best-effort, ignores user
     *  rejection of the fullscreen API. */
    setTvMode(on) {
      const want = !!on;
      if (this.tvMode === want) return;
      this.tvMode = want;
      try {
        if (want && !document.fullscreenElement) {
          const p = document.documentElement.requestFullscreen?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else if (!want && document.fullscreenElement) {
          document.exitFullscreen?.().catch(() => {});
        }
      } catch (_) { /* browser may block fullscreen — that's fine */ }
    },

    // (Phase 7 cleanup: _scopeFor removed — was only used by the
    // legacy LSLayoutStore.save scope-string builder, which itself
    // went away with saveAsDefault.)

    // (Phase 7 cleanup: admin-default reconcile, upgrade-prompt
    // preview/apply/cancel/dismiss, and _checkUpgrade all removed.
    // System presets are code-only now; design changes ship via deploy.
    // upgradeAvailable / previewingUpgrade / _preUpgradeLayout state
    // also gone.)

    // (Cleanup Phase B — Xav 2026-05-31): _defaultLayout method removed.
    // All callers went away when Phase 7 deleted the admin-default
    // reconcile + the resetLayout-to-defaults branch (resetLayout on a
    // user dashboard now goes straight to empty). Layouts come from
    // /api/system-presets or /api/user-dashboards/{id} exclusively.

    /** Picks which scoreboard widget renders in the headline zone for
     *  the current sport. Returns the FIRST sport-specific scoreboard
     *  match, else the wildcard scoreboard, else null. Used by
     *  templates/live.html to dispatch which partial renders.
     *
     *  Future bespoke scoreboards (NBA quarter clock, golf leader, etc.)
     *  just register `slot:'scoreboard'` + their sportScope; no template
     *  change needed beyond an x-show branch for the new partial. */
    get activeScoreboardId() {
      /* The board's STORED choice wins, when it is a real one for this sport;
         otherwise the sport's default.

         Was latent (dashboard allocation audit, 2026-08-08): this resolved
         purely by sport and ignored layout.scoreboard_id entirely. Harmless
         while every sport has exactly one scoreboard - the stored choice, when
         anything ever set it, would equal the default anyway. What made it
         worth closing rather than leaving as a comment is the Dashboard
         Studio: its diff already reports "~ scoreboard changed"
         (live_v2/studio_diff.js), so the moment a second scoreboard exists the
         Studio would promise a change the live board silently refused to make.
         A tool that lies about what it is about to publish is a worse bug than
         the one-line render it was hiding.

         VALIDATED, and that is the whole trick: an id that is not among this
         sport's registered scoreboards is ignored rather than returned. Every
         template branch keys off this value, so honouring a stale or
         wrong-sport id would render an empty headline zone - a blank board
         with nothing in the console. */
      if (!window.LSWidgets || !window.LSWidgets.forSlot) return 'scoreboard';
      const sid = this._sportId;
      const all = window.LSWidgets.forSlot('scoreboard', sid);
      const chosen = this.layout && this.layout.scoreboard_id;
      if (chosen && all.some(w => w.id === chosen)) return chosen;
      // Prefer entries with a typed sportScope (array) over '*' fallback.
      const specific = all.find(w => Array.isArray(w.sportScope));
      if (specific) return specific.id;
      const wildcard = all.find(w => !w.sportScope || w.sportScope === '*');
      return wildcard ? wildcard.id : null;
    },

    _reloadLayoutForCurrentSport() {
      const sid = this._sportId;
      const pid = this._preset;
      // System preset: load from /api/system-presets (cached on the
      // host as _systemPresetData; section materialisation handled
      // inside _loadLayoutFromSystemPreset). User dashboards: caller
      // (switchToActiveDashboard) loads via /api/user-dashboards.
      // Reload only fires for system presets here — sport-change watcher
      // is the entry point.
      if (this._isSystemDashboard) {
        // Cleanup Phase B — no offline fallback. If the API call fails
        // the current layout stays put until a successful reload.
        return this._loadLayoutFromSystemPreset(sid, pid);
      } else {
        // User dashboards re-load via switchToActiveDashboard, not via
        // sport changes (user dashboards are sport-scoped — switching
        // sport drops out of the dashboard's scope, picker should
        // refresh the list).
        this.layout = _migrateLayout(null, sid);
        this._rowsFloor = 1;
      }
    },

    // ── Modal helpers ─────────────────────────────────────────────────
    openModal(id) { if (id) this.activeModal = id; },
    closeModal() { this.activeModal = null; },
    get activeModalTitle() {
      switch (this.activeModal) {
        case 'all_players_modal': return 'All Players';
        case 'my_players_modal':  return 'My Players';
        case 'my_lineups_modal':  return 'My Lineups';
        case 'dreamteam_modal':   return 'Dream Team';
        default:                  return '';
      }
    },
    get activeModalSubtitle() {
      switch (this.activeModal) {
        case 'all_players_modal': return 'Full slate pool — sortable, filterable';
        case 'my_players_modal':  return 'Every player across your lineups';
        case 'my_lineups_modal':  return 'All your lineups with live, pace, and player detail';
        case 'dreamteam_modal':   return "Today's Dream Team with salary, role, and stats";
        default:                  return '';
      }
    },

    // ── Panel-grid helpers (operate on layout.panels) ─────────────────

    /** Tablet projection (768-1279px): pack panels into a forced 2-up
     *  grid. Any panel saved with w<6 is promoted to w=6 (half the
     *  viewport); panels saved with w>=6 keep a full-width row. Walks
     *  layout.panels in order (so the user's mental sequence stays),
     *  fills left col then right col, increments row by the taller of
     *  the two siblings. Returns Map<id, {x,y,w,h}>.
     *
     *  Render-time projection only — never mutates layout.panels, so
     *  exiting tablet mode (resize back to desktop) restores the user's
     *  exact saved arrangement.
     */
    get _tabletPlacements() {
      const map = new Map();
      const panels = this.layout.panels || [];
      let row = 0;
      let leftH = 0;          // h of the left-col widget on the current row, 0 if empty
      for (const p of panels) {
        const h = Math.max(1, p.h | 0);
        // > 6, not >= 6. The presets are built from 6-wide halves, so "half or
        // wider is a full row" sent EVERY default panel full-width: an iPad in
        // landscape rendered the desktop board as a single 4,400px column where
        // a desktop shows the same section 2x2 (mobile audit 2026-09-12).
        // ...or a panel whose own layout needs more width than 2-up can give.
        // 2-up is half the grid, which is 298px on a 1024 iPad and 383px on a
        // 1194 iPad Pro - fine for a KPI, too narrow for a card that prints a
        // sentence. minWidthPx lets such a widget say so, instead of every
        // panel going full-width (tried in 2026-09-12: a 4,400px column).
        const reg = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(p.id);
        const minW = (reg && reg.minWidthPx) | 0;
        const halfW = (((this._gridW || (typeof window !== 'undefined' ? window.innerWidth : 0)) | 0) - 8) / 2;
        const tooNarrow = minW > 0 && halfW > 0 && halfW < minW;
        const wantsFull = (p.w | 0) > 6 || tooNarrow;
        if (wantsFull) {
          // Flush any in-progress row before starting a full-width one.
          if (leftH > 0) { row += leftH; leftH = 0; }
          map.set(p.id, { x: 0, y: row, w: 12, h });
          row += h;
        } else if (leftH === 0) {
          map.set(p.id, { x: 0, y: row, w: 6, h });
          leftH = h;
        } else {
          map.set(p.id, { x: 6, y: row, w: 6, h });
          row += Math.max(leftH, h);
          leftH = 0;
        }
      }
      return map;
    },

    // ── Breathing panels (Broadcast board, Xav 2026-09-30) ─────────────
    // A panel with nothing to say yet (no homers, nobody near a bonus, no big
    // moments) leaves the grid for the dock; the busy panels in its column
    // take its rows. A widget opts in by declaring `quiet(scope)` in its
    // registry entry - it returns the one-line "why I'm quiet" text, or ''.
    // It is read against the LIVE widget's own Alpine scope, so a stateful
    // widget (the homer feed fills itself from a watcher) answers from its
    // real state, not a fresh copy. Everything else is never quiet.
    _breathTick:   0,            // bumped every 2s: widgets mount after the first read
    _breathForced: {},           // { id: true } - "show it anyway" from the dock
    _breathWoken:  {},           // { id: true } - just came back; ringed for a few seconds
    get _breathOn() {
      return this.activeSkin === 'broadcast' && !this.editMode && !this.mobile
          && !this.tablet && !this._phoneCanvasOn;
    },
    _panelQuietLine(id) {
      const def = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id);
      if (!def || typeof def.quiet !== 'function') return '';
      if (this._breathForced[id]) return '';
      void this._breathTick;
      const root = document.querySelector('.ls-grid [data-widget-id="' + id + '"]');
      if (!root || !window.Alpine) return '';
      const el = root.hasAttribute('x-data') ? root : (root.querySelector('[x-data]') || root);
      try { return def.quiet(window.Alpine.$data(el)) || ''; } catch (_) { return ''; }
    },
    get _breathQuiet() {
      if (!this._breathOn) return [];
      const out = [];
      for (const p of (this.layout.panels || [])) {
        const line = this._panelQuietLine(p.id);
        if (!line) continue;
        const def = window.LSWidgets.get(p.id) || {};
        out.push({ id: p.id, line, title: this.widgetTitle(p.id, def.title || p.id) });
      }
      // A column whose panels are ALL quiet keeps them: the dock is for
      // making room for something busy, not for emptying the board.
      const quiet = new Set(out.map(q => q.id));
      for (const p of (this.layout.panels || [])) if (!this.panelShouldRender(p.id)) quiet.add(p.id);
      const cols = new Map();
      for (const p of (this.layout.panels || [])) {
        const k = (p.x | 0) + ':' + (p.w | 0);
        if (!cols.has(k)) cols.set(k, []);
        cols.get(k).push(p.id);
      }
      const keep = new Set();
      for (const ids of cols.values()) if (ids.every(i => quiet.has(i))) ids.forEach(i => keep.add(i));
      // ...except a band across the whole board (Big Moments under the
      // Director and the Wire): the panels above it take its rows. Kept only
      // when nothing on the board is busy.
      const all = (this.layout.panels || []);
      const anyBusy = all.some(p => !quiet.has(p.id));
      for (const p of all) if ((p.w | 0) >= 12 && anyBusy) keep.delete(p.id);
      return out.filter(q => !keep.has(q.id));
    },
    get _breathQuietKey() { return this._breathQuiet.map(q => q.id).join(','); },
    /** id -> {x,y,w,h} for the busy panels, null for a docked one. Panels in
     *  the same column (same x and width) re-stack top to bottom over the
     *  column's full height, each keeping its share of it. */
    get _breathPlacements() {
      // Out of the column: docked panels, and lineup-only panels a spectator
      // never gets (they left a hole where My Lineups / My Players would be).
      const quiet = new Set(this._breathQuiet.map(q => q.id));
      for (const p of (this.layout.panels || [])) if (!this.panelShouldRender(p.id)) quiet.add(p.id);
      const map = new Map();
      if (!this._breathOn || !quiet.size) return map;
      const cols = new Map();
      for (const p of (this.layout.panels || [])) {
        const k = (p.x | 0) + ':' + (p.w | 0);
        if (!cols.has(k)) cols.set(k, []);
        cols.get(k).push(p);
      }
      // a quiet band across the whole board: whatever stands on it grows down
      // over its rows
      const bands = (this.layout.panels || []).filter(p => (p.w | 0) >= 12 && quiet.has(p.id)
                                                       && this._breathQuiet.some(q => q.id === p.id));
      for (const b of bands) {
        map.set(b.id, null);
        for (const p of (this.layout.panels || [])) {
          if (p === b || quiet.has(p.id)) continue;
          if ((p.y | 0) + Math.max(1, p.h | 0) === (b.y | 0)) {
            const cur = map.get(p.id) || { id: p.id, x: p.x, w: p.w, y: p.y | 0, h: Math.max(1, p.h | 0) };
            map.set(p.id, { ...cur, h: cur.h + Math.max(1, b.h | 0) });
          }
        }
      }
      for (const ps of cols.values()) {
        if (ps.some(p => bands.includes(p))) continue;
        if (!ps.some(p => quiet.has(p.id))) continue;
        ps.sort((a, b) => (a.y | 0) - (b.y | 0));
        const top = ps[0].y | 0;
        const bottom = Math.max(...ps.map(p => (p.y | 0) + Math.max(1, p.h | 0)));
        let live = ps.filter(p => !quiet.has(p.id));
        // A column with nothing busy keeps its quiet panels (they stay out of
        // the dock) - stretched over the rows an absent panel left behind.
        if (!live.length) live = ps.filter(p => this.panelShouldRender(p.id));
        if (!live.length) continue;
        const sum = live.reduce((t, p) => t + Math.max(1, p.h | 0), 0);
        let y = top;
        live.forEach((p, i) => {
          const h = i === live.length - 1 ? bottom - y
                  : Math.max(1, Math.round(Math.max(1, p.h | 0) * (bottom - top) / sum));
          map.set(p.id, { id: p.id, x: p.x, w: p.w, y, h });
          y += h;
        });
        ps.filter(p => !live.includes(p)).forEach(p => map.set(p.id, null));
      }
      return map;
    },
    _breathForce(id) {
      this._breathForced = { ...this._breathForced, [id]: true };
    },

    cellStyle(w) {
      if (this._breathOn) {
        const m = this._breathPlacements;
        if (m.has(w.id)) {
          const p = m.get(w.id);
          if (!p) return 'display:none';
          return LSGrid.cellStyle(p) + (this._breathWoken[w.id] ? '; --bc-woken: 1' : '');
        }
        if (this._breathWoken[w.id]) return LSGrid.cellStyle(w) + '; --bc-woken: 1';
      }
      if (this.tablet) {
        const projected = this._tabletPlacements.get(w.id);
        if (projected) return LSGrid.cellStyle(projected);
      }
      return LSGrid.cellStyle(w);
    },
    cellOrder(id) { return LSGrid.orderOf(this.layout.panels, id); },

    /** Returns a CSS custom-property declaration `; --ls-max-rows: N`
     *  if the widget declares a maxRows cap (set in widgets_registry +
     *  merged from DB via _catalog.js). Empty string otherwise. The
     *  widget's own CSS multiplies by its per-row height to produce a
     *  max-height in stack/auto-h contexts. Centralised so all 16
     *  widget x-for blocks can append `+ _maxRowsVar(w.id)` without
     *  duplicating registry lookups. (Xav 2026-05-30.) */
    _maxRowsVar(id) {
      const def = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id);
      const n   = def && def.maxRows;
      return (n && n > 0) ? ('; --ls-max-rows: ' + (n | 0)) : '';
    },

    /** Cell width preset for panel widgets — sets w to 3/4/6/12.
     *  Clamps to the registry minCells.w so the user can't pick a
     *  preset below the widget's designed floor. */
    setCellWidth(id, w) {
      const cell = this.layout.panels.find(c => c.id === id);
      if (!cell) return;
      const def  = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id);
      const minW = (def && def.minCells && (def.minCells.w | 0)) || 1;
      const newW = Math.max(minW, Math.min(12, w | 0));
      const maxX = 12 - newW;
      cell.w = newW;
      if ((cell.x | 0) > maxX) cell.x = maxX;
      this.layout = { ...this.layout, panels: [...this.layout.panels] };
      this._saveSoon();
    },
    cellWidthIs(id, w) {
      const cell = this.layout.panels.find(c => c.id === id);
      return cell ? (cell.w | 0) === (w | 0) : false;
    },

    /** Max (y + h) across a panel list — the rows the content actually
     *  occupies. */
    _maxPanelRows(panels) {
      let max = 1;
      for (const w of (panels || [])) {
        const span = (w.y | 0) + Math.max(1, w.h | 0);
        if (span > max) max = span;
      }
      return max;
    },
    /** Total grid rows — drives grid-template-rows. Content rows, but never
     *  below the load-time floor so shrinking a panel reveals empty space
     *  instead of the rows re-expanding to refill the fixed height.
     *  In tablet mode, the projected placements determine row count. */
    get gridRows() {
      if (this.tablet) {
        let max = 1;
        for (const p of this._tabletPlacements.values()) {
          const span = (p.y | 0) + Math.max(1, p.h | 0);
          if (span > max) max = span;
        }
        return max;
      }
      return Math.max(this._maxPanelRows(this.layout.panels), this._rowsFloor || 1);
    },

    /** True if a widget is visible in any slot. */
    isVisible(id) {
      return this.layout.scoreboard_id === id
          || (this.layout.kpis || []).includes(id)
          || (this.layout.panels || []).some(w => w.id === id);
    },

    // ── Personal strip getters (unchanged) ────────────────────────────
    get bestLineup() {
      const lus = this.lineupRows || [];
      if (!lus.length) return null;
      let best = lus[0];
      for (const lu of lus) {
        if ((lu.live_total || 0) > (best.live_total || 0)) best = lu;
      }
      return best;
    },
    get bestLineupTotal() { return this.bestLineup?.live_total || 0; },
    get bestLineupLabel() { return this.bestLineup?.lineup_name || 'No lineup'; },
    get topContributor() {
      let best = null;
      for (const lu of (this.lineupRows || [])) {
        for (const p of (lu.players || [])) {
          if ((p.live_fpts || 0) > 0 && (!best || (p.live_fpts || 0) > (best.live_fpts || 0))) {
            best = p;
          }
        }
      }
      return best;
    },
    get deltaToDream() {
      const dream = (this.dreamTeam || []).reduce(
        (s, p) => s + (p.live_fpts || 0) * (p.role_mult || 1), 0,
      );
      if (dream <= 0) return null;
      return this.bestLineupTotal - dream;
    },

    // ── Picker (per-slot catalogs) ────────────────────────────────────
    /** Available widgets for a given slot, minus those already in use. */
    availableForSlot(slot) {
      const sid = this._sportId;
      // Board format: live reads it off the slate template (_formatVariant
      // getter); the Studio has no real slate, so initStudioPreview stamps the
      // preset's variant on the plain formatVariant prop. Either resolves the
      // active format so the picker only offers format-appropriate widgets.
      const fmt = this._formatVariant || this.formatVariant || '';
      const used = new Set([
        this.layout.scoreboard_id,
        ...(this.layout.kpis || []),
        ...(this.layout.panels || []).map(w => w.id),
      ].filter(Boolean));
      return window.LSWidgets.forSlot(slot, sid)
        .filter(w => !used.has(w.id)
                     && w.is_db_active !== false          // hide admin-deactivated widgets (P1 audit)
                     && window.LSWidgets._matchFormat(w, fmt));
    },

    /** Same as availableForSlot but grouped by widget.category for the
     *  picker UI (Phase 5 of the dashboard refactor). Returns:
     *    [ { category: 'Lineups', items: [w, ...] }, ... ]
     *  Categories follow LS_PICKER_CATEGORY_ORDER below; unknown
     *  categories sort alphabetically; 'Other' (no category set) is
     *  always last. Items within a group keep sort_order. */
    availableForSlotGrouped(slot) {
      const all = this.availableForSlot(slot);
      const groups = new Map();
      for (const w of all) {
        const cat = w.category || 'Other';
        if (!groups.has(cat)) groups.set(cat, []);
        groups.get(cat).push(w);
      }
      const idx = (cat) => {
        const i = LS_PICKER_CATEGORY_ORDER.indexOf(cat);
        return i === -1 ? 9999 : i;
      };
      return [...groups.entries()]
        .sort(([a], [b]) => {
          if (a === 'Other') return 1;
          if (b === 'Other') return -1;
          const ia = idx(a), ib = idx(b);
          if (ia !== ib) return ia - ib;
          return a.localeCompare(b);
        })
        .map(([category, items]) => ({ category, items }));
    },

    /** Render-gate for an in-layout KPI. Simple in-layout check —
     *  projection-gated KPIs render regardless of whether the user has
     *  projections; they show a 'needs projections' state inside the
     *  tile rather than hiding (Xav 2026-05-27: hiding was too
     *  complicated, easier to show a warning in-tile). */
    // Widgets that read the viewer's OWN lineups — meaningless with none. On a
    // no-lineup view (a spectator, or a locked single-stat slate opened without
    // an entry) they'd render empty, so we drop them entirely and let the board
    // fill around it. The lineup_scores panel x-for gates on this too.
    _LINEUP_WIDGETS: ['lineup_scores', 'kpi_my_best_lineup', 'kpi_vs_dream_team',
                      'batter_pulse', 'pitcher_pulse', 'top10_my_lineups', 'top10_my_players',
                      'kpi_mlb_my_bombs', 'kpi_mlb_my_ks', 'kpi_mlb_at_bat_watch',
                      'kpi_most_owned', 'kpi_team_news',
                      'my_stacks', 'kpi_playing_now', 'kpi_live_pace', 'kpi_last15'],
    _needsLineup(id) { return this._LINEUP_WIDGETS.indexOf(id) >= 0; },
    /** Section tabs to show for the current viewer. A section whose panels are
     *  ALL lineup-gated (e.g. single-stat MY STUFF) would render empty for a
     *  no-lineup spectator, so the whole tab is dropped rather than shown blank.
     *  hasLineup viewers see every section. Used by the tab strip AND section
     *  resolution so a spectator never lands on a hidden tab. */
    get _visibleSections() {
      const secs = (this._systemPresetData && this._systemPresetData.sections) || [];
      let base = this.hasLineup
        ? secs
        : secs.filter(s => ((s.layout && s.layout.panels) || []).some(p => !this._needsLineup(p.id)));
      // Broadcast with no lineup is the Spectator board: The Action, The Slate
      // and The Podium. My Lineups, My Players and The Lab are all about your
      // lineups - three tabs of one-sentence panels (Xav 2026-10-10, audit v2
      // run 3 UX-1 option 3; the plan's Spectator format). Adding a lineup
      // brings them back.
      if (!this.hasLineup && this.activeSkin === 'broadcast') {
        base = base.filter(s => !['my_lineups', 'my_players', 'analytics'].includes(s.id));
      }
      // Free (one lineup): The Lab shows, LOCKED with a Pro badge - seen, not
      // hidden, so a free player knows what Pro adds (Xav 2026-10-11, plan
      // Phase 1). My Lineups is open: the one-lineup cap is the gate already
      // (Xav 2026-10-11). _boardFree also lets an admin preview it (?free=1).
      else if (this._boardFree && this.activeSkin === 'broadcast') {
        base = base.map(s => (s.id === 'analytics' ? { ...s, _locked: true } : s));
      }
      // THE SLATE (the Cover): a UNIVERSAL synthetic tab prepended as tab 1
      // across EVERY preset while the slate is pre-lock — the curated "here's
      // the slate" entry (fixtures, probables, weather, countdown). It is NOT in
      // _systemPresetData.sections, so edit mode (which iterates that) never
      // lists it → curated / non-editable for free. It's also the DEFAULT landing
      // (see _loadLayoutFromSystemPreset), which lazy-defers the heavy widget
      // board until the user clicks into THE ACTION / a real tab. See
      // docs/pre_slate_spec.md.
      // The Cover is "Pre-Slate" (Xav 2026-10-07): tab 1 until lock, never
      // mixed with the board's own The Slate tab, which keeps its place.
      if (this._isPregame) return [{ id: 'pregame', label: 'Pre-Slate', _pregame: true }, ...base];
      return base;
    },
    // ── THE SLATE / Cover (pre-game hub) ──────────────────────────────
    get _isPregame() {
      // Show the Cover whenever the slate is pre-lock. Sport-aware variants
      // only (partials/_pregame_<sport>.html via the _pregame.html dispatcher);
      // other sports keep the normal board until their variant is built.
      if (!['mlb', 'nfl', 'nba', 'nhl'].includes(this._sportId)) return false;
      const s = this.selectedSlate;
      // Use the slate's own lock/start time as the pre-game signal — it's set at
      // slate-selection, BEFORE allGames is fetched. The section resolver runs
      // pre-fetch, so keying off allGames here would be too late (the board would
      // already materialise). Future start_time = pre-lock = show the Cover.
      if (s && s.start_time) {
        try { return new Date(s.start_time).getTime() > Date.now(); } catch (e) {}
      }
      // Fallback once game data is in (e.g. missing start_time).
      const gs = this.allGames || [];
      return gs.length > 0 && gs.every(g => (g.status ?? 1) < 2);
    },
    /** Clicked rest-of-slate card (game_id) promoted to the featured deep
     *  card; null = default (next to lock). A stale pick (game went live /
     *  slate switched) simply misses the find below and falls back — no
     *  explicit reset needed. */
    _pregameSel: null,
    _pregameFeature(g) {
      this._pregameSel = g ? String(g.game_id) : null;
    },
    /** {featured, rest, count} — upcoming games sorted by start time.
     *  featured = the user's clicked pick, else the next game to lock;
     *  rest = every other upcoming game. */
    get _pregameGames() {
      const up = (this.allGames || []).filter(g => (g.status ?? 1) < 2)
        .slice().sort((a, b) => new Date(a.game_time_utc || 0) - new Date(b.game_time_utc || 0));
      let featured = up[0] || null;
      if (this._pregameSel != null) {
        const pick = up.find(g => String(g.game_id) === this._pregameSel);
        if (pick) featured = pick;
      }
      return { featured, rest: up.filter(g => g !== featured), count: up.length };
    },
    _pregameTime(iso) {
      if (!iso) return '';
      try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch (e) { return ''; }
    },
    _pregameCountdown(iso) {
      if (!iso) return '';
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) return 'Starting';
      const m = Math.floor(ms / 60000), h = Math.floor(m / 60);
      return h > 0 ? (h + 'h ' + (m % 60) + 'm') : (m + 'm');
    },
    /** Featured game's confirmed batting orders, split away/home by LiveSlate
     *  team_id — id-to-id (game.home_team_id/away_team_id vs the player's
     *  team_id, both canonical), never abbr, so no cross-source drift (a DK
     *  "WSH" pool player vs a provider "WAS" game would silently mismatch on
     *  abbr). game_id filter first so a doubleheader's two games stay distinct.
     *  Empty per side until bat_order posts (~1-2h pre first pitch). Reuses the
     *  confirmed_lineups data shape (allSlatePlayers + stats.bat_order). */
    _pregameLineup(game) {
      const empty = { away: [], home: [], posted: false };
      if (!game) return empty;
      const gid = String(game.game_id);
      const rows = (this.allSlatePlayers || [])
        .filter(p => String(p.game_id) === gid && (p.stats?.bat_order || 0) > 0);
      const forTeam = tid => (tid == null || tid === '') ? []
        : rows.filter(p => String(p.team_id) === String(tid))
              .sort((a, b) => (a.stats.bat_order || 0) - (b.stats.bat_order || 0));
      const away = forTeam(game.away_team_id), home = forTeam(game.home_team_id);
      return { away, home, posted: (away.length + home.length) > 0 };
    },
    /** A game's weather (stamped into live_state by the poller), or null. Shape:
     *  {temperature_f, condition, wind_speed, wind_direction, precipitation,
     *  roof, venue_name} — or {roof:'dome', venue_name} for a fixed dome. */
    _pregameWx(game) {
      const ls = game && game.live_state;
      return (ls && ls.weather) || null;
    },
    /** Human "updated Xm ago" for a weather cell, from wx.updated_at (stamped
     *  when the poller last fetched — within the 20-min cache window). */
    _pregameWxAge(wx) {
      const iso = wx && wx.updated_at;
      if (!iso) return '';
      const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
      if (!isFinite(secs) || secs < 0) return '';
      if (secs < 90) return 'updated just now';
      const m = Math.floor(secs / 60);
      if (m < 60) return 'updated ' + m + 'm ago';
      const h = Math.floor(m / 60);
      return 'updated ' + h + 'h ago';
    },
    // ── THE SLATE — indoor variant helpers (NBA / NHL, _pregame_court) ─
    /** What the countdown counts down to, in the sport's own words. */
    get _pregameWord() {
      const w = window.LSModelUtil ? window.LSModelUtil.startWord(this._sportId) : 'kick-off';   // one list (LSModelUtil)
      return w.charAt(0).toUpperCase() + w.slice(1);
    },
    /** One side's rest, from /api/slate-brief .rest (the league's schedule by
     *  game id): {label, b2b}. '' label when the schedule has nothing. */
    _restFor(game, side) {
      const r = ((((this._brief || {}).rest || {})[String(game && game.game_id)]) || {})[side] || {};
      if (r.b2b) return { label: 'Back-to-back', b2b: true };
      if (r.days) return { label: r.days + " days' rest", b2b: false };
      return { label: this._brief && (this._brief.rest || {})[String(game && game.game_id)] ? 'Rested' : '', b2b: false };
    },
    /** NBA: one side's confirmed starting five - the league flags starters in
     *  the boxscore ~30-40 min before tip (pool stats.starter). Side by TEAM ID
     *  (game.home_team_id / away_team_id vs the player's team_id), never abbrev. */
    _startersFor(game, side) {
      if (!game) return [];
      const tid = String(game[side + '_team_id']), gid = String(game.game_id);
      return (this.allSlatePlayers || [])
        .filter(p => String(p.game_id) === gid && String(p.team_id) === tid && (p.stats || {}).starter)
        .slice(0, 5);
    },
    /** NHL: one side's goalies with this season's line, the likely starter
     *  flagged (most starts) - /api/slate-brief .goalies. Never "confirmed". */
    _goaliesFor(game, side) {
      return ((((this._brief || {}).goalies || {})[String(game && game.game_id)]) || {})[side] || [];
    },

    // ── THE SLATE — NFL variant helpers ──────────────────────────────
    /** Betting line stamped into live_state by the feed (ESPN scoreboard
     *  odds; BDL wires at GOAT validation). {details, over_under} or null. */
    _pregameOdds(game) {
      const ls = game && game.live_state;
      // NBA / NHL: no line in the feed - /api/slate-brief .odds (ESPN, joined
      // by team id server-side, written in our team codes).
      return (ls && ls.odds) || (((this._brief || {}).odds || {})[String(game && game.game_id)]) || null;
    },
    /** Team record ("10-7") from the feed stamp, '' pre-data. */
    _pregameRec(game, side) {
      const ls = game && game.live_state;
      return (ls && ls[side + '_record']) || '';
    },
    /** Injury report for one game off the pool's DK statuses. Grouped by the
     *  player rows' OWN team string (display grouping, not matching — never
     *  abbrev-matched against the game sides, per the no-abbrev rule). Rows
     *  sorted worst-first (OUT → DOUB → QUES) then by salary. */
    _pregameInjuries(game) {
      const empty = { groups: [], count: 0 };
      if (!game) return empty;
      const FLAG = { injured: 'OUT', doubtful: 'DOUB', possible: 'QUES' };
      const order = { OUT: 0, DOUB: 1, QUES: 2 };
      const gid = String(game.game_id);
      const rows = (this.allSlatePlayers || [])
        .filter(p => String(p.game_id) === gid && FLAG[p.lineup_status])
        .map(p => ({ ...p, _flag: FLAG[p.lineup_status] }))
        .sort((a, b) => (order[a._flag] - order[b._flag]) || ((b.salary || 0) - (a.salary || 0)));
      const byTeam = {};
      rows.forEach(p => { (byTeam[p.team || '—'] = byTeam[p.team || '—'] || []).push(p); });
      return {
        groups: Object.keys(byTeam).sort().map(t => ({ team: t, rows: byTeam[t] })),
        count: rows.length,
      };
    },
    /** THE cover's job: warn BEFORE lock about flagged players in MY lineups.
     *  Joins my lineup players (canonical player_id) to the pool's platform
     *  injury status — id-to-id, never names. Worst-first, then by how many
     *  of my lineups carry the player. [{name, team, pos, _flag, n}] */
    get _pregameMyAlerts() {
      const FLAG = { injured: 'OUT', doubtful: 'DOUB', possible: 'QUES' };
      const order = { OUT: 0, DOUB: 1, QUES: 2 };
      const lus = this.lineupRows || [];
      if (!lus.length) return [];
      const counts = {};
      lus.forEach(lu => (lu.players || []).forEach(p => {
        const k = String(p.player_id || '');
        if (k) counts[k] = (counts[k] || 0) + 1;
      }));
      const out = [];
      (this.allSlatePlayers || []).forEach(p => {
        const f = FLAG[p.lineup_status];
        const n = counts[String(p.player_id || '')];
        if (f && n) out.push({ name: p.name, team: p.team, pos: p.pos, _flag: f, n });
      });
      return out.sort((a, b) => (order[a._flag] - order[b._flag]) || (b.n - a.n));
    },
    // ── THE SLATE · news + Brief (/api/slate-brief) ────────────────────
    // Loaded when THE SLATE renders, then every five minutes while the slate
    // is still pre-game. News is free; locked Brief tiles arrive from the
    // server as {title, count, locked} with nothing else in them.
    _brief: null,
    _briefFor: null,
    _briefAt: 0,
    _briefTimer: null,
    _briefNewsAll: false,
    async _loadBrief(force) {
      const s = this.selectedSlate;
      if (!s || !s.id || !this._isPregame) return;
      const fresh = this._briefFor === s.id && (Date.now() - this._briefAt) < 5 * 60000;
      if (fresh && !force) return;
      if (this._briefFor !== s.id) { this._brief = null; this._briefNewsAll = false; this._scope = null; }
      this._briefFor = s.id; this._briefAt = Date.now();
      try {
        const r = await fetch('/api/slate-brief?slate_id=' + encodeURIComponent(s.id), { credentials: 'same-origin' });
        if (r.ok && this._briefFor === s.id) this._brief = await r.json();
      } catch (e) { /* the tab is complete without it */ }
      clearTimeout(this._briefTimer);
      this._briefTimer = setTimeout(() => this._loadBrief(true), 5 * 60000);
    },
    /** A story's reach into my lineups: how many of my lineups carry any
     *  player it tags - id-to-id, like the lineup alert. 0 with none. */
    _briefMine(item) {
      const tagged = new Set((item.players || []).map(q => q.player_id));
      let n = 0;
      (this.lineupRows || []).forEach(lu => {
        if ((lu.players || []).some(p => tagged.has(String(p.player_id || '')))) n++;
      });
      return n;
    },
    get _briefNews() {
      const all = (this._brief && this._brief.news) || [];
      return this._briefNewsAll ? all : all.slice(0, 6);
    },
    _briefAgo(iso) {
      if (!iso) return '';
      const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
      if (s < 3600) return Math.max(1, Math.round(s / 60)) + 'm ago';
      if (s < 86400) return Math.round(s / 3600) + 'h ago';
      return Math.round(s / 86400) + 'd ago';
    },
    _briefMoney(v) { return v ? '$' + Math.round(v).toLocaleString('en-US') : ''; },
    _briefFlag(st) { return ({ injured: 'OUT', doubtful: 'DOUBT', possible: 'QUES' })[st] || ''; },
    _briefBar(t, tile) {
      const span = (tile.hi - tile.lo) || 1;
      return Math.round(12 + 88 * (t.total - tile.lo) / span) + '%';
    },

    // ── THE SLATE scope (NFL): ONE selector, two zoom levels ─────────────
    // The games strip is the only thing that changes what the page is about:
    // null = the whole slate (the default), or a game_id = that game only.
    // The Brief and News follow it, so nothing ever sits beside something it
    // isn't about (Xav: "a bit weird UX" when slate-wide Brief/News sat next to
    // one game's detail - scope mock approved 2026-09-27).
    //
    // Scoping is BY GAME ID through the pool (player_id -> the pool row's
    // game_id), never by matching team abbreviations across sources.
    _scope: null,
    _scopeSlate: null,
    _scopeSet(id) { this._scope = id ? String(id) : null; this._briefNewsAll = false; },
    /** A new slate starts on All games - a game id from the last slate means nothing here. */
    _scopeFor(slateId) { if (slateId !== this._scopeSlate) { this._scopeSlate = slateId; this._scope = null; } },
    get _scopeGame() {
      if (!this._scope) return null;
      return (this._pregameGames.featured && String(this._pregameGames.featured.game_id) === this._scope
                ? this._pregameGames.featured
                : (this._pregameGames.rest || []).find(g => String(g.game_id) === this._scope)) || null;
    },
    /** Every upcoming game, kickoff order - the strip. */
    get _scopeGames() {
      const pg = this._pregameGames;
      return pg.featured ? [pg.featured, ...(pg.rest || [])]
        .sort((a, b) => new Date(a.game_time_utc || 0) - new Date(b.game_time_utc || 0)) : [];
    },
    get _pidGame() {
      const m = {};
      (this.allSlatePlayers || []).forEach(p => { if (p.player_id) m[String(p.player_id)] = String(p.game_id || ''); });
      return m;
    },
    _inGame(item, gid) {
      if (!gid) return true;
      const own = item.game_id || this._pidGame[String(item.player_id || '')];
      return String(own || '') === String(gid);
    },
    _newsIn(gid) {
      const all = (this._brief && this._brief.news) || [];
      return gid ? all.filter(n => (n.players || []).some(p => this._inGame(p, gid))) : all;
    },
    get _scopeNews() {
      const list = this._newsIn(this._scope);
      return this._briefNewsAll ? list : list.slice(0, 6);
    },
    _outIn(g) {
      const gid = String(g.game_id);
      return (this.allSlatePlayers || []).filter(p => String(p.game_id) === gid && p.lineup_status === 'injured').length;
    },
    _briefTile(kind) {
      return ((this._brief && this._brief.brief) || []).find(t => t.kind === kind) || null;
    },
    /** Heads-up for the current scope: what DK has out that the news has as
     *  playing, and (in a game) that game's priciest absences. Locked for free
     *  users - then only whether this scope has anything, never what. */
    get _scopeHeads() {
      const gid = this._scope, conf = this._briefTile('conflict'), fall = this._briefTile('fallout');
      if ((conf && conf.locked) || (fall && fall.locked)) {
        const n = [conf, fall].filter(Boolean).reduce((s, t) =>
          s + (gid ? ((t.games || []).includes(gid) ? 1 : 0) : (t.kind === 'conflict' ? t.count : 0)), 0);
        return { locked: true, n };
      }
      const c = ((conf && conf.items) || []).filter(i => this._inGame(i, gid));
      const f = gid ? ((fall && fall.items) || []).filter(i => this._inGame(i, gid)) : [];
      return { locked: false, conflicts: c, fallout: f, n: c.length + f.length };
    },
    /** A game's expected points per team, highest first - worked out on the
     *  SERVER (core.slate_brief.totals_by_game) and read here by game_id.
     *  Doing it here meant comparing the odds' team code with the game's, an
     *  abbreviation join the dashboard never makes (no_abbrev_joins guard). */
    _impl(g) {
      return ((this._brief && this._brief.totals) || {})[String(g && g.game_id)] || [];
    },
    // ── THE SLATE scope — MLB tiles ─────────────────────────────────────
    // Same shell as NFL (strip, "Showing" line, Brief + a right column) with
    // baseball's own facts. MLB has no odds in our feed and no player-id link
    // to ESPN news yet, so its right column is LINEUPS - the thing that
    // actually moves before first pitch. Everything joins by id: my lineup
    // players -> the pool row (player_id) -> its game_id / team_id; a team's
    // batting order is the pool rows with that team_id and a bat_order.
    get _poolById() {
      const m = {};
      (this.allSlatePlayers || []).forEach(p => { if (p.player_id) m[String(p.player_id)] = p; });
      return m;
    },
    _isPitcher(pos) { return /(^|\/)(S?P|RP)($|\/)/.test(String(pos || '')); },
    _surname(n) { const w = String(n || '').replace(/\b(Jr|Sr|II|III|IV)\.?$/i, '').trim().split(/\s+/); return w[w.length - 1] || ''; },
    /** Has this team's batting order posted? By team_id + game_id. */
    _mlbPosted(g, side) {
      return (this._pregameLineup(g)[side] || []).length > 0;
    },
    /** Lineups posted across the scope: {posted, total, pending: [labels]}. */
    get _mlbLineupsStatus() {
      const games = this._scopeGame ? [this._scopeGame] : this._scopeGames;
      let posted = 0; const pending = [];
      games.forEach(g => ['away', 'home'].forEach(side => {
        if (this._mlbPosted(g, side)) posted++;
        else pending.push(g[side + '_team']);
      }));
      return { posted, total: games.length * 2, pending };
    },
    /** Heads-up, MLB: MY players who are left out of a POSTED batting order,
     *  or flagged by DK - the late-swap alert. Pitchers are never "left out"
     *  (they don't bat). Scoped to the selected game by game_id. */
    get _mlbHeads() {
      const pool = this._poolById, gid = this._scope, seen = {};
      const FLAG = { injured: 'OUT', doubtful: 'DOUB', possible: 'QUES' };
      (this.lineupRows || []).forEach(lu => (lu.players || []).forEach(lp => {
        const p = pool[String(lp.player_id || '')];
        if (!p || (gid && String(p.game_id) !== gid)) return;
        const game = this._scopeGames.find(g => String(g.game_id) === String(p.game_id));
        let why = FLAG[p.lineup_status] || '';
        if (!why && game && !this._isPitcher(p.pos) && !(p.stats && p.stats.bat_order > 0)) {
          const side = String(game.home_team_id) === String(p.team_id) ? 'home'
                     : String(game.away_team_id) === String(p.team_id) ? 'away' : '';
          if (side && this._mlbPosted(game, side)) why = 'NOT IN LINEUP';
        }
        if (!why) return;
        const k = String(p.player_id);
        seen[k] = seen[k] || { player_id: k, name: p.name, team: p.team, pos: p.pos, why, n: 0 };
        seen[k].n++;
      }));
      const order = { 'NOT IN LINEUP': 0, OUT: 1, DOUB: 2, QUES: 3 };
      return Object.values(seen).sort((a, b) => (order[a.why] - order[b.why]) || (b.n - a.n));
    },
    /** Open-air games where wind or rain is worth a thought (rain = delay risk). */
    get _mlbWeatherWatch() {
      return this._scopeGames.map(g => ({ g, w: this._pregameWx(g) || {} }))
        .filter(({ w }) => w.roof === 'open' && ((w.gust || 0) >= 20 || (w.wind_speed || 0) >= 15 || (w.precipitation || 0) >= 40))
        .map(({ g, w }) => ({ g, text: this._chipWx(g) }))
        .sort((a, b) => ((this._pregameWx(b.g).gust || 0) + (this._pregameWx(b.g).precipitation || 0) / 2)
                      - ((this._pregameWx(a.g).gust || 0) + (this._pregameWx(a.g).precipitation || 0) / 2));
    },
    _mlbProbable(g, side) {
      return (g && g.live_state && g.live_state[side + '_probable_pitcher']) || '';
    },
    get _mlbProbablesTbd() {
      return this._scopeGames.filter(g => !this._mlbProbable(g, 'away') || !this._mlbProbable(g, 'home'));
    },

    /** One short weather phrase for a strip chip: what matters for DFS - wind
     *  and rain - with the temperature left to the game's own Weather tile. */
    _chipWx(g) {
      const w = this._pregameWx(g);
      if (!w) return '';
      if (w.roof === 'dome') return 'Dome';
      const gust = Math.round(w.gust || 0), wind = Math.round(w.wind_speed || 0);
      const rain = Math.round(w.precipitation || 0);
      return (gust >= 20 ? 'gusts ' + gust : (w.wind_direction || '') + ' ' + wind + ' mph')
        + (rain >= 40 ? ' · ' + rain + '% rain' : '');
    },
    kpiShouldRender(id) {
      if (this._needsLineup(id) && !this.hasLineup) return false;
      if (this._kpiNeedsAdd(id)) return false;   // its "needs a lineup" tile stands in
      // Broadcast: a projections-only tile (Pace, Best Est. Final) with no
      // projections uploaded is a dead "needs projections" box in the strip -
      // drop it and let the others take the width. Other boards unchanged.
      if (this.activeSkin === 'broadcast' && !this.hasProjections && !this.editMode) {
        const w = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id);
        if (w && w.requiresProjections) return false;
      }
      return this._kpiList.indexOf(id) >= 0;
    },
    /** The KPI strip to show. A layout may carry `pregame_kpis` (the Broadcast
     *  board does): until the first game on the slate starts, those replace the
     *  live KPIs, which would all read "–" or 0.00. Keyed on the games' own
     *  status rather than the slate's start time, so it flips at the real
     *  kick-off, not the scheduled one. Edit mode always shows the live list -
     *  that is the list the edit tools move. */
    get _kpiList() {
      const L = this.layout || {};
      if (!this.editMode && Array.isArray(L.pregame_kpis) && L.pregame_kpis.length && this._beforeKickoff) {
        return L.pregame_kpis;
      }
      // Live-only tiles (PACE, HOTTEST "no live action yet") made a finished
      // slate read as if it were still on (Broadcast audit run 1, LIVE-7 /
      // Xav P7): a board can name what its strip becomes at the end.
      if (!this.editMode && Array.isArray(L.final_kpis) && L.final_kpis.length && this._slateFinal) {
        return L.final_kpis;
      }
      return L.kpis || [];
    },
    /** Broadcast, every game final: a live-only label says what the number
     *  has become - "Last 15 Min" on a finished slate is the best 15 minutes,
     *  "Pace" is how the night ended against projection (Broadcast audit v2,
     *  NUMBERS-13 / LIVE-14). '' = keep the tile's own title. */
    _kpiFinalTitle(id) {
      if (this.activeSkin !== 'broadcast' || this.editMode || !this._slateFinal) return '';
      // the basis in the name: "Vs Projection" over "missed DK average" read
      // as a contradiction (run 2, UX-6); Playing Now / Next Up are live
      // words on a finished board (LIVE-7 / NUMBERS-6)
      const proj = (this.lineupRows || []).some(lu => (lu.players || []).some(p => Number(p.proj) > 0));
      const vs = proj ? 'Vs Projection' : 'Vs DK Average';
      return ({ kpi_last15: 'Best 15 Min', kpi_live_pace: vs, kpi_pace_spark: vs,
                kpi_pct_pace: 'Beat / Missed', kpi_playing_now: 'Your Players', kpi_next_up: 'Games' })[id] || '';
    },
    /** Every game on the slate is final. */
    get _slateFinal() {
      const gs = this.allGames || [];
      const st = (g) => (typeof this.effStatus === 'function' ? this.effStatus(g) : g.status);
      return gs.length > 0 && gs.every(g => (st(g) || 0) >= 3);
    },
    /** ONE sentence for every panel that needs a lineup and has none: what
     *  would show here before and during the slate, and the plain fact once
     *  it is over - "the race starts here" on a finished slate, three ways of
     *  saying it in one Lab (Broadcast audit v2 run 3, LIVE-4 / UX-1). */
    noLineupLine(what) {
      return this._slateFinal ? 'No lineups on this slate.' : 'Add a lineup to see ' + what + ' here.';
    },
    /** Where "Add a lineup" goes: the builder for this slate, back here after. */
    get addLineupHref() {
      const s = this.selectedSlate;
      return s ? ('/build?slate=' + s.id + '&return=/live/' + s.id) : '/slates';
    },
    /** Broadcast, no lineups: a lineup tile keeps its place in the strip,
     *  "–" and "needs a lineup", instead of dropping out - tabs lost tiles
     *  (The Action down to one stretched tile) and every tab's strip read
     *  differently (Broadcast audit v2, LIVE-5 / PREM-9). The way in, "Add a
     *  lineup", is on the panels' empty states (UX-4). */
    _kpiNeedsAdd(id) {
      return this.activeSkin === 'broadcast' && !this.editMode && !this.hasLineup
        && this._kpiPersonal(id) && this._kpiList.indexOf(id) >= 0;
    },
    _kpiPersonal(id) { return this._needsLineup(id) || this._BC_PERSONAL_KPIS.indexOf(id) >= 0; },
    /** ...but one of them says so, with the way in: four "needs a lineup"
     *  tiles a tab and an ADD A LINEUP on every panel was a wall of the same
     *  message (Broadcast audit v2 run 2, PREM-7 / UX-9 / LIVE-9). The first
     *  personal tile of the strip is the add tile; the rest step aside. */
    _kpiAddLead(id) {
      return this._kpiNeedsAdd(id) && this._kpiList.find(k => this._kpiPersonal(k)) === id;
    },
    /** Broadcast tiles about YOUR lineups beyond the shared lineup gate: with
     *  none they read 0.00 or a reason that was not the reason ("no three
     *  teammates in a lineup"), so they take the "needs a lineup" tile. */
    _BC_PERSONAL_KPIS: ['kpi_avg_live', 'kpi_lineup_spread', 'kpi_pct_pace', 'kpi_unique_players', 'kpi_my_best_player',
                        'kpi_my_best_value', 'kpi_my_best_stack3', 'kpi_hottest_mine',
                        'kpi_coldest_mine', 'kpi_nba_on_court', 'kpi_on_field', 'kpi_nfl_my_tds', 'kpi_nhl_goals_points',
                        'kpi_dream_owned', 'kpi_position_mix', 'kpi_momentum', 'kpi_carrying', 'kpi_dream_gap', 'kpi_pace_spark',
                        'kpi_best_play', 'kpi_hit_rate'],
    get _beforeKickoff() {
      const gs = this.allGames || [];
      return gs.length > 0 && gs.every(g => (g.status ?? 1) < 2);
    },
    /** Panel twin of kpiShouldRender: a lineup-gated panel drops for a no-lineup
     *  spectator. The template x-for already filters by id, so this is just the
     *  lineup gate — one helper instead of the inline `hasLineup || !_needsLineup`
     *  copied per panel. */
    panelShouldRender(id) {
      if (this.hasLineup || !this._needsLineup(id)) return true;
      // Broadcast keeps a panel that has its own no-lineup state, rather than
      // leaving a hole in the tab (v2 run 1, SPACE-9).
      return this.activeSkin === 'broadcast' && !this.editMode && this._BC_KEEP_NO_LINEUP.indexOf(id) >= 0;
    },
    _BC_KEEP_NO_LINEUP: ['my_stacks'],
    openPicker(slot) {
      const next = slot || 'panel';
      // Toggle: clicking the same picker's trigger again closes it.
      // Switching from one slot's picker to another (KPI → Panel) keeps
      // the dropdown open but swaps content.
      if (this.pickerOpen && this.pickerSlot === next) {
        this.pickerOpen = false;
        return;
      }
      this.pickerSlot = next;
      this.pickerOpen = true;
    },

    // ── Suggest-a-widget (lean suggestion box, Xav 2026-06-14) ────────
    suggestOpen:  false,
    suggestBusy:  false,
    suggestSent:  false,
    suggestForm:  { kind: 'panel', title: '', description: '' },
    openSuggest(slot) {
      this.pickerOpen  = false;
      this.suggestSent = false;
      this.suggestForm = { kind: (slot === 'kpi' ? 'kpi' : 'panel'), title: '', description: '' };
      this.suggestOpen = true;
    },
    closeSuggest() { this.suggestOpen = false; },
    async submitSuggest() {
      const title = (this.suggestForm.title || '').trim();
      if (!title || this.suggestBusy) return;
      this.suggestBusy = true;
      try {
        const r = await fetch('/api/widget-ideas', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind:        this.suggestForm.kind,
            title:       title,
            description: (this.suggestForm.description || '').trim(),
            sport_id:    this._sportId || null,
          }),
        });
        if (r.ok) {
          this.suggestSent = true;
          // Brief "thanks" beat, then close.
          setTimeout(() => { this.suggestOpen = false; }, 1100);
        }
      } catch (_) { /* best-effort; user can retry */ }
      this.suggestBusy = false;
    },

    // ── Add / remove (dispatchers — route to right slot via registry) ─
    addWidget(id) {
      const def = window.LSWidgets.get(id);
      if (!def) return;
      const slot = def.slot || 'panel';
      if      (slot === 'panel')      this.addPanel(id);
      else if (slot === 'kpi')        this.addKPI(id);
      else if (slot === 'scoreboard') this.setScoreboard(id);
      this.pickerOpen = false;
    },
    removeWidget(id) {
      const def  = window.LSWidgets.get(id);
      const slot = (def && def.slot) || 'panel';
      if      (slot === 'panel')      this.removePanel(id);
      else if (slot === 'kpi')        this.removeKPI(id);
      else if (slot === 'scoreboard') this.setScoreboard(null);
    },

    // ── Panel slot helpers ────────────────────────────────────────────
    addPanel(id) {
      const def = window.LSWidgets.get(id);
      if (!def) return;
      const maxY = this.layout.panels.reduce(
        (m, w) => Math.max(m, (w.y | 0) + (w.h | 0)), 0,
      );
      this.layout = {
        ...this.layout,
        panels: [...this.layout.panels, {
          id, x: 0, y: maxY,
          w: def.defaultCells.w, h: def.defaultCells.h,
        }],
      };
      this._saveSoon();
    },
    removePanel(id) {
      if (!this.isWidgetRemovable(id)) return;  // defense in depth
      this.layout = {
        ...this.layout,
        panels: this.layout.panels.filter(w => w.id !== id),
      };
      this._saveSoon();
    },

    // ── KPI slot helpers ──────────────────────────────────────────────
    /** Hard cap on KPIs in the headline strip: four, the number the board's
     *  strip is drawn for (plan Phase 6, Xav 2026-10-11; the server caps it
     *  too - user_dashboards.MAX_KPIS_PER_STRIP). The picker disables
     *  further adds at the cap; remove one to swap a different KPI in. */
    KPI_MAX: 4,
    addKPI(id) {
      const cur = this.layout.kpis || [];
      if (cur.includes(id)) return;
      if (cur.length >= this.KPI_MAX) return;     // hard cap — picker also disables click
      this.layout = {
        ...this.layout,
        kpis: [...cur, id],
      };
      this._saveSoon();
    },
    /** Convenience getter used by the picker header to show "N / 7"
     *  + by the at-cap UI to disable further additions. */
    get kpiCount() { return (this.layout.kpis || []).length; },
    get kpiAtCap() { return this.kpiCount >= this.KPI_MAX; },
    removeKPI(id) {
      if (!this.isWidgetRemovable(id)) return;  // defense in depth
      this.layout = {
        ...this.layout,
        kpis: (this.layout.kpis || []).filter(k => k !== id),
      };
      this._saveSoon();
    },

    /** Phase 6: can a user remove this widget from their layout?
     *  - removable=false  → locked in (admin pinned it)
     *  - required=true    → must be present (admin marked the spine)
     *  Returns true if neither flag blocks removal. Templates use this
     *  to hide the × button; remove helpers double-check defensively. */
    isWidgetRemovable(id) {
      const w = window.LSWidgets && window.LSWidgets.get && window.LSWidgets.get(id);
      if (!w) return true;                       // unknown id → can't lock
      if (w.removable === false) return false;
      if (w.required  === true)  return false;
      return true;
    },
    moveKPI(id, direction) {
      const arr = [...(this.layout.kpis || [])];
      const i   = arr.indexOf(id);
      if (i < 0) return;
      const j = direction === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      // FLIP — capture KPI strip before the order swap, animate the slide.
      const strip  = this.$refs && this.$refs.kpisStrip;
      const before = (strip && window.LSFlip) ? window.LSFlip.capture(strip) : null;
      this.layout = { ...this.layout, kpis: arr };
      this.$nextTick(() => { if (before && window.LSFlip) window.LSFlip.play(strip, before); });
      this._saveSoon();
    },

    /** The KPI slots are placed by CSS `order`, so the DOM - and Tab - ran in
     *  template order, not the order on screen (WCAG 2.4.3; Broadcast audit
     *  run 2, FIT-15). Move the slots into their visual order. Alpine treats a
     *  node removed and re-added in one batch as a move, so nothing re-inits. */
    _sortKpis(strip) {
      if (!strip) return;
      const slots = [...strip.children].filter(c => c.classList.contains('ls-kpi-slot'));
      const want  = [...slots].sort((a, b) => (+a.style.order || 0) - (+b.style.order || 0));
      if (want.every((el, i) => el === slots[i])) return;
      want.forEach(el => strip.appendChild(el));
    },
    /** A slot an x-if mounts lands after its template: re-sort on every mount. */
    /** Broadcast: ONE screen-reader summary of the strip per update, not
     *  four tiles announcing at once (Xav 2026-10-09, 3a; run 2 FIT-15).
     *  The tiles keep their own aria-live for the other boards; here it is
     *  turned off and a single status line reads "Label value, ..." at most
     *  every 15s, only when something changed. */
    _kpiAnnouncer(strip) {
      if (!strip || strip.__lsAnn) return;
      strip.__lsAnn = true;
      const out = document.createElement('div');
      out.className = 'sr-only';
      out.setAttribute('role', 'status');
      out.setAttribute('aria-live', 'polite');
      out.setAttribute('aria-atomic', 'true');
      strip.parentNode.insertBefore(out, strip.nextSibling);
      let last = '', at = 0;
      const tick = () => {
        if (!document.contains(strip)) { clearInterval(iv); out.remove(); return; }
        const bc = this.activeSkin === 'broadcast';
        strip.querySelectorAll('[aria-live]').forEach(n => { if (n !== out) n.setAttribute('aria-live', bc ? 'off' : 'polite'); });
        if (!bc) return;
        const text = [...strip.querySelectorAll('.ls-kpi-slot')].filter(e => e.offsetParent).map(e => {
          const l = e.querySelector(':scope > .ls-kpi-label'), v = e.querySelector('.ls-kpi-value');
          return ((l && l.textContent) || '').trim() + ' ' + ((v && v.textContent) || '').trim();
        }).join(', ');
        const now = Date.now();
        if (text && text !== last && now - at >= 15000) { last = text; at = now; out.textContent = text; }
      };
      const iv = setInterval(tick, 3000);
      tick();
    },
    _watchKpiOrder(strip) {
      if (!strip || strip._lsKpiOrder) return;
      strip._lsKpiOrder = new MutationObserver(() => this._sortKpis(strip));
      strip._lsKpiOrder.observe(strip, { childList: true });
    },

    // ── Scoreboard slot helper ────────────────────────────────────────
    setScoreboard(id) {
      this.layout = { ...this.layout, scoreboard_id: id || null };
      this._saveSoon();
    },

    // ── Reset ─────────────────────────────────────────────────────────
    /** Reset a USER dashboard back to an empty grid. Unreachable on
     *  system presets — Edit is locked there (it's the Customize CTA).
     *  Persists via the user-dashboards PUT (through _saveSoon). */
    async resetLayout() {
      if (this._isSystemDashboard) return;   // presets are read-only — edit locked
      const ok = await this._confirm({
        title:        'Reset dashboard',
        message:      'Reset this dashboard to an empty grid? Your widgets will be cleared.',
        confirmLabel: 'Reset',
        danger:       true,
      });
      if (!ok) return;
      this.layout = { scoreboard_id: null, kpis: [], panels: [], widgetOpts: {}, defaultVersion: 0 };
      this._rowsFloor = 1;
      this._saveSoon();
    },

    /** "Tidy up" — gravity-compact the panel grid, reclaiming the holes that
     *  removes/shrinks leave behind (drag/resize reflow only pushes DOWN,
     *  never fills gaps). No-op on system presets and when already tidy.
     *  FLIP-animates the settle so panels visibly slide up into place. */
    tidyLayout() {
      if (!this._editable) return;
      const panels = this.layout.panels || [];
      if (!panels.length) return;
      const compacted = LSGrid.compact(panels);
      const moved = compacted.some(c => {
        const cur = panels.find(p => p.id === c.id);
        return !cur || cur.y !== c.y;
      });
      if (!moved) return;                    // already tidy — skip the write
      const grid   = this.$refs && this.$refs.grid;
      const before = (grid && window.LSFlip) ? window.LSFlip.capture(grid) : null;
      this.layout = { ...this.layout, panels: compacted };
      this._rowsFloor = this._maxPanelRows(compacted) || 1;
      this.$nextTick(() => { if (before && window.LSFlip) window.LSFlip.play(grid, before); });
      this._saveSoon();
    },

    // (Phase 7 cleanup: saveAsDefault method removed. System presets
    // live in code (dashboard_defaults_seed.py + SYSTEM_PRESETS_V2),
    // not as user-editable DB rows. The admin's "Save as default" pill
    // is gone from the edit toolbar.)

    // ── Drag / drop (panels only) ─────────────────────────────────────
    // Move-to-position semantics with cascade sibling reflow. The grid
    // CONTAINER handles dragover + drop (not individual cells), so users
    // can drop anywhere — including empty space. A ghost rectangle shows
    // the prospective drop position during dragover.
    _dragGhost: null,    // {x, y, w, h} or null while not dragging

    // Resize state — independent from drag state. Uses raw mousedown/move/up
    // events (NOT HTML5 drag) so the corner handle stays responsive and
    // doesn't conflict with the move-drag on the rest of the cell.
    _resize:        null,
    _onResizeMove:  null,
    _onResizeEnd:   null,
    /** Live W × H badge that follows the cursor during a resize. Cleared
     *  on mouseup. Rendered at the top of the dashboard via x-show. */
    _resizeBadge:   null,

    /** Tag the cell whose id matches `id` with .ls-cell-is-active so the
     *  CSS dim/lift kicks in. Stores the marked element in
     *  _lastActiveCell so the next call can find it cheaply for cleanup
     *  (avoids a querySelectorAll every time). Pass id=null on
     *  end-of-operation to clear. */
    _lastActiveCell: null,
    _markActiveCell(id) {
      if (this._lastActiveCell) {
        this._lastActiveCell.classList.remove('ls-cell-is-active');
        this._lastActiveCell = null;
      }
      if (!id) return;
      const grid = this.$refs && this.$refs.grid;
      if (!grid) return;
      const cell = grid.querySelector('[data-flip-id="' + id + '"]');
      if (!cell) return;
      cell.classList.add('ls-cell-is-active');
      this._lastActiveCell = cell;
    },

    onDragStart(ev, id) {
      if (this.mobile || this.tablet) { ev.preventDefault(); return; }
      this._dragSrcId = id;
      this._markActiveCell(id);
      try { ev.dataTransfer.setData('text/plain', id); } catch (_) {}
      ev.dataTransfer.effectAllowed = 'move';
    },

    /** Cell-level dragover stays present so the browser knows children
     *  are valid drop targets (otherwise drops on a cell get rejected).
     *  We DON'T compute ghost here — onGridDragOver below owns that. */
    onDragOver(ev) {
      if (this.mobile || this.tablet) return;
      ev.preventDefault();
      ev.dataTransfer.dropEffect = 'move';
    },

    /** Grid-level dragover — fires whenever the cursor is over the grid
     *  container (including over child cells, since events bubble). Track
     *  the prospective drop cell + render a ghost outline. */
    onGridDragOver(ev) {
      if (this.mobile || this.tablet || !this._dragSrcId) return;
      ev.preventDefault();
      ev.dataTransfer.dropEffect = 'move';
      const grid = this.$refs.grid;
      if (!grid) return;
      const dragged = this.layout.panels.find(w => w.id === this._dragSrcId);
      if (!dragged) return;
      const cell = LSGrid.cellFromMouse(ev, grid, Math.max(1, this.gridRows));
      this._dragGhost = {
        id: '__ghost',
        x: Math.max(0, Math.min(12 - dragged.w, cell.col)),
        y: Math.max(0, cell.row),
        w: dragged.w,
        h: dragged.h,
      };
    },

    /** Grid-level drop — move the dragged widget to the cursor's cell
     *  and reflow any siblings that overlap. FLIP-animates the
     *  displacement so siblings glide to their new positions instead
     *  of teleporting. */
    onGridDrop(ev) {
      if (this.mobile || this.tablet) return;
      ev.preventDefault();
      const srcId = this._dragSrcId || (() => {
        try { return ev.dataTransfer.getData('text/plain'); } catch (_) { return ''; }
      })();
      this._dragSrcId = null;
      this._dragGhost = null;
      if (!srcId) { this._markActiveCell(null); return; }
      const grid = this.$refs.grid;
      if (!grid) { this._markActiveCell(null); return; }
      const cell = LSGrid.cellFromMouse(ev, grid, Math.max(1, this.gridRows));
      // Clear the active-cell transform BEFORE capturing FLIP positions,
      // otherwise FLIP records the scaled/rotated bounds and animates a
      // weird zoom-out. Cleanup before measurement is the rule.
      this._markActiveCell(null);
      const before = (window.LSFlip && window.LSFlip.capture(grid)) || null;
      const next = LSGrid.moveAndReflow(this.layout.panels, srcId, cell.col, cell.row);
      this.layout = { ...this.layout, panels: next };
      this.$nextTick(() => { if (before && window.LSFlip) window.LSFlip.play(grid, before); });
      this._saveSoon();
    },

    /** Drag cancelled (Esc, dropped outside, etc.) — clear ghost. */
    onDragEnd() {
      this._dragSrcId = null;
      this._dragGhost = null;
      this._markActiveCell(null);
    },

    // ── Resize (SE-corner handle) ─────────────────────────────────────
    // mousedown on the handle captures initial cursor + widget size,
    // then we listen on document for mousemove/mouseup. The cell's
    // HTML5 drag does NOT fire because the handle's mousedown calls
    // preventDefault — that suppresses the browser's drag initiation.
    /** Direction map: which axes a given handle drags. dx/dy = origin
     *  shift coefficient (1 = origin moves with cursor, 0 = stays put).
     *  dw/dh = size growth coefficient (+1 grows with cursor, -1 shrinks). */
    _resizeDirs: {
      n:  { dx: 0,  dy: 1,  dw: 0,  dh: -1 },
      s:  { dx: 0,  dy: 0,  dw: 0,  dh:  1 },
      e:  { dx: 0,  dy: 0,  dw: 1,  dh:  0 },
      w:  { dx: 1,  dy: 0,  dw: -1, dh:  0 },
      ne: { dx: 0,  dy: 1,  dw: 1,  dh: -1 },
      nw: { dx: 1,  dy: 1,  dw: -1, dh: -1 },
      se: { dx: 0,  dy: 0,  dw: 1,  dh:  1 },
      sw: { dx: 1,  dy: 0,  dw: -1, dh:  1 },
    },

    /** Direction → CSS cursor token. Used by both the live edge-hover
     *  cursor swap and the body cursor lock during an active resize. */
    _resizeCursor(dir) {
      if (dir === 'n' || dir === 's') return 'ns-resize';
      if (dir === 'e' || dir === 'w') return 'ew-resize';
      if (dir === 'ne' || dir === 'sw') return 'nesw-resize';
      if (dir === 'nw' || dir === 'se') return 'nwse-resize';
      return '';
    },

    /** Detect whether a mouse position inside a cell is on a resize edge,
     *  and which one. Returns '' if the cursor is in the cell body (no
     *  resize). `edge` is the hit-area thickness in CSS pixels. */
    _edgeAt(cellEl, clientX, clientY, edge) {
      if (!cellEl) return '';
      const r = cellEl.getBoundingClientRect();
      const mx = clientX - r.left;
      const my = clientY - r.top;
      if (mx < 0 || mx > r.width || my < 0 || my > r.height) return '';
      const onTop    = my < edge;
      const onBottom = my > r.height - edge;
      const onLeft   = mx < edge;
      const onRight  = mx > r.width - edge;
      let d = '';
      if (onTop) d += 'n';
      if (onBottom) d += 's';
      if (onLeft) d += 'w';
      if (onRight) d += 'e';
      return d;
    },

    /** Delegated mousedown on the grid (capture phase) — intercepts
     *  resize-edge clicks BEFORE the cell's HTML5 drag has a chance to
     *  start. If the click is on an edge/corner, kicks off a resize in
     *  that direction; otherwise the event bubbles normally for drag. */
    onCellEdgeMouseDown(ev) {
      if (this.mobile || this.tablet || !this.editMode) return;
      if (ev.button !== 0) return;             // left-click only
      const cell = ev.target && ev.target.closest && ev.target.closest('.ls-cell');
      if (!cell) return;
      const id = cell.getAttribute('data-flip-id');
      if (!id) return;
      const dir = this._edgeAt(cell, ev.clientX, ev.clientY, 10);
      if (!dir) return;                        // body click → let drag handle it
      this.onResizeStart(ev, id, dir);
    },

    /** Delegated mousemove on the grid — swaps the cell's cursor in
     *  edit mode so the user gets the standard OS resize affordance
     *  when hovering an edge. Cheap (one rect read per move). */
    onCellEdgeMouseMove(ev) {
      if (this.mobile || this.tablet || !this.editMode) return;
      if (this._resize) return;                // dragging — body cursor wins
      const cell = ev.target && ev.target.closest && ev.target.closest('.ls-cell');
      // Reset cursor on the previously hovered cell if any.
      if (this._edgeHoverCell && this._edgeHoverCell !== cell) {
        this._edgeHoverCell.style.cursor = '';
        this._edgeHoverCell = null;
      }
      if (!cell) return;
      const dir = this._edgeAt(cell, ev.clientX, ev.clientY, 10);
      cell.style.cursor = dir ? this._resizeCursor(dir) : '';
      this._edgeHoverCell = cell;
    },

    onResizeStart(ev, id, dir) {
      if (this.mobile || this.tablet) return;
      ev.preventDefault();
      ev.stopPropagation();
      const widget = this.layout.panels.find(w => w.id === id);
      if (!widget) return;
      const grid = this.$refs.grid;
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      const safeDir = (dir && this._resizeDirs[dir]) ? dir : 'se';
      this._resize = {
        id,
        dir: safeDir,
        startX: ev.clientX,
        startY: ev.clientY,
        startW: widget.w,
        startH: widget.h,
        startCellX: widget.x,
        startCellY: widget.y,
        colW: rect.width / 12,
        rowH: rect.height / Math.max(1, this.gridRows),
      };
      // Initial ghost = the widget's current bounds.
      this._dragGhost = {
        id: '__resize_ghost',
        x: widget.x, y: widget.y, w: widget.w, h: widget.h,
      };
      // Live readout — initialize with the current dimensions.
      this._resizeBadge = { x: ev.clientX, y: ev.clientY, w: widget.w, h: widget.h };
      // Flag the resized cell as active so the dim/lift CSS applies.
      this._markActiveCell(id);
      // Lock body cursor so it stays as the resize axis even when the
      // mouse drifts off the cell edge during the drag.
      document.body.style.cursor = this._resizeCursor(safeDir);
      // Bind closure ONCE so removeEventListener can match later.
      if (!this._onResizeMove) this._onResizeMove = this._resizeMove.bind(this);
      if (!this._onResizeEnd)  this._onResizeEnd  = this._resizeEnd.bind(this);
      document.addEventListener('mousemove', this._onResizeMove);
      document.addEventListener('mouseup',   this._onResizeEnd);
    },

    /** Compute candidate {x, y, w, h} from raw mouse deltas, clamped to
     *  the grid. Shared by _resizeMove (ghost preview) and _resizeEnd
     *  (commit) so they can't drift apart. */
    _resizeGeometry(ev) {
      const r = this._resize;
      const dir = this._resizeDirs[r.dir];
      const dCols = Math.round((ev.clientX - r.startX) / r.colW);
      const dRows = Math.round((ev.clientY - r.startY) / r.rowH);
      let newX = r.startCellX + dir.dx * dCols;
      let newY = r.startCellY + dir.dy * dRows;
      let newW = r.startW + dir.dw * dCols;
      let newH = r.startH + dir.dh * dRows;
      // Clamp width/height ≥ 1. If the user dragged past zero on a
      // shrink-from-origin direction (N/W/NW), freeze the moving edge
      // 1 cell from the opposite edge — natural "can't invert" behaviour.
      if (newW < 1) {
        if (dir.dw < 0) newX = r.startCellX + r.startW - 1;
        newW = 1;
      }
      if (newH < 1) {
        if (dir.dh < 0) newY = r.startCellY + r.startH - 1;
        newH = 1;
      }
      newX = Math.max(0, Math.min(12 - newW, newX));
      newY = Math.max(0, newY);
      return { x: newX, y: newY, w: newW, h: newH };
    },

    /** Mouse-move while resizing — updates the ghost to preview the
     *  new size + the live W × H badge. The actual widget w/h aren't
     *  committed until mouseup. */
    _resizeMove(ev) {
      const r = this._resize;
      if (!r) return;
      const g = this._resizeGeometry(ev);
      this._dragGhost = { id: '__resize_ghost', x: g.x, y: g.y, w: g.w, h: g.h };
      this._resizeBadge = { x: ev.clientX, y: ev.clientY, w: g.w, h: g.h };
    },

    /** Mouseup — commit the resize + reflow siblings. FLIP-animates any
     *  siblings that get pushed to new positions by the size change.
     *  IMPORTANT: compute the final geometry BEFORE clearing
     *  `this._resize`, because `_resizeGeometry` reads it directly. */
    _resizeEnd(ev) {
      const r = this._resize;
      document.removeEventListener('mousemove', this._onResizeMove);
      document.removeEventListener('mouseup',   this._onResizeEnd);
      if (!r) {
        // Defensive cleanup even if state was cleared mid-flight.
        this._resize = null;
        this._dragGhost = null;
        this._resizeBadge = null;
        document.body.style.cursor = '';
        return;
      }
      const g = this._resizeGeometry(ev);
      // Now safe to tear down transient state.
      this._resize = null;
      this._dragGhost = null;
      this._resizeBadge = null;
      document.body.style.cursor = '';
      // Clear active-cell BEFORE FLIP capture so the scaled/rotated
      // bounds don't pollute the "before" snapshot.
      this._markActiveCell(null);
      if (g.x === r.startCellX && g.y === r.startCellY
          && g.w === r.startW && g.h === r.startH) return;
      const grid   = this.$refs.grid;
      const before = (grid && window.LSFlip) ? window.LSFlip.capture(grid) : null;
      const next = LSGrid.resizeAtAndReflow(this.layout.panels, r.id, g.x, g.y, g.w, g.h);
      this.layout = { ...this.layout, panels: next };
      this.$nextTick(() => { if (before && window.LSFlip) window.LSFlip.play(grid, before); });
      this._saveSoon();
    },

    // ── Mobile reorder (panels only) ──────────────────────────────────
    moveUp(id) {
      const arr = [...this.layout.panels];
      const i   = arr.findIndex(w => w.id === id);
      if (i <= 0) return;
      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
      this.layout = { ...this.layout, panels: arr };
      this._saveSoon();
    },
    moveDown(id) {
      const arr = [...this.layout.panels];
      const i   = arr.findIndex(w => w.id === id);
      if (i < 0 || i >= arr.length - 1) return;
      [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
      this.layout = { ...this.layout, panels: arr };
      this._saveSoon();
    },

    // ── Persistence (debounced; sends the whole layout object) ────────
    /** Phase 5 (Xav 2026-05-31): persistence routes on _activeDashboard.kind.
     *    user   — PUT /api/user-dashboards/{id} with the layout fields
     *             that user dashboards own (scoreboard_id, kpis, panels,
     *             widgetOpts). Refreshes the picker list afterward so the
     *             updated_at timestamp + name are current.
     *    system — no-op. System presets are read-only; Edit button on a
     *             system preset opens the Customize modal instead of
     *             toggling editMode. This branch is a belt-and-braces
     *             guard in case some legacy code path writes anyway.
     *
     *  Phase 7 will delete the legacy LSLayoutStore.save / writeCached
     *  paths entirely.
     */
    _saveSoon() {
      if (!this._uid) return;
      // Dashboards are edited on the desktop only. On the phone this.layout can
      // be the board's PHONE projection - saving it would rewrite the desktop.
      if (this.shellMode) return;
      clearTimeout(this._saveTimer);
      const snap = this.layout;
      const active = this._activeDashboard;
      this._saveTimer = setTimeout(async () => {
        let ok = false;
        if (active && active.kind === 'user' && active.id) {
          try {
            const r = await fetch(`/api/user-dashboards/${encodeURIComponent(active.id)}`, {
              method:      'PUT',
              credentials: 'same-origin',
              headers:     { 'Content-Type': 'application/json' },
              // Sectioned model (Xav 2026-06-14): persist the whole tab
              // list. _sectionsForSave folds the live layout back into the
              // active section first.
              body:        JSON.stringify({ sections: this._sectionsForSave() }),
            });
            ok = r.ok;
            if (ok) {
              // Refresh the cached dashboard in this.userDashboards so the
              // picker shows the new name/updated_at without a separate
              // round-trip. Best-effort.
              try {
                const data = await r.json();
                if (data && data.dashboard) {
                  const idx = this.userDashboards.findIndex(d => d.id === data.dashboard.id);
                  if (idx >= 0) this.userDashboards[idx] = data.dashboard;
                }
              } catch (_) { /* response decode optional */ }
            }
          } catch (_) { ok = false; }
        } else {
          // System preset — silently swallow. Edit on system is a
          // Customize CTA, not a save path.
          ok = true;
        }
        // Saved-pulse indicator (Xav 2026-05-27): flash a green dot
        // next to the Edit button briefly so users know auto-save
        // fired. Closes the "did it save?" question without nagging.
        if (ok) {
          this.saveFailed = false;
          this.savedPulse = true;
          clearTimeout(this._savedPulseT);
          this._savedPulseT = setTimeout(() => { this.savedPulse = false; }, 1400);
        } else {
          // Surface a failed save instead of swallowing it (v4 audit — the
          // silent failure let the user believe their layout persisted). The
          // red state persists until the next successful auto-save.
          this.savedPulse = false;
          this.saveFailed = true;
        }
      }, 600);
    },

    /** Saved-pulse state — visible dot fades for ~1.4s after each
     *  successful auto-save. Used by the indicator next to the
     *  Edit/Done button in the sub-nav. */
    savedPulse:    false,
    _savedPulseT:  null,
    // Persistent red indicator when an auto-save fails (v4 audit — was
    // silent). Cleared on the next successful save.
    saveFailed:    false,
  };

  Object.defineProperties(base, Object.getOwnPropertyDescriptors(host));

  const _origInit = base.init;
  base.init = async function () {
    if (_origInit) await _origInit.call(this);
    this.initWidgets();
  };

  return base;
}

window.liveDashboardV2 = liveDashboardV2;
